import React, { useState, useEffect, useMemo, useRef } from 'react';
import { supabase, getSelectedOrgId } from '../services/supabaseClient';
import { createPortal } from 'react-dom';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import styled, { keyframes } from 'styled-components';
import { useAuth } from '../context/AuthContext';
import {
    format, startOfWeek, endOfWeek, startOfMonth, endOfMonth,
    eachDayOfInterval, isSameMonth, isSameDay, addMonths, subMonths,
    addDays, addWeeks, differenceInDays
} from 'date-fns';
import { es } from 'date-fns/locale';
import { roomsService } from '../services/roomsService';
import { tasksService } from '../services/tasksService';
import { stickiesService } from '../services/stickiesService';
import { usersService } from '../services/usersService';
import { getInsumos } from '../services/insumosService';
import { deviceService } from '../services/deviceService';
import { TrazAppDeviceDetailModal } from '../components/TrazAppDeviceDetailModal';
import { Room } from '../types/rooms';
import { Task, StickyNote, RecurrenceConfig, TaskType, Insumo } from '../types';
import { PrintableRoomCalendar } from '../components/Esquejera/PrintableRoomCalendar';


import { GroupDetailModal } from '../components/GroupDetailModal';
import { LoadingSpinner } from '../components/LoadingSpinner';
import AudioRecorderWidget from '../components/AudioRecorderWidget';

import { EsquejeraGrid } from '../components/Esquejera/EsquejeraGrid';
import { PrintableMapReport } from '../components/Esquejera/PrintableMapReport';
import { PrintableBatchLabels } from '../components/Esquejera/PrintableBatchLabels';
import { LivingSoilGrid } from '../components/LivingSoil/LivingSoilGrid';
import { LivingSoilBatchModal } from '../components/LivingSoil/LivingSoilBatchModal';
import { StageSelectionModal } from '../components/LivingSoil/StageSelectionModal';
import { DndContext, DragEndEvent, useSensor, useSensors, MouseSensor, TouchSensor, pointerWithin, useDraggable, useDroppable, DragOverlay } from '@dnd-kit/core';
import { getGeneticColor } from '../utils/geneticColors';
import { Batch, CloneMap, BatchStage } from '../types/rooms';
import { Genetic } from '../types/genetics';
import { useReactToPrint } from 'react-to-print';



import { ConfirmationModal } from '../components/ConfirmationModal';
import { DeleteReasonModal } from '../components/DeleteReasonModal';

import { TransplantModal } from '../components/Esquejera/TransplantModal';
import { HarvestModal } from '../components/Flowering/HarvestModal';
import { ToastModal } from '../components/ToastModal';
import { CustomDatePicker } from '../components/CustomDatePicker';
import { PromptModal } from '../components/PromptModal';
import { CustomSelect } from '../components/CustomSelect';
import { Button as ShadcnButton } from '../components/ui/Button';
import { Badge as ShadcnBadge } from '../components/ui/Badge';
import { StickyNoteModal } from '../components/StickyNoteModal';
import { DaySummaryModal } from '../components/DaySummaryModal';
import { LocationActionModal } from '../components/LocationActionModal';
import {
  ArrowLeft as LucideArrowLeft,
  Sprout as LucideSprout,
  Layers as LucideLayers,
  Thermometer as LucideThermometer,
  Calendar as LucideCalendar,
  CheckSquare as LucideCheckSquare,
  History as LucideHistory,
  Scissors as LucideScissors,
  Settings as LucideSettings,
  StickyNote as LucideStickyNote,
  RefreshCw as LucideRefreshCw,
  X as LucideX,
  Plus as LucidePlus,
  Trash2 as LucideTrash2,
  ClipboardList as LucideClipboardList,
  UploadCloud as LucideUploadCloud,
  User as LucideUser,
  Package as LucidePackage,
  FileText as LucideFileText,
  Check as LucideCheck,
  Tag as LucideTag,
  Edit3 as LucideEdit3,
  Grid as LucideGrid,
  AlertTriangle as LucideAlertTriangle,
  Leaf as LucideLeaf,
  Flower2 as LucideFlower2,
  Warehouse as LucideWarehouse,
  ArrowLeft,
  ArrowLeftRight,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Clock,
  Dna,
  Droplets,
  Loader2,
  Map as LucideMap,
  Move,
  Pencil,
  Printer,
  Sprout,
  Thermometer,
  Wifi
} from 'lucide-react';

import { createGlobalStyle } from 'styled-components';

// Global Print Styles
// Handles hiding the report on screen, and hiding everything ELSE on print.
const GlobalPrintStyles = createGlobalStyle`
@media screen {
    .printable-area { display: none!important; }
    .printable-map-details { display: none !important; }
    .printable-report { display: none; }
}

@media print {
    @page { size: landscape; margin: 10mm; }

    /* RESET */
    body {
        margin: 0; padding: 0;
        background: white;
        -webkit-print-color-adjust: exact;
    }
    
    /* Hide common non-print elements */
    .no-print, nav, aside, header, footer, .Toastify, .no-print-map-header, .no-print-map-controls {
        display: none !important;
    }

    /* Force all layout wrappers to give up their dark backgrounds and heights */
    div[class*="RoomDetailContainer"], div[class*="Container"], div[class*="Layout"] {
        background: transparent !important;
        background-color: transparent !important;
        min-height: 0 !important;
    }

    /* NOTE: We removed the aggressive 'body * { visibility: hidden }' 
       because PrintableMapReport now uses a Portal and handles its own isolation 
       by hiding siblings. This prevents the blank page issue. */

    /* ==========================================================================
       CASE 2: MAP PRINT (Triggered by "Imprimir Mapa" button -> class .printing-map)
       ========================================================================== */
    
    /* 1. AGGRESSIVELY HIDE REPORT when printing map */
    body.printing-map .printable-report {
        display: none !important;
        visibility: hidden !important;
        opacity: 0 !important;
        width: 0 !important;
        height: 0 !important;
        overflow: hidden !important;
        position: static !important; /* Break absolute positioning */
        z-index: -1000 !important;
    }

    /* 2. SHOW ACTIVE MAP CONTAINER */
    body.printing-map .active-map-container {
        visibility: visible !important;
        display: block !important;
        position: absolute !important;
        top: 0 !important;
        left: 0 !important;
        width: 100% !important;
        margin: 0 !important;
        padding: 0 !important;
        background: white;
        z-index: 10000;
    }

    /* Show all children of active map container */
    body.printing-map .active-map-container * {
        visibility: visible !important;
    }

    /* Except controls that are inside it */
    body.printing-map .active-map-container .no-print,
    body.printing-map .active-map-container button {
        display: none !important;
    }

    /* Map Grid Styles */
    body.printing-map .printable-map-grid {
        visibility: visible !important;
        display: block !important;
        width: 100% !important;
        border: 2px solid #000 !important;
        page-break-after: always;
        margin-bottom: 2rem;
    }

    body.printing-map .printable-map-grid * {
        visibility: visible !important;
        color: #000 !important;
        border-color: #000 !important;
    }
    
    body.printing-map .map-grid-item {
        break-inside: avoid;
        border: 1px solid #000 !important;
    }

    /* Map Details Table Styles */
    body.printing-map .printable-map-details {
        display: block !important;
        visibility: visible !important;
        margin-top: 1rem;
    }
    
    body.printing-map .printable-map-details * {
        visibility: visible !important;
        color: black !important;
    }
    
    body.printing-map .printable-map-details table {
        width: 100%;
        border-collapse: collapse;
    }
    
    body.printing-map .printable-map-details th,
    body.printing-map .printable-map-details td {
        border: 1px solid #000 !important;
    }
}
`;

// We also need some global-like styles to handle the flex overrides on inline styles
const RoomDetailContainer = styled.div`
  .room-actions-wrapper {
    @media (max-width: 768px) {
      padding: 1rem !important;
    }
  }
  
  .action-buttons-container {
    @media (max-width: 768px) {
      justify-content: center !important;
      width: 100%;
    }
  }
  
  .no-print-map-header {
      @media (max-width: 768px) {
          flex-direction: column;
          height: auto !important;
          gap: 1rem;
          padding: 1rem !important;
      }
  }

  .map-and-sidebar-container {
      @media (max-width: 768px) {
          display: flex !important;
          flex-direction: column !important;
          gap: 1.5rem !important;
      }
  }

  .main-content-wrapper {
      @media (max-width: 768px) {
          display: contents !important;
      }
  }

  .map-list-container {
      @media (max-width: 768px) {
          display: contents !important;
      }
  }

  .mobile-maps-list-wrapper {
      @media (max-width: 768px) {
          order: 1 !important;
          width: 100% !important;
          margin-bottom: 0 !important;
      }
      &.empty-maps {
          @media (max-width: 768px) {
              order: 2 !important; /* Si no hay mapas, va despues de los lotes */
          }
      }
  }

  .sidebar-container {
      @media (max-width: 768px) {
          width: 100% !important;
          max-height: 400px !important; /* Cap height to allow scrolling inner content */
          position: relative !important; /* Remove sticky to avoid overriding other content */
          top: auto !important;
          margin-top: 0 !important;
          order: 2 !important; /* Lotes van despues de mapas activos */
      }
      &.empty-maps {
          @media (max-width: 768px) {
              order: 1 !important; /* Lotes van antes si no hay mapas activos */
          }
      }
  }

  .map-drop-zone-wrapper {
      @media (max-width: 768px) {
          order: 3 !important; /* Drop Zone en el lugar #3 */
          margin-top: 0 !important;
          width: 100% !important;
      }
  }

  .create-map-card-wrapper {
      @media (max-width: 768px) {
          order: 4 !important; /* Create Card en el lugar #4 */
          margin-top: 0 !important;
          width: 100% !important;
      }
  }

  .drying-group-header {
      @media (max-width: 768px) {
          flex-direction: column !important;
          align-items: flex-start !important;
          gap: 1rem !important;
      }
  }

  .drying-group-actions {
      @media (max-width: 768px) {
          width: 100% !important;
          justify-content: flex-end !important;
      }
  }

  .drying-batch-card-header {
      @media (max-width: 768px) {
          flex-direction: column !important;
          align-items: flex-start !important;
          gap: 0.25rem !important;
      }
  }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const fadeOut = keyframes`
  from { opacity: 1; }
  to { opacity: 0; }
`;

const scaleIn = keyframes`
  from { transform: scale(0.95); opacity: 0; }
  to { transform: scale(1); opacity: 1; }
`;

const scaleOut = keyframes`
  from { transform: scale(1); opacity: 1; }
  to { transform: scale(0.95); opacity: 0; }
`;

const Container = styled.div`
  .notes-header-container {
      justify-content: flex-start !important;
  }
  
  .notes-title {
      margin: 0 !important;
  }
  
  @media (max-width: 768px) {
      .notes-header-container {
          justify-content: center !important;
      }
      .notes-title {
          margin: 0 auto !important;
      }
  }


padding: 2rem;
padding-top: 1.5rem;
max-width: 1200px;
margin: 0 auto;
animation: ${fadeIn} 0.5s ease-in-out;
`;

const rotate = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const SpinningIcon = styled(Loader2)`
  animation: ${rotate} 1s linear infinite;
`;

const CreateCard = styled.div`
  background: rgba(15, 23, 42, 0.4);
  backdrop-filter: blur(12px);
  border-radius: 1.25rem;
  border: 2px dashed rgba(255, 255, 255, 0.2);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  min-height: 250px;
  gap: 1rem;
  transition: all 0.2s ease;
  opacity: 0.8;
  color: #94a3b8;
  width: 100%;

  &:hover {
    border-color: #4ade80;
    color: #4ade80;
    background: rgba(74, 222, 128, 0.1);
    opacity: 1;
    box-shadow: 0 0 15px rgba(74, 222, 128, 0.2);
  }

  @media (max-width: 768px) {
    min-height: 80px;
    flex-direction: row;
    padding: 1rem;
    gap: 1rem;
    border-radius: 1rem;
    
    p {
      font-size: 0.9rem !important;
      margin: 0;
      text-align: left;
    }
  }
`;

const DashedCircle = styled.div`
  width: 60px;
  height: 60px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: inherit;
  transition: all 0.5s ease;

  &::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0; bottom: 0;
    border-radius: 50%;
    border: 2px dashed currentColor;
    transition: all 0.5s ease;
  }

  ${CreateCard}:hover &::before {
    animation: ${rotate} 10s linear infinite;
  }

  @media (max-width: 768px) {
    width: 40px;
    height: 40px;
    font-size: 1rem;
    flex-shrink: 0;
  }
`;

const EmptyStickyState = styled.div`
  background: rgba(15, 23, 42, 0.4);
  backdrop-filter: blur(12px);
  border-radius: 0.75rem;
  border: 2px dashed rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 1.5rem;
  gap: 1.5rem;
  transition: all 0.2s ease;
  color: #94a3b8;
  width: 100%;
  margin-top: 1rem;

  &:hover {
    border-color: #fde047; /* Yellow/Gold */
    color: #facc15;
    background: rgba(253, 224, 71, 0.1);
    box-shadow: 0 0 15px rgba(253, 224, 71, 0.2);
  }
`;

const DashedStickyCircle = styled.div`
width: 50px;
height: 50px;
position: relative;
display: flex;
align-items: center;
justify-content: center;
font-size: 1.25rem; /* Icon size */
color: inherit;
transition: all 0.5s ease;

  &::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0; bottom: 0;
    border-radius: 50%;
    border: 2px dashed currentColor;
    transition: all 0.5s ease;
}

  ${EmptyStickyState}: hover &::before {
    animation: ${rotate} 10s linear infinite;
}
`;

// Helper for colors
const getTaskStyles = (type: string) => {
    switch (type) {
        case 'danger': return { bg: 'rgba(248, 113, 113, 0.1)', color: '#f87171', border: 'rgba(248, 113, 113, 0.2)' };
        case 'warning': return { bg: 'rgba(250, 204, 21, 0.1)', color: '#facc15', border: 'rgba(250, 204, 21, 0.2)' };
        case 'fertilizar':
        case 'enmienda':
        case 'te_compost': return { bg: 'rgba(74, 222, 128, 0.1)', color: '#4ade80', border: 'rgba(74, 222, 128, 0.2)' };
        case 'riego': return { bg: 'rgba(56, 189, 248, 0.1)', color: '#38bdf8', border: 'rgba(56, 189, 248, 0.2)' };
        case 'poda_apical': return { bg: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', border: 'rgba(239, 68, 68, 0.2)' }; // Red
        case 'defoliacion': return { bg: 'rgba(249, 115, 22, 0.1)', color: '#f97316', border: 'rgba(249, 115, 22, 0.2)' }; // Orange
        case 'hst':
        case 'lst':
        case 'entrenamiento': return { bg: 'rgba(6, 182, 212, 0.15)', color: '#06b6d4', border: 'rgba(6, 182, 212, 0.3)' };
        case 'esquejes': return { bg: 'rgba(217, 119, 6, 0.1)', color: '#d97706', border: 'rgba(217, 119, 6, 0.2)' };
        case 'info':
        default: return { bg: 'rgba(148, 163, 184, 0.1)', color: '#cbd5e1', border: 'rgba(148, 163, 184, 0.2)' };
    }
};

// Badges
const Badge = styled.span<{ stage?: string, taskType?: string }>`
padding: 2px 8px;
border-radius: 9999px;
font-size: 0.7rem;
font-weight: 600;
text-transform: capitalize;
display: inline-flex;
align-items: center;
border: 1px solid transparent; 
  
  ${p => {
        if (p.stage) {
            return `
            background: ${p.stage === 'vegetation' ? 'rgba(74, 222, 128, 0.1)' : p.stage === 'flowering' ? 'rgba(244, 114, 182, 0.1)' : (p.stage === 'drying' || p.stage === 'curing') ? 'rgba(251, 146, 60, 0.1)' : p.stage === 'living_soil' ? 'rgba(45, 212, 191, 0.1)' : 'rgba(148, 163, 184, 0.1)'};
            color: ${p.stage === 'vegetation' ? '#4ade80' : p.stage === 'flowering' ? '#f472b6' : (p.stage === 'drying' || p.stage === 'curing') ? '#fb923c' : p.stage === 'living_soil' ? '#2dd4bf' : '#cbd5e1'};
            border-color: ${p.stage === 'vegetation' ? 'rgba(74, 222, 128, 0.2)' : p.stage === 'flowering' ? 'rgba(244, 114, 182, 0.2)' : (p.stage === 'drying' || p.stage === 'curing') ? 'rgba(251, 146, 60, 0.2)' : p.stage === 'living_soil' ? 'rgba(45, 212, 191, 0.2)' : 'rgba(148, 163, 184, 0.2)'};
            backdrop-filter: blur(4px);
          `;
        }
        if (p.taskType) {
            const s = getTaskStyles(p.taskType || '');
            return `background: ${s.bg}; color: ${s.color}; border-color: ${s.border || 'transparent'}; backdrop-filter: blur(4px);`;
        }
        return `background: rgba(148, 163, 184, 0.1); color: #cbd5e1; border-color: rgba(148, 163, 184, 0.2); backdrop-filter: blur(4px);`;
    }
    }
`;


const Title = styled.h1`
  font-size: 1.8rem;
  color: #f8fafc;
  margin: 1rem 0 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;

  @media (max-width: 768px) {
    font-size: 1.4rem;
    flex-direction: column;
    justify-content: center;
    margin: 0.5rem 0;
    gap: 0.5rem;
  }
`;

const StyledActionButton = styled.button<{ $variant?: 'primary' | 'secondary' | 'gold' | 'success' | 'danger' }>`
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.45rem 0.95rem;
  border-radius: 0.65rem;
  font-weight: 600;
  font-size: 0.825rem;
  letter-spacing: -0.01em;
  cursor: pointer;
  transition: all 0.18s cubic-bezier(0.4, 0, 0.2, 1);
  backdrop-filter: blur(12px);
  user-select: none;

  border: ${p => {
    switch (p.$variant) {
      case 'gold': return '1px solid rgba(245, 158, 11, 0.35)';
      case 'primary':
      case 'success': return '1px solid rgba(16, 185, 129, 0.35)';
      case 'danger': return '1px solid rgba(239, 68, 68, 0.35)';
      default: return '1px solid rgba(255, 255, 255, 0.12)';
    }
  }};

  background: ${p => {
    switch (p.$variant) {
      case 'gold': return 'rgba(245, 158, 11, 0.12)';
      case 'primary':
      case 'success': return 'rgba(16, 185, 129, 0.14)';
      case 'danger': return 'rgba(239, 68, 68, 0.14)';
      default: return 'rgba(255, 255, 255, 0.05)';
    }
  }};

  color: ${p => {
    switch (p.$variant) {
      case 'gold': return '#fbbf24';
      case 'primary':
      case 'success': return '#34d399';
      case 'danger': return '#f87171';
      default: return '#f1f5f9';
    }
  }};

  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.06);

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
    background: ${p => {
      switch (p.$variant) {
        case 'gold': return 'rgba(245, 158, 11, 0.22)';
        case 'primary':
        case 'success': return 'rgba(16, 185, 129, 0.22)';
        case 'danger': return 'rgba(239, 68, 68, 0.22)';
        default: return 'rgba(255, 255, 255, 0.1)';
      }
    }};
    border-color: ${p => {
      switch (p.$variant) {
        case 'gold': return 'rgba(245, 158, 11, 0.6)';
        case 'primary':
        case 'success': return 'rgba(16, 185, 129, 0.6)';
        case 'danger': return 'rgba(239, 68, 68, 0.6)';
        default: return 'rgba(255, 255, 255, 0.22)';
      }
    }};
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

// Modal Utils
const ModalOverlay = styled.div<{ isClosing?: boolean }>`
position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0, 0, 0, 0.5); z-index: 10001;
display: flex; align-items: center; justify-content: center; backdrop-filter: blur(2px);
animation: ${p => p.isClosing ? fadeOut : fadeIn} 0.2s ease-in-out forwards;
`;

const PortalModalOverlay = ({ children, isClosing }: { children: React.ReactNode, isClosing?: boolean }) => {
    return createPortal(
        <ModalOverlay onClick={(e) => e.stopPropagation()} isClosing={isClosing}>
            {children}
        </ModalOverlay>,
        document.body
    );
};
const ModalContent = styled.div<{ isClosing?: boolean }>`
  background: rgba(15, 23, 42, 0.96);
  backdrop-filter: blur(24px);
  -webkit-backdrop-filter: blur(24px);
  border: 1px solid rgba(255, 255, 255, 0.12);
  padding: 2rem;
  border-radius: 1.25rem;
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.7), 0 0 0 1px rgba(255, 255, 255, 0.05);
  color: #f8fafc;
  animation: ${p => p.isClosing ? scaleOut : scaleIn} 0.22s cubic-bezier(0.16, 1, 0.3, 1) forwards;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: rgba(15, 23, 42, 0.5);
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.15);
    border-radius: 9999px;
  }

  @media(max-width: 768px) {
    padding: 1.25rem;
    width: 95%;
    border-radius: 1rem;
  }
`;

const TaskModalContent = styled.div<{ isClosing?: boolean }>`
  animation: ${p => p.isClosing ? scaleOut : scaleIn} 0.22s cubic-bezier(0.16, 1, 0.3, 1) forwards;
  background: rgba(15, 23, 42, 0.96);
  backdrop-filter: blur(24px);
  -webkit-backdrop-filter: blur(24px);
  border: 1px solid rgba(255, 255, 255, 0.12);
  padding: 2.25rem;
  border-radius: 1.25rem;
  width: 95%;
  max-width: 980px;
  max-height: 90vh;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.7), 0 0 0 1px rgba(255, 255, 255, 0.05);
  color: #f8fafc;

  /* Custom scrollbar for high-end feel */
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: rgba(15, 23, 42, 0.5);
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.15);
    border-radius: 9999px;
  }

  @media(max-width: 768px) {
    padding: 1.25rem;
    width: 95%;
    border-radius: 1rem;
  }
`;

// New Styled Components for Interactive Elements
const StageButton = styled.button<{ isActive: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 1rem 0.5rem;
  background: ${p => p.isActive ? 'rgba(74, 222, 128, 0.2)' : 'rgba(15, 23, 42, 0.4)'};
  backdrop-filter: blur(8px);
  color: ${p => p.isActive ? '#4ade80' : '#94a3b8'};
  border: 1px solid ${p => p.isActive ? 'rgba(74, 222, 128, 0.5)' : 'rgba(255, 255, 255, 0.1)'};
  border-radius: 0.5rem;
  cursor: pointer;
  transition: all 0.2s;
  font-weight: 600;
  font-size: 0.9rem;
  box-shadow: ${p => p.isActive ? '0 4px 6px rgba(74, 222, 128, 0.2)' : 'none'};

  &:hover {
    transform: translateY(-2px);
    background: ${p => p.isActive ? 'rgba(74, 222, 128, 0.3)' : 'rgba(255, 255, 255, 0.05)'};
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.2);
    border-color: ${p => p.isActive ? 'rgba(74, 222, 128, 0.6)' : 'rgba(255, 255, 255, 0.2)'};
  }
`;

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const ButtonSpinner = styled.div`
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid white;
  border-radius: 50%;
  width: 1rem;
  height: 1rem;
  animation: ${spin} 1s linear infinite;
`;

// Responsive Calendar Styled Components
const CalendarContainer = styled.div`
  background: rgba(15, 23, 42, 0.4);
  backdrop-filter: blur(12px);
  border-radius: 1rem;
  padding: 1.5rem;
  box-shadow: 0 4px 6px -1px rgba(0,0,0,0.3);
  border: 1px solid rgba(255, 255, 255, 0.1);

  @media (max-width: 768px) {
    padding: 0.75rem;
  }
`;

const CalendarHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;

  @media (max-width: 768px) {
    flex-wrap: wrap;
    gap: 1rem;
    
    h2 {
      order: 1;
      width: 100%;
      text-align: center;
      font-size: 1.25rem !important;
    }
    
    button:first-of-type {
      order: 2;
      flex: 1;
      justify-content: center;
    }

    button:last-of-type {
      order: 3;
      flex: 1;
      justify-content: center;
    }
  }
`;

const CalendarScrollWrapper = styled.div`
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  border-radius: 0.5rem;
  
  &::-webkit-scrollbar {
    height: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 3px;
  }
`;

const CalendarGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, minmax(60px, 1fr));
  gap: 1px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 0.5rem;
  overflow: hidden;

  @media (max-width: 768px) {
    min-width: 600px; /* Force scroll on mobile for premium feel */
  }
`;

const ModalActionButton = styled.button<{ variant?: 'primary' | 'danger' | 'secondary', disabled?: boolean }>`
  background: ${p => p.variant === 'primary' ? 'rgba(74, 222, 128, 0.2)' : p.variant === 'danger' ? 'rgba(239, 68, 68, 0.2)' : 'rgba(255, 255, 255, 0.05)'};
  backdrop-filter: blur(8px);
  color: ${p => p.variant === 'primary' ? '#4ade80' : p.variant === 'danger' ? '#f87171' : '#f8fafc'};
  border: 1px solid ${p => p.variant === 'danger' ? 'rgba(239, 68, 68, 0.5)' : p.variant === 'secondary' ? 'rgba(255, 255, 255, 0.1)' : p.variant === 'primary' ? 'rgba(74, 222, 128, 0.5)' : 'none'};
  padding: 0.5rem 1.5rem;
  border-radius: 0.5rem;
  font-weight: 600;
  cursor: ${p => p.disabled ? 'not-allowed' : 'pointer'};
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s;
  opacity: ${p => p.disabled ? 0.5 : 1};

  &:hover {
    background: ${p => p.disabled ? 'none' : (p.variant === 'primary' ? 'rgba(74, 222, 128, 0.3)' : p.variant === 'danger' ? 'rgba(239, 68, 68, 0.3)' : 'rgba(255, 255, 255, 0.1)')};
    transform: ${p => p.disabled ? 'none' : 'translateY(-1px)'};
    box-shadow: ${p => p.disabled ? 'none' : '0 4px 6px rgba(0,0,0,0.3)'};
  }
`;






const CreateMapDropZone = ({ children }: { children: React.ReactNode }) => {
    const { setNodeRef, isOver } = useDroppable({ id: 'create-map-zone' });

    return (
        <div ref={setNodeRef} style={{
            position: 'relative',
            minHeight: '200px',
            borderRadius: '0.5rem',
            border: isOver ? '2px dashed #48bb78' : '2px dashed transparent',
            background: isOver ? 'rgba(72, 187, 120, 0.1)' : 'transparent',
            transition: 'all 0.2s',
            // Agregamos un minHeight condicional para móviles si es posible (via query param o asumiendo clase, pero al ser obj usa minHeight)
            // Ya que style overrrides no soporta queries nativos lo forzamos con una class o bajamos el default
        }} className="responsive-dropzone-container">
            <style>{`
                @media (max-width: 768px) {
                    .responsive-dropzone-container { min-height: 80px !important; }
                }
            `}</style>
            {children}
            {isOver && (
                <div style={{
                    position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: 'rgba(15, 23, 42, 0.85)', backdropFilter: 'blur(4px)', zIndex: 10,
                    pointerEvents: 'none', borderRadius: '0.5rem'
                }}>
                    <div style={{ color: '#4ade80', fontWeight: 'bold', fontSize: '1.2rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <LucidePlus /> Soltar para crear Mesa
                    </div>
                </div>
            )}
        </div>
    );
};

const DroppableMapCard = ({ map, children, onClick }: { map: CloneMap, children: React.ReactNode, onClick: () => void }) => {
    const { setNodeRef, isOver } = useDroppable({
        id: `map-${map.id}`,
        data: { type: 'map', map }
    });

    return (
        <div
            ref={setNodeRef}
            onClick={onClick}
            style={{
                border: isOver ? '2px solid rgba(56, 189, 248, 0.5)' : '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '0.75rem',
                padding: '1.25rem',
                background: isOver ? 'rgba(56, 189, 248, 0.1)' : 'rgba(15, 23, 42, 0.4)',
                backdropFilter: 'blur(12px)',
                cursor: 'pointer',
                transition: 'all 0.2s',
                boxShadow: isOver ? '0 0 15px rgba(56, 189, 248, 0.2)' : '0 4px 6px -1px rgba(0, 0, 0, 0.3)'
            }}
        >
            {children}
        </div>
    );
};




const CancelButton = styled.button`
  flex: 1;
  padding: 0.6rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 0.5rem;
  background: rgba(15, 23, 42, 0.6);
  backdrop-filter: blur(12px);
  color: #94a3b8;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.2s;
  &:hover { background: rgba(255, 255, 255, 0.1); color: #f8fafc; }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const ActionButton = styled.button<{ $variant?: 'primary' | 'danger' | 'success' }>`
  padding: 0.75rem 1rem;
  border: 1px solid ${p => p.$variant === 'danger' ? 'rgba(239, 68, 68, 0.5)' : p.$variant === 'success' ? 'rgba(74, 222, 128, 0.5)' : 'rgba(56, 189, 248, 0.5)'};
  border-radius: 0.5rem;
  background: ${p => p.$variant === 'danger' ? 'rgba(239, 68, 68, 0.2)' : p.$variant === 'success' ? 'rgba(74, 222, 128, 0.2)' : 'rgba(56, 189, 248, 0.2)'};
  backdrop-filter: blur(8px);
  color: ${p => p.$variant === 'danger' ? '#f87171' : p.$variant === 'success' ? '#4ade80' : '#38bdf8'};
  cursor: pointer;
  font-weight: 600;
  font-size: 0.9rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.2);
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;

  &:hover {
    background: ${p => p.$variant === 'danger' ? 'rgba(239, 68, 68, 0.3)' : p.$variant === 'success' ? 'rgba(74, 222, 128, 0.3)' : 'rgba(56, 189, 248, 0.3)'};
    transform: translateY(-1px);
    box-shadow: 0 6px 8px -2px rgba(0, 0, 0, 0.3);
  }
  &:disabled {
    background: rgba(255, 255, 255, 0.05);
    border-color: rgba(255, 255, 255, 0.1);
    color: #64748b;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;



const HoverButton = styled.button`
  background: rgba(249, 115, 22, 0.2);
  color: #fb923c;
  border: 1px solid rgba(249, 115, 22, 0.5);
  backdrop-filter: blur(8px);
  padding: 0.25rem 0.5rem;
  border-radius: 0.3rem;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.75rem;
  margin-right: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(0,0,0,0.2);

  &:hover {
    transform: scale(1.05);
    background: rgba(249, 115, 22, 0.3);
    box-shadow: 0 4px 6px rgba(0,0,0,0.3);
  }

  &:active {
    transform: scale(0.95);
  }
`;


const FormGroup = styled.div`
  margin-bottom: 1.5rem;
  label {
    display: block;
    margin-bottom: 0.5rem;
    font-weight: 600;
    color: #cbd5e1;
    font-size: 0.9rem;
  }
  input, select, textarea {
    width: 100%;
    padding: 0.75rem;
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 0.5rem;
    font-size: 0.95rem;
    color: #f8fafc;
    background: rgba(30, 41, 59, 0.5);
    backdrop-filter: blur(8px);
    transition: all 0.2s;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);

    &:focus {
      outline: none;
      border-color: rgba(56, 189, 248, 0.5);
      box-shadow: 0 0 0 3px rgba(56, 189, 248, 0.1);
    }
    &::placeholder {
      color: #64748b;
    }
    
    // Autofill Dark Mode override (Webkit specific)
    &:-webkit-autofill,
    &:-webkit-autofill:hover, 
    &:-webkit-autofill:focus, 
    &:-webkit-autofill:active{
        -webkit-box-shadow: 0 0 0 30px #0f172a inset !important;
        -webkit-text-fill-color: #f8fafc !important;
        transition: background-color 5000s ease-in-out 0s;
    }
  }
  textarea { min-height: 100px; resize: vertical; font-family: inherit; }
`;

// Room Header Components


const HeaderGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 0.75rem;
    margin-bottom: 1.5rem;
  }
`;

const RoomHeaderContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 1.25rem;
  margin-bottom: 1.75rem;
  justify-content: space-between;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    gap: 0.75rem;
    margin-bottom: 1rem;
  }
`;

const StatCard = styled.div`
  background: rgba(15, 23, 42, 0.65);
  backdrop-filter: blur(16px);
  padding: 1.35rem 1.5rem;
  border-radius: 1.25rem;
  box-shadow: 0 10px 30px -5px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.08);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-start;
  border: 1px solid rgba(255, 255, 255, 0.09);
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 20px 35px -5px rgba(0, 0, 0, 0.55), inset 0 1px 0 rgba(255, 255, 255, 0.12);
    border-color: rgba(255, 255, 255, 0.18);
  }
  
  @media (max-width: 768px) {
    padding: 1rem;
    border-radius: 1rem;
  }

  h3 {
    font-size: 0.75rem;
    color: #94a3b8;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    margin-bottom: 0.65rem;
    display: flex;
    align-items: center;
    gap: 0.45rem;
    font-weight: 700;

    @media (max-width: 768px) {
      font-size: 0.65rem;
      margin-bottom: 0.25rem;
      line-height: 1.2;
    }
  }

  .value {
    font-size: 2.1rem;
    font-weight: 800;
    letter-spacing: -0.025em;
    font-variant-numeric: tabular-nums;
    color: #f8fafc;
    line-height: 1.1;

    @media (max-width: 768px) {
      font-size: 1.35rem;
    }
  }

  .sub {
    font-size: 0.8125rem;
    color: #64748b;
    margin-top: 0.4rem;
    font-weight: 500;

    @media (max-width: 768px) {
      font-size: 0.75rem;
      margin-top: 0.15rem;
    }
  }
`;




const DraggableStockBatch = ({ batch, onClick }: { batch: Batch, onClick?: () => void }) => {
    const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
        id: `stock - ${batch.id} `,
        data: { type: 'batch', batch, fromStock: true }
    });

    const geneticName = batch.genetic?.name || batch.name;
    const colors = getGeneticColor(geneticName);

    return (
        <div
            ref={setNodeRef} {...listeners} {...attributes}
            onClick={onClick}
            style={{
                transform: isDragging ? 'translate3d(0,0,0)' : undefined,
                opacity: isDragging ? 0.5 : 1,
                background: 'rgba(30, 41, 59, 0.5)',
                backdropFilter: 'blur(8px)',
                padding: '0.5rem',
                marginBottom: '0',
                borderRadius: '0.5rem',
                border: `1px solid ${colors.border}`,
                borderLeft: `4px solid ${colors.border}`,
                cursor: 'grab',
                boxShadow: '0 4px 6px -1px rgba(0,0,0,0.3)',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                gap: '1rem'
            }}
        >
            <div>
                <div style={{ fontWeight: 'bold', fontSize: '0.8rem', color: '#f8fafc' }}>{geneticName}</div>
                {batch.tracking_code && (
                    <div style={{ fontSize: '0.85rem', fontWeight: 'bold', color: '#4ade80' }}>
                        {batch.tracking_code}
                    </div>
                )}
                <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>
                    {/* Prefer batch.name as it contains the "burned" date string which is correct, avoiding timezone shifts */}
                    {batch.name || (batch.start_date ? format(new Date(batch.start_date), 'dd/MM/yy HH:mm') : '')}
                </div>
            </div>
            <div style={{
                background: 'rgba(56, 189, 248, 0.1)', color: '#38bdf8', fontWeight: 'bold', border: '1px solid rgba(56, 189, 248, 0.2)',
                padding: '0.15rem 0.4rem', borderRadius: '4px', fontSize: '0.75rem'
            }}>
                x{batch.quantity}
            </div>
        </div>
    );
};

const DraggableGenetic = ({ genetic }: { genetic: Genetic }) => {
    const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
        id: `genetic - ${genetic.id} `,
        data: { type: 'genetic', genetic, fromSidebar: true }
    });

    return (
        <div
            ref={setNodeRef} {...listeners} {...attributes}
            style={{
                transform: isDragging ? 'translate3d(0,0,0)' : undefined,
                opacity: isDragging ? 0.5 : 1,
                background: 'rgba(30, 41, 59, 0.5)', padding: '0.75rem', marginBottom: '0.5rem',
                backdropFilter: 'blur(8px)',
                borderRadius: '0.5rem', border: '1px solid rgba(255, 255, 255, 0.05)',
                cursor: 'grab', boxShadow: '0 4px 6px rgba(0,0,0,0.3)',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center'
            }}
        >
            <div>
                <div style={{ fontWeight: 'bold', fontSize: '0.9rem', color: '#f8fafc' }}>{genetic.name}</div>
                <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{genetic.type === 'photoperiodic' ? 'Fotoperiódica' : 'Automática'}</div>
            </div>
            <div style={{
                background: 'rgba(56, 189, 248, 0.1)', color: '#38bdf8', fontWeight: 'bold',
                padding: '0.25rem 0.5rem', borderRadius: '999px', fontSize: '0.8rem'
            }}>
                <Dna />
            </div>
        </div>
    );
};


// --- SIDEBAR GROUPING UTILS ---
const groupBatchesByGeneticDate = (batches: Batch[], groupByGenetic: boolean = false) => {
    // 1. Identify Roots & Orphans
    const batchMap = new Map();
    batches.forEach(b => batchMap.set(b.id, { ...b, children: [] }));

    const roots: any[] = [];
    const orphans: any[] = [];

    batches.forEach(b => {
        if (b.parent_batch_id && batchMap.has(b.parent_batch_id)) {
            batchMap.get(b.parent_batch_id).children.push(b);
        } else {
            if (b.parent_batch_id) orphans.push(b); // Orphaned child
            else roots.push(batchMap.get(b.id)); // Proper Root
        }
    });

    // 2. Initial Grouping (Parent-Child)
    const groups: any[] = [];
    roots.forEach(root => {
        root.children.sort((a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
        groups.push({ root, children: root.children });
    });
    orphans.forEach(o => groups.push({ root: o, children: [] }));

    // 3. Smart Grouping (Virtual Batches)
    const smartGroups: any[] = [];
    const groupMap = new Map<string, any[]>();

    groups.forEach(group => {
        const { root } = group;

        // Check for Custom Group Tag in group_name (new) or Notes (legacy)
        const notes = root.notes || '';
        const groupMatch = notes.match(/\[Grupo:\s*(.*?)\]/);
        const groupName = root.group_name || (groupMatch ? groupMatch[1].trim() : null);

        if (groupName) {
            // Priority Grouping by group_name or legacy "Grupo: X" note
            const key = `GROUP|${groupName}`;
            if (!groupMap.has(key)) {
                groupMap.set(key, []);
            }
            groupMap.get(key)!.push(group);
        } else if (groupByGenetic) {
            // Automatic Grouping by GENETIC (for Drying Rooms)
            const geneticName = root.genetic?.name || root.name || 'Desconocida';
            // Use genetic name + TIME (Minute) as key to separate batches created same day but different runs
            const timeStr = new Date(root.created_at).toISOString().slice(0, 16);
            const key = `GENETIC|${geneticName}|${timeStr}`;

            if (!groupMap.has(key)) {
                groupMap.set(key, []);
            }
            groupMap.get(key)!.push(group);
        } else {
            // Default Grouping - DISABLED to prevent unwanted merging.
            // Treat each batch as unique unless explicitly grouped via notes.
            const key = `UNIQUE|${root.id}`;

            if (!groupMap.has(key)) {
                groupMap.set(key, []);
            }
            groupMap.get(key)!.push(group);
        }
    });

    groupMap.forEach((bundledGroups, key) => {
        // If it's a Custom Group or Genetic, we merge
        if (key.startsWith('GROUP|') || key.startsWith('GENETIC|')) {
            // Sort by name inside the group for consistency?
            bundledGroups.sort((a, b) => a.root.name.localeCompare(b.root.name));

            const primary = bundledGroups[0];
            const rest = bundledGroups.slice(1);
            const mergedChildren = [...primary.children];

            // Only force name for Custom Groups. For Genetic groups, let the view decide (Lote #...)
            if (key.startsWith('GROUP|')) {
                const groupName = key.split('|')[1];
                primary.root._virtualGroupName = groupName;
            } else if (key.startsWith('GENETIC|')) {
                // If grouping by genetic, try to inherit the richest parent_batch metadata from any child
                // so the Drying Room can correctly reconstruct the HarvestModal name.
                if (!primary.root.parent_batch) {
                    const richerChild = bundledGroups.find(g => g.root.parent_batch);
                    if (richerChild) {
                        primary.root.parent_batch = richerChild.root.parent_batch;
                    }
                }

                // Also try to inherit the clone_map_id if present
                if (!primary.root.clone_map_id) {
                    const richerChildMap = bundledGroups.find(g => g.root.clone_map_id);
                    if (richerChildMap) {
                        primary.root.clone_map_id = richerChildMap.root.clone_map_id;
                    }
                }
            }

            rest.forEach(g => {
                mergedChildren.push(g.root);
                mergedChildren.push(...g.children);
            });

            // Push merged group
            smartGroups.push({
                root: primary.root, // _virtualGroupName already set if applicable
                children: mergedChildren
            });
        } else {
            // Standard Logic
            if (bundledGroups.length === 1) {
                smartGroups.push(bundledGroups[0]);
            } else {
                // Merge
                bundledGroups.sort((a, b) => a.root.name.localeCompare(b.root.name));
                const primary = bundledGroups[0];
                const rest = bundledGroups.slice(1);
                const mergedChildren = [...primary.children];
                rest.forEach(g => {
                    mergedChildren.push(g.root);
                    mergedChildren.push(...g.children);
                });
                mergedChildren.sort((a: any, b: any) => a.name.localeCompare(b.name));
                smartGroups.push({ root: primary.root, children: mergedChildren });
            }
        }
    });

    // Sort by Date Desc
    // We might want Groups to appear first? Or just by date of the "primary" root?
    // Current sort works by date of root.
    smartGroups.sort((a, b) => new Date(b.root.created_at).getTime() - new Date(a.root.created_at).getTime());
    return smartGroups;
};

const SidebarBatchGroup = ({ group, expanded, onToggleExpand, childrenRender, onBatchGroupClick, renderHeaderActions }: { group: any, expanded: boolean, onToggleExpand: () => void, childrenRender: (batch: any) => React.ReactNode, onBatchGroupClick?: (batch: any) => void, renderHeaderActions?: (batch: any) => React.ReactNode }) => {
    const { root, children } = group;


    const totalQty = root.quantity + children.reduce((acc: number, c: any) => acc + c.quantity, 0);
    // Custom Name Logic
    let displayName = root.name;

    // Check for Virtual Group Name
    if (root._virtualGroupName) {
        displayName = root._virtualGroupName;
    } else {
        // Use the saved name (which has the correct time string) and prepend the code
        const dateObj = new Date(root.created_at);
        const formattedDate = `${dateObj.getDate().toString().padStart(2, '0')}/${(dateObj.getMonth() + 1).toString().padStart(2, '0')}/${dateObj.getFullYear().toString().slice(-2)} ${dateObj.getHours().toString().padStart(2, '0')}:${dateObj.getMinutes().toString().padStart(2, '0')}`;

        const geneticStr = root.genetic?.name || root.name || 'Desconocida';

        const originalCode = root.tracking_code ? root.tracking_code :
            (root.name && root.name.includes("Lote ")) ? root.name.replace("Lote ", "").substring(0, 4) :
                root.id.substring(0, 4);

        displayName = `${originalCode} - ${geneticStr} - ${formattedDate}`;
    }

    const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
        id: `group - ${root.id} `,
        data: { type: 'batch-group', group, fromSidebar: true }
    });

    return (
        <div
            ref={setNodeRef} {...listeners} {...attributes}
            style={{
                marginBottom: '0.25rem',
                background: 'rgba(30, 41, 59, 0.5)',
                backdropFilter: 'blur(8px)',
                borderRadius: '0.5rem',
                border: '1px solid rgba(255, 255, 255, 0.05)',
                overflow: 'hidden',
                transform: isDragging ? 'translate3d(0,0,0)' : undefined,
                opacity: isDragging ? 0.3 : 1,
                cursor: 'grab',
                touchAction: 'manipulation'
            }}
        >
            <style>{`
                @media (max-width: 768px) {
                    .desktop-sidebar-header-${root.id} { display: none !important; }
                    .mobile-sidebar-header-${root.id} { display: flex !important; }
                }
                @media (min-width: 769px) {
                    .mobile-sidebar-header-${root.id} { display: none !important; }
                }
            `}</style>

            {/* Header */}
            <div
                onClick={() => {
                    if (onBatchGroupClick) {
                        onBatchGroupClick({ ...root, _totalQuantity: totalQty, _displayName: displayName });
                    } else {
                        onToggleExpand();
                    }
                }}
                style={{
                    padding: '0.35rem 0.5rem',
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    background: expanded ? 'rgba(56, 189, 248, 0.1)' : 'rgba(15, 23, 42, 0.6)',
                    borderBottom: expanded ? '1px solid rgba(56, 189, 248, 0.2)' : 'none',
                    gap: '1rem'
                }}
            >
                {/* Desktop Version */}
                <div className={`desktop-sidebar-header-${root.id}`} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', width: '100%', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <span
                            onClick={(e) => {
                                e.stopPropagation();
                                onToggleExpand();
                            }}
                            style={{ color: '#38bdf8', fontSize: '0.65rem', padding: '0.25rem', cursor: 'pointer' }}
                        >
                            {expanded ? '▼' : '▶'}
                        </span>
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <strong style={{ fontSize: '0.8rem', color: '#f8fafc' }}>{displayName}</strong>
                            <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>Total: {totalQty} u.</span>
                        </div>
                    </div>
                    {/* Header Actions for Groups or Single Batches */}
                    {renderHeaderActions && (
                        <div onClick={(e) => e.stopPropagation()} style={{ display: 'flex', gap: '0.25rem' }}>
                            {renderHeaderActions(root)}
                        </div>
                    )}
                </div>

                {/* Mobile Version (Compressed Line) */}
                <div className={`mobile-sidebar-header-${root.id}`} style={{ display: 'none', alignItems: 'center', width: '100%', gap: '0.5rem', overflow: 'hidden' }}>
                    <span
                        onClick={(e) => {
                            e.stopPropagation();
                            onToggleExpand();
                        }}
                        style={{ color: '#38bdf8', fontSize: '0.65rem', padding: '0.25rem', cursor: 'pointer', flexShrink: 0 }}
                    >
                        {expanded ? '▼' : '▶'}
                    </span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', overflow: 'hidden', whiteSpace: 'nowrap', width: '100%' }}>
                        <strong style={{ fontSize: '0.85rem', color: '#f8fafc', overflow: 'hidden', textOverflow: 'ellipsis' }}>{displayName}</strong>
                        <span style={{ fontSize: '0.8rem', color: '#4ade80', fontWeight: 600, flexShrink: 0 }}>- {totalQty} u.</span>
                    </div>
                </div>

            </div>

            {/* Content with internal scroll */}
            {/* Content with internal scroll */}
            <ExpandableContainer $expanded={expanded}>
                <ExpandableInner>
                    <div
                        onPointerDown={(e) => e.stopPropagation()}
                        style={{
                            padding: '0.25rem',
                            background: 'rgba(30, 41, 59, 0.5)',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '0.25rem',
                            maxHeight: '350px', // Limit height for internal scrolling
                            overflowY: 'auto',
                            cursor: 'default'
                        }}
                    >
                        {childrenRender(root)}
                        {children.map((child: any) => childrenRender(child))}
                    </div>
                </ExpandableInner>
            </ExpandableContainer>

        </div >
    );
};


const RoomDetail: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();

    // --- ROOM EDIT STATE (HOISTED) ---
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isClosingEditRoom, setIsClosingEditRoom] = useState(false);
    const [isUpdatingRoom, setIsUpdatingRoom] = useState(false);

    const closeEditRoomModal = () => {
        setIsClosingEditRoom(true);
        setTimeout(() => {
            setIsEditModalOpen(false);
            setIsClosingEditRoom(false);
        }, 200);
    };
    const [editRoomName, setEditRoomName] = useState('');
    const [editRoomType, setEditRoomType] = useState('');
    const [editRoomStartDate, setEditRoomStartDate] = useState('');

    // --- BULK SELECTION & DELETE ---
    const [isSelectionMode, setIsSelectionMode] = useState(false);
    const [selectedBatchIds, setSelectedBatchIds] = useState<Set<string>>(new Set());
    const [isBulkDeleteConfirmOpen, setIsBulkDeleteConfirmOpen] = useState(false);
    const [isClosingBulkDelete, setIsClosingBulkDelete] = useState(false); // Animation State
    const [isDeletingBulk, setIsDeletingBulk] = useState(false); // Loading state for delete button

    const handleCloseBulkDelete = () => {
        setIsClosingBulkDelete(true);
        setTimeout(() => {
            setIsBulkDeleteConfirmOpen(false);
            setIsClosingBulkDelete(false);
        }, 200);
    };





    const handleBulkDeleteFirstStep = () => {
        if (selectedBatchIds.size === 0) return;
        setIsBulkDeleteConfirmOpen(true);
    };

    const handleBulkDeleteConfirm = async (reason: string, notes: string) => {
        setIsDeletingBulk(true);
        try {
            const finalReason = notes ? `${reason} - ${notes}` : reason;
            const success = await roomsService.deleteBatches(Array.from(selectedBatchIds), finalReason, user?.id);
            if (success) {
                // Keep modal open but show success toast after small delay or immediately
                await new Promise(resolve => setTimeout(resolve, 500)); // Smooth UX

                handleCloseBulkDelete(); // Close Modal with animation

                // Wait for animation to finish BEFORE changing UI state (prevents glitch)
                setTimeout(async () => {
                    setToastState({ isOpen: true, message: 'Lotes eliminados correctamente', type: 'success' });
                    setIsSelectionMode(false);
                    setSelectedBatchIds(new Set());
                    // Force refresh room data to remove deleted items visually
                    if (id) await loadData(id, false, false);
                }, 200);
            } else {
                setToastState({ isOpen: true, message: 'Error al eliminar lotes', type: 'error' });
                // Clear selection
                setSelectedBatchIds(new Set());
                setIsBulkDeleteConfirmOpen(false);
                setIsSelectionMode(false); // Optional: Exit selection mode // Immediate close on error is fine, or use handler
            }
        } catch (error) {
            console.error(error);
            setToastState({ isOpen: true, message: 'Error inesperado', type: 'error' });
            setIsBulkDeleteConfirmOpen(false);
        } finally {
            setIsDeletingBulk(false);
        }
    };
    // --- END BULK SELECTION ---

    const [room, setRoom] = useState<Room | null>(null);
    const [loading, setLoading] = useState(true);
    const [insumos, setInsumos] = useState<Insumo[]>([]);

    const [isMetricsModalOpen, setIsMetricsModalOpen] = useState(false);
    const [metricsData, setMetricsData] = useState<any[]>([]);

    const handleBulkStageChange = async (newStage: any) => {
        if (selectedBatchIds.size === 0) return;
        setLoading(true);
        try {
            // Update all selected batches
            const updatePromises = Array.from(selectedBatchIds).map(id =>
                roomsService.updateBatchStage(id, newStage, user?.id)
            );
            await Promise.all(updatePromises);

            showToast(`Fase actualizada a ${newStage} para ${selectedBatchIds.size} lotes.`, 'success');

            // Clear selection and refresh
            setIsSelectionMode(false);
            setSelectedBatchIds(new Set());
            setIsStageModalOpen(false);

            if (id) await loadData(id, false, false);

        } catch (error) {
            console.error(error);
            showToast("Error al actualizar la fase.", 'error');
        } finally {
            setLoading(false);
        }
    };



    // Genetics Modal State
    const [isGeneticsModalOpen, setIsGeneticsModalOpen] = useState(false);

    // Living Soil Stage Modal
    const [isStageModalOpen, setIsStageModalOpen] = useState(false);

    // Transplant Modal (Use constants to satisfy unused implementation for now)
    const loadingMetrics = false;
    // const transplantRoom = room; // Unused warning fix

    // ...

    // Group Detail Modal State
    // const [selectedGroupName, setSelectedGroupName] = useState<string | null>(null); // Unused warning fix
    const [selectedGroupName] = useState<string | null>(null);

    // ...

    // const handleOpenGroupDetail = ... (Already removed)

    // Transplant Modal


    // Plant Detail Modal

    // Move/Delete/Assign confirmations

    // History

    // Harvest Modal










    // Batch Selection State
    const [selectedBatchId, setSelectedBatchId] = useState<string | null>(null);


    // Create Batch Modal State
    const [isCreateBatchModalOpen, setIsCreateBatchModalOpen] = useState(false);
    const [isClosingCreateBatch, setIsClosingCreateBatch] = useState(false);
    const [isCreatingBatch, setIsCreatingBatch] = useState(false);

    const closeCreateBatchModal = () => {
        setIsClosingCreateBatch(true);
        setTimeout(() => {
            setIsCreateBatchModalOpen(false);
            setIsClosingCreateBatch(false);
        }, 200);
    };
    const [newBatch, setNewBatch] = useState({
        geneticId: '',
        quantity: '',
        date: new Date().toISOString()
    });
    const [genetics, setGenetics] = useState<any[]>([]);

    useEffect(() => {
        const fetchGenetics = async () => {
            const data = await roomsService.getGenetics();
            setGenetics(data);
        };
        fetchGenetics();
    }, []);

    // Group Detail Modal State
    // selectedGroupName moved to line 796 to fix lint warning
    const [isGroupDetailModalOpen, setIsGroupDetailModalOpen] = useState(false);

    // Calendar State
    const [currentDate, setCurrentDate] = useState(new Date());
    const [tasks, setTasks] = useState<Task[]>([]);
    const [stickies, setStickies] = useState<StickyNote[]>([]);


    // Day Summary State
    const [isDaySummaryOpen, setIsDaySummaryOpen] = useState(false);
    const [selectedDayForSummary, setSelectedDayForSummary] = useState<Date | null>(null);

    // ==========================================
    // VIRTUAL TASKS GENERATION (RECURRENCE LOGIC)
    // ==========================================
    const [searchParams, setSearchParams] = useSearchParams();
    const activeMapId = searchParams.get('mapId');
    const setActiveMapId = (id: string | null) => {
        setSearchParams(prev => {
            const newParams = new URLSearchParams(prev);
            if (id) {
                newParams.set('mapId', id);
            } else {
                newParams.delete('mapId');
            }
            return newParams;
        });
    };

    const allTasksForView = useMemo(() => {
        const virtualTasks: Task[] = [];
        const endOfViewingWindow = endOfMonth(addMonths(currentDate, 1));

        tasks.forEach(task => {
            if (!task.recurrence || !task.due_date) return;

            const rec = task.recurrence;
            const [year, month, day] = task.due_date.split('T')[0].split('-');
            let lastDate = new Date(Number(year), Number(month) - 1, Number(day), 12, 0, 0);
            if (isNaN(lastDate.getTime())) return;

            let safetyCounter = 0;

            while (lastDate < endOfViewingWindow && safetyCounter < 100) {
                let nextDate: Date | null = null;
                const interval = typeof rec.interval === 'string' ? parseInt(rec.interval) || 1 : rec.interval || 1;

                if (rec.type === 'custom' || rec.type === 'daily' || rec.type === 'weekly') {
                    if (rec.unit === 'day' || rec.type === 'daily') {
                        nextDate = addDays(lastDate, interval);
                    } else if (rec.unit === 'week' || rec.type === 'weekly') {
                        nextDate = addWeeks(lastDate, interval);
                    } else if (rec.unit === 'month') {
                        nextDate = addMonths(lastDate, interval);
                    }
                }

                if (nextDate && nextDate <= endOfViewingWindow) {
                    const formattedNextDate = format(nextDate, 'yyyy-MM-dd');
                    const isExcluded = rec.exceptions && rec.exceptions.includes(formattedNextDate);

                    if (!isExcluded) {
                        virtualTasks.push({
                            ...task,
                            id: `virtual - ${task.id} -${nextDate.getTime()} `,
                            due_date: formattedNextDate,
                            status: 'pending',
                            title: `${task.title} (Proyectada)`,
                            type: task.type
                        });
                    }
                    lastDate = nextDate;
                } else {
                    break;
                }
                safetyCounter++;
            }
        });

        return [...tasks, ...virtualTasks].filter(t => {
            if (activeMapId) {
                return t.map_id === activeMapId;
            } else {
                return !t.map_id || t.map_id === null;
            }
        });
    }, [tasks, currentDate, activeMapId]);

    // Sticky Modal State
    const [isStickyModalOpen, setIsStickyModalOpen] = useState(false);

    const handleCloseStickyModal = () => {
        setIsStickyModalOpen(false);
    };
    const [isProjectionAlertOpen, setIsProjectionAlertOpen] = useState(false);

    // Recurrence State
    const [recurrenceEnabled, setRecurrenceEnabled] = useState(false);
    const [recurrenceConfig, setRecurrenceConfig] = useState<RecurrenceConfig>({
        type: 'weekly',
        interval: 1,
        unit: 'week',
        daysOfWeek: []
    });


    const [stickyContent, setStickyContent] = useState('');
    const [stickyColor, setStickyColor] = useState<StickyNote['color']>('yellow');
    const [selectedSticky] = useState<StickyNote | null>(null);
    const [isSavingSticky, setIsSavingSticky] = useState(false);

    const [stickyToDelete, setStickyToDelete] = useState<string | null>(null);
    const [isDeletingSticky, setIsDeletingSticky] = useState(false);
    const [isStickyDeleteModalClosing, setIsStickyDeleteModalClosing] = useState(false);

    const handleCloseStickyDeleteModal = () => {
        setIsStickyDeleteModalClosing(true);
        setTimeout(() => {
            setStickyToDelete(null);
            setIsStickyDeleteModalClosing(false);
        }, 200);
    };


    // Interactive Calendar State
    const { user } = useAuth();
    // Task Modal State
    const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
    const [isClosingTaskModal, setIsClosingTaskModal] = useState(false);
    const [isSavingTask, setIsSavingTask] = useState(false);

    // --- Printing Setup for Calendar ---
    const calendarPrintRef = useRef<HTMLDivElement>(null);
    const handlePrintCalendar = useReactToPrint({
        contentRef: calendarPrintRef,
        documentTitle: `Calendario_Sala_${room?.name || 'Desconocida'}_${format(currentDate, 'MMMM_yyyy', { locale: es })}`,
        onBeforePrint: () => new Promise<void>((resolve) => {
            console.log("Preparando impresión horizontal del calendario...");
            resolve();
        }),
        onAfterPrint: () => console.log("Impresión de calendario finalizada."),
        onPrintError: (e) => console.error("Error al imprimir el calendario", e)
    });

    const closeTaskModal = () => {
        setIsClosingTaskModal(true);
        setTimeout(() => {
            setIsTaskModalOpen(false);
            setIsClosingTaskModal(false);
        }, 200);
    };
    const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
    const [selectedTask, setSelectedTask] = useState<Task | null>(null);
    const [taskForm, setTaskForm] = useState({ title: '', type: 'info', due_date: '', description: '', assigned_to: '', crop_id: '' as string | null, insumo_id: '', estimated_volume: '' });
    const [taskTypes, setTaskTypes] = useState<TaskType[]>([]);

    // Real Users State
    const [users, setUsers] = useState<any[]>([]);

    // Clone Maps State
    const [cloneMaps, setCloneMaps] = useState<CloneMap[]>([]);

    // Distribution Modal State
    const [isDistributionConfirmOpen, setIsDistributionConfirmOpen] = useState(false);
    const [distributionData, setDistributionData] = useState<{
        batches: Batch[];
        position: string;
        mapId: string;
        map: CloneMap;
    } | null>(null);

    const [toastState, setToastState] = useState<{ isOpen: boolean; message: string; type: 'success' | 'error' | 'info' }>({
        isOpen: false,
        message: '',
        type: 'info'
    });

    useEffect(() => {
        if (isEditModalOpen && room) {
            setEditRoomName(room.name);
            setEditRoomType(room.type || 'vegetation');
            // If start_date is absolute ISO, split the string directly to keep the stored date, avoiding Date object UTC shift
            setEditRoomStartDate(room.start_date ? room.start_date.split('T')[0] : '');
        }
    }, [isEditModalOpen, room]);

    const [isMapModalOpen, setIsMapModalOpen] = useState(false);
    const [isClosingMapModal, setIsClosingMapModal] = useState(false);
    const [isCreatingMap, setIsCreatingMap] = useState(false);

    const closeMapModal = () => {
        setIsClosingMapModal(true);
        setTimeout(() => {
            setIsMapModalOpen(false);
            setIsClosingMapModal(false);
        }, 200);
    };
    const [newMapName, setNewMapName] = useState('');
    const [newMapRows, setNewMapRows] = useState<number | string>('');
    const [newMapCols, setNewMapCols] = useState<number | string>('');

    // Edit Map Modal State
    const [isEditMapModalOpen, setIsEditMapModalOpen] = useState(false);
    const [isClosingEditMap, setIsClosingEditMap] = useState(false);
    const [editingMapId, setEditingMapId] = useState<string | null>(null);
    // const [targetMapId, setTargetMapId] = useState<string | null>(null);
    const [isBulkEditModalOpen, setIsBulkEditModalOpen] = useState(false);
    const [bulkEditForm, setBulkEditForm] = useState({ stage: '', notes: '' });
    const [isUpdatingMap, setIsUpdatingMap] = useState(false);

    // --- Change Phase Modal State (New) ---
    const [isChangePhaseModalOpen, setIsChangePhaseModalOpen] = useState(false);
    const [changePhaseForm, setChangePhaseForm] = useState({ stage: '' as BatchStage | '', notes: '' });

    // Handle Change Phase Submit (Adapter for Bulk Edit Logic)
    const handleChangePhaseSubmit = async () => {
        if (!room) return;
        setIsUpdatingMap(true); // Reuse loading state
        try {
            const updates = Array.from(selectedBatchIds).map(async (batchId) => {
                const currentBatch = room.batches?.find(b => b.id === batchId);
                if (!currentBatch) return;

                const updates: any = {};
                // Only update if changed/set
                if (changePhaseForm.stage) updates.stage = changePhaseForm.stage;
                // Notes: If empty string, do we clear? Or only if explicit?
                // For simplicity, always update note if user opened this modal
                updates.notes = changePhaseForm.notes;

                const { error } = await supabase
                    .from('batches')
                    .update(updates)
                    .eq('id', batchId);

                if (error) throw error;

                // Log History
                await supabase.from('room_history').insert({
                    room_id: room.id,
                    batch_id: batchId,
                    user_id: user?.id, // Assuming user context is available
                    action: 'update',
                    notes: `Cambio de fase a ${changePhaseForm.stage || 'sin cambio'}: ${changePhaseForm.notes}`
                });
            });

            await Promise.all(updates);

            // Refresh
            const { data: updatedRoom } = await supabase
                .from('rooms')
                .select('*, batches(*, genetic:genetics(*))')
                .eq('id', room.id)
                .single();

            if (updatedRoom) setRoom(updatedRoom);

            // Close
            setIsChangePhaseModalOpen(false);
            setIsSelectionMode(false);
            setSelectedBatchIds(new Set());

        } catch (error) {
            console.error("Error updating phase:", error);
            alert("Error al actualizar la fase");
        } finally {
            setIsUpdatingMap(false);
        }
    };

    const closeEditMapModal = () => {
        setIsClosingEditMap(true);
        setTimeout(() => {
            setIsEditMapModalOpen(false);
            setIsClosingEditMap(false);
        }, 200);
    };

    // --- Loading Data ---
    const [editMapName, setEditMapName] = useState('');
    const [editMapRows, setEditMapRows] = useState<number | string>('');
    const [editMapCols, setEditMapCols] = useState<number | string>('');


    // Map Deletion Modal State
    const [isDeleteMapModalOpen, setIsDeleteMapModalOpen] = useState(false);
    const [isClosingDeleteMap, setIsClosingDeleteMap] = useState(false);
    const [isDeletingMap, setIsDeletingMap] = useState(false);
    const [mapIdToDelete, setMapIdToDelete] = useState<string | null>(null);

    const closeDeleteMapModal = () => {
        setIsClosingDeleteMap(true);
        setTimeout(() => {
            setIsDeleteMapModalOpen(false);
            setIsClosingDeleteMap(false);
        }, 200);
    };

    // History Modal State
    const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
    const [isClosingHistory, setIsClosingHistory] = useState(false);

    const closeHistoryModal = () => {
        setIsClosingHistory(true);
        setTimeout(() => {
            setIsHistoryModalOpen(false);
            setIsClosingHistory(false);
        }, 200);
    };
    const [historyLoading, setHistoryLoading] = useState(false);
    const [roomHistory, setRoomHistory] = useState<any[]>([]);


    // Map Clear Modal State
    const [isClearMapConfirmOpen, setIsClearMapConfirmOpen] = useState(false);

    // Create Map from Batch Confirmation State
    const [isCreateMapConfirmOpen, setIsCreateMapConfirmOpen] = useState(false);
    const [pendingMapBatch, setPendingMapBatch] = useState<Batch | null>(null);

    // Create Map from Group Confirmation State
    const [isCreateMapFromGroupConfirmOpen, setIsCreateMapFromGroupConfirmOpen] = useState(false);
    const [pendingMapGroup, setPendingMapGroup] = useState<any | null>(null);
    const [isClosingCreateMapFromGroup, setIsClosingCreateMapFromGroup] = useState(false);
    const [isCreatingMapFromGroup, setIsCreatingMapFromGroup] = useState(false);

    const closeCreateMapFromGroupModal = () => {
        setIsClosingCreateMapFromGroup(true);
        setTimeout(() => {
            setIsCreateMapFromGroupConfirmOpen(false);
            setIsClosingCreateMapFromGroup(false);
        }, 200);
    };

    const [deleteConfirm, setDeleteConfirm] = useState<{ isOpen: boolean; batch: Batch | null }>({
        isOpen: false,
        batch: null
    });
    const [isDeletingBatch, setIsDeletingBatch] = useState(false);
    const [isClosingDeleteConfirm, setIsClosingDeleteConfirm] = useState(false);

    // Custom Modal for Available Batches
    const [availableBatchToDelete, setAvailableBatchToDelete] = useState<Batch | null>(null);
    const [isDeletingAvailable, setIsDeletingAvailable] = useState(false);

    const closeDeleteConfirm = () => {
        setIsClosingDeleteConfirm(true);
        setTimeout(() => {
            setDeleteConfirm({ isOpen: false, batch: null });
            setIsClosingDeleteConfirm(false);
        }, 200);
    };

    const [moveConfirm, setMoveConfirm] = useState<{ isOpen: boolean; position: string | null }>({
        isOpen: false,
        position: null
    });


    // State for Map Assignment Modal
    const [assignModal, setAssignModal] = useState<{ isOpen: boolean; batch: Batch | null; quantity: string | number }>({
        isOpen: false,
        batch: null,
        quantity: 1
    });
    const [isAssignModalClosing, setIsAssignModalClosing] = useState(false);
    const [isAutoAssigning, setIsAutoAssigning] = useState(false);

    const handleCloseAssignModal = () => {
        setIsAssignModalClosing(true);
        setTimeout(() => {
            setAssignModal({ ...assignModal, isOpen: false });
            setIsAssignModalClosing(false);
        }, 300);
    };

    // Plant Detail Modal State (Individual Clone on Map)
    const [notePromptModal, setNotePromptModal] = useState<{ isOpen: boolean, batch: Batch | null }>({ isOpen: false, batch: null });
    const [plantDetailModal, setPlantDetailModal] = useState<{ isOpen: boolean; batch: Batch | null }>({
        isOpen: false,
        batch: null
    });
    const [isClosingPlantDetail, setIsClosingPlantDetail] = useState(false);

    const closePlantDetailModal = () => {
        setIsClosingPlantDetail(true);
        setTimeout(() => {
            setIsClosingPlantDetail(false);
            setPlantDetailModal({ isOpen: false, batch: null });
        }, 190);
    };

    // Click-to-Fill State (Legacy-keeping for potentially manual, or removing if fully replaced)
    // We will replace handleBatchClick to open modal instead.


    // Relocation State
    const [movingBatch, setMovingBatch] = useState<Batch | null>(null);

    // Genetics List for Sidebar (Available Genetics for dropdown)
    // availableGenetics removed in favor of genetics

    // Edit Batch Modal State
    const [isEditBatchModalOpen, setIsEditBatchModalOpen] = useState(false);
    const [editBatchForm, setEditBatchForm] = useState<{ id: string, name: string, quantity: number | string, notes: string, start_date?: string }>({
        id: '', name: '', quantity: '', notes: '', start_date: ''
    });
    const [isUpdatingBatch, setIsUpdatingBatch] = useState(false);

    // Print State Control


    // Ref for detecting clicks outside map for deselection
    const mapContainerRef = useRef<HTMLDivElement>(null);
    const toolbarRef = useRef<HTMLDivElement>(null);
    const mapReportRef = useRef<HTMLDivElement>(null);
    const batchLabelsRef = useRef<HTMLDivElement>(null);

    const handlePrintMap = useReactToPrint({
        contentRef: mapReportRef,
        documentTitle: `Mapa_Distribucion_${new Date().toLocaleDateString('es-AR').replace(/\//g, '-')}`,

        // =========================================================================================
        // 🛡️ BLINDAJE DE IMPRESIÓN (¡NO TOCAR!) 🛡️
        // Este 'pageStyle' se inyecta directamente en el <iframe> virtual que crea react-to-print.
        // Es la ÚNICA FORMA de vencer al dark mode global de index.css o StyledComponents.
        // Si se borra esto, el mapa se imprimirá con fondo azul oscuro y arruinará el PDF.
        // =========================================================================================
        pageStyle: `
            @page { size: landscape; margin: 10mm; }
            html, body {
                background-color: white !important;
                background-image: none !important;
                color: black !important;
                margin: 0 !important;
                padding: 0 !important;
            }
            /* Ocultar elementos no deseados que se hayan colado al clonar el DOM */
            .no-print { display: none !important; }
            /* Forzar el reporte a fondo blanco puro */
            .printable-report { background-color: white !important; }
            /* Forzar navegador a respetar el blanco ignorando el tema oscuro del OS */
            * {
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
            }
        `
    });

    const handlePrintLabels = useReactToPrint({
        contentRef: batchLabelsRef,
        documentTitle: `Etiquetas_Lotes`,
        pageStyle: `
            @page { size: portrait; margin: 10mm; }
            html, body {
                background-color: white !important;
                background-image: none !important;
                color: black !important;
                margin: 0 !important;
                padding: 0 !important;
            }
            .no-print { display: none !important; }
            .printable-report { background-color: white !important; }
            * {
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
            }
        `
    });




    const [isRelocatingSelection, setIsRelocatingSelection] = useState(false);
    const [isClosingEditBatch, setIsClosingEditBatch] = useState(false);

    const closeEditBatchModal = () => {
        setIsClosingEditBatch(true);
        setTimeout(() => {
            setIsEditBatchModalOpen(false);
            setIsClosingEditBatch(false);
        }, 200);
    };

    // --- SIDEBAR GROUP EXPANSION STATE ---
    const [expandedSidebarGroups, setExpandedSidebarGroups] = useState<Set<string>>(new Set());
    const [expandedDryingGroups, setExpandedDryingGroups] = useState<Set<string>>(new Set());

    const toggleSidebarGroupExpansion = (groupId: string) => {
        setExpandedSidebarGroups(prev => {
            const next = new Set(prev);
            if (next.has(groupId)) next.delete(groupId);
            else next.add(groupId);
            return next;
        });
    };

    const toggleDryingGroupExpansion = (groupId: string) => {
        setExpandedDryingGroups(prev => {
            const next = new Set(prev);
            if (next.has(groupId)) next.delete(groupId);
            else next.add(groupId);
            return next;
        });
    };

    const activeMap = cloneMaps.find(m => m.id === activeMapId);

    const handleOpenEditBatch = (batch: Batch) => {
        // Strip the date/time suffix from the name so users don't confuse it
        // Batch names follow the pattern: "PREFIX - DD/MM/YY HH:mm" or "SEM - PREFIX - DD/MM/YY HH:mm"
        // We remove the last " - DD/MM/YY HH:mm" segment
        const nameWithoutDate = batch.name.replace(/\s*-\s*\d{2}\/\d{2}\/\d{2}\s+\d{2}:\d{2}$/, '');

        setEditBatchForm({
            id: batch.id,
            name: nameWithoutDate,
            quantity: batch.quantity.toString(),
            notes: batch.notes || '',
            start_date: batch.start_date ? batch.start_date.split('T')[0] : (batch.created_at ? batch.created_at.split('T')[0] : '')
        });
        setIsEditBatchModalOpen(true);
        // Close other modals if open
        setPlantDetailModal({ ...plantDetailModal, isOpen: false });
    };

    const [allRooms, setAllRooms] = useState<Room[]>([]);

    // Transplant Modal State
    const [isTransplantModalOpen, setIsTransplantModalOpen] = useState(false);
    const [isClosingTransplant, setIsClosingTransplant] = useState(false);
    const [isClosingDistribution, setIsClosingDistribution] = useState(false);
    const [isClosingSingleDistribution, setIsClosingSingleDistribution] = useState(false);

    const closeTransplantModal = () => {
        setIsClosingTransplant(true);
        setTimeout(() => {
            setIsTransplantModalOpen(false);
            setIsClosingTransplant(false);
        }, 200);
    };

    const [isSingleDistributeConfirmOpen, setIsSingleDistributeConfirmOpen] = useState(false);
    const [singleDistributionData, setSingleDistributionData] = useState<{ batchId: string, position: string, quantity: number, mapId: string } | null>(null);

    const [transplantForm, setTransplantForm] = useState<{ batchId: string, quantity: number }>({ batchId: '', quantity: 1 });

    const [isHarvestModalOpen, setIsHarvestModalOpen] = useState(false);
    const [isClosingHarvestModal, setIsClosingHarvestModal] = useState(false);

    const closeHarvestModal = () => {
        setIsClosingHarvestModal(true);
        setTimeout(() => {
            setIsHarvestModalOpen(false);
            setIsClosingHarvestModal(false);
        }, 300);
    };

    // Toast State
    const [toastModal, setToastModal] = useState<{ isOpen: boolean; message: string; type: 'success' | 'error' | 'info' }>({
        isOpen: false, message: '', type: 'info'
    });
    const showToast = (message: string, type: 'success' | 'error' | 'info' = 'info') => {
        setToastModal({ isOpen: true, message, type });
    };

    useEffect(() => {
        // Fetch all rooms for transplant destination selection
        const fetchAllRooms = async () => {
            const rooms = await roomsService.getRooms();
            setAllRooms(rooms);
        };
        fetchAllRooms();
    }, []);


    // HARVEST CONFIRM HANDLER
    // HARVEST CONFIRM HANDLER
    const [harvestTargetMapId, setHarvestTargetMapId] = useState<string | null>(null); // Track which map is being harvested

    // HARVEST CONFIRM HANDLER
    const handleHarvestConfirm = async (selectedBatchesData: { id: string, weight: number }[], targetRoomId?: string) => {
        try {
            await roomsService.harvestBatches(selectedBatchesData, targetRoomId);
            showToast('Cosecha registrada correctamente', 'success');

            // Reload data after delay
            if (id) await loadData(id);

        } catch (error) {
            console.error(error);
            showToast('Error al registrar cosecha', 'error');
        }
    };

    // FINALIZE DRYING BATCH HANDLER
    const [finalizeBatch, setFinalizeBatch] = useState<{ id: string, name: string, quantity: number } | null>(null);
    const [finalizeBatches, setFinalizeBatches] = useState<Batch[] | null>(null); // For group finalization
    const [finalWeight, setFinalWeight] = useState('');
    const [finalNotes, setFinalNotes] = useState('');
    const [isClosingFinalize, setIsClosingFinalize] = useState(false);
    const [isFinalizing, setIsFinalizing] = useState(false);

    const handleOpenFinalize = (batch: Batch) => {
        setFinalizeBatch({ id: batch.id, name: batch.genetic?.name || batch.name, quantity: batch.quantity });
        setFinalizeBatches(null);
        setFinalWeight('');
        setFinalNotes('');
    };

    const handleOpenGroupFinalize = (e: React.MouseEvent, batches: Batch[]) => {
        e.stopPropagation();
        setFinalizeBatches(batches);
        setFinalizeBatch(null);
        setFinalWeight('');
        setFinalNotes('');
    };

    const handleConfirmFinalize = async () => {
        if (!finalizeBatch && !finalizeBatches) return;

        const weight = parseFloat(finalWeight);
        if (isNaN(weight) || weight < 0) {
            alert("Por favor ingresa un peso válido.");
            return;
        }

        setIsFinalizing(true);

        try {
            if (finalizeBatches) {
                // Group Finalization Logic
                const totalPlants = finalizeBatches.reduce((acc, b) => acc + b.quantity, 0);
                if (totalPlants === 0) {
                    alert("No hay plantas para finalizar en este grupo.");
                    setIsFinalizing(false);
                    return;
                }

                const weightPerPlant = weight / totalPlants;
                const promises = finalizeBatches.map(batch => {
                    const batchWeight = weightPerPlant * batch.quantity;
                    return roomsService.finalizeBatch(batch.id, batchWeight, finalNotes);
                });

                await Promise.all(promises);

                // Animate Close
                setIsClosingFinalize(true);
                setTimeout(async () => {
                    showToast(`Grupo finalizado: ${finalizeBatches.length} lotes enviados a stock`, 'success');
                    setFinalizeBatches(null);
                    setIsClosingFinalize(false);
                    // Silent refresh to avoid flicker
                    if (id) await loadData(id, false);
                    setIsFinalizing(false);
                }, 200);

            } else if (finalizeBatch) {
                // Single Batch Finalization Logic
                const success = await roomsService.finalizeBatch(finalizeBatch.id, weight, finalNotes);
                if (success) {
                    // Animate Close
                    setIsClosingFinalize(true);
                    setTimeout(async () => {
                        showToast("Lote finalizado y enviado a stock", 'success');
                        setFinalizeBatch(null);
                        setIsClosingFinalize(false);
                        // Silent refresh to avoid flicker
                        if (id) await loadData(id, false);
                        setIsFinalizing(false);
                    }, 200);
                } else {
                    showToast("Error al finalizar el lote", 'error');
                    setIsFinalizing(false);
                }
            }
        } catch (error) {
            console.error(error);
            showToast("Error al finalizar", 'error');
            setIsFinalizing(false);
        }
    };

    const handleTransplant = async (
        destinationRoomId: string,
        singles: string[],
        groupsPayload: { name: string, batchIds: string[] }[],
        substrateId?: string,
        estimatedVolume?: number
    ) => {
        if (!room) return;

        try {
            // Determine the new stage based on destination room type
            const { data: toRoom } = await supabase.from('rooms').select('type').eq('id', destinationRoomId).single();
            let newStage: BatchStage = 'vegetation'; // Default fallback
            if (toRoom?.type === 'flowering' || toRoom?.type === 'living_soil') {
                newStage = 'flowering';
            } else if (toRoom?.type === 'drying') {
                newStage = 'drying';
            } else if (toRoom?.type === 'clones') {
                newStage = 'clones';
            }

            // 1. Move Singles (Existing Logic)
            if (singles.length > 0) {
                await roomsService.moveBatches(singles, destinationRoomId, undefined); // Pass undefined to preserve notes
            }

            // 2. Process Groups (Merge Logic)
            if (groupsPayload && groupsPayload.length > 0) {
                const groupPromises: Promise<void>[] = [];

                for (const group of groupsPayload) {
                    if (group.batchIds.length === 0) continue;

                    // USER REQUIREMENT: Tracking IDs must NEVER change.
                    // Previous logic merged batches into a new one, destroying IDs.
                    // New logic: Move each batch individually.
                    // We treat the "Group Name" as a labeling/note preference, not a database merge.

                    for (const batchId of group.batchIds) {
                        const batch = room.batches?.find(b => b.id === batchId);

                        if (batch) {
                            // Enqueue operation
                            groupPromises.push((async () => {
                                // Update batch info without logging a redundant movement
                                await roomsService.updateBatch(batchId, {
                                    stage: newStage, // Set dynamically instead of hardcoded 'vegetation'
                                    group_name: group.name, // Native DB grouping via new column
                                    current_room_id: destinationRoomId,
                                    clone_map_id: null,
                                    grid_position: null
                                }); // Note omitted intentionally to prevent double-logging

                                // Move the batch physically AND log the Transplant event simultaneously
                                await roomsService.moveBatch(
                                    batchId,
                                    room.id,
                                    destinationRoomId,
                                    `Transplante: Inicio de ${newStage === 'flowering' ? 'Floración' : 'Vegetación'}`,
                                    undefined,
                                    undefined,
                                    undefined,
                                    user?.id // Triggers the unified batch_movement write
                                );
                            })());
                        }
                    }
                }
                // Execute all group batch operations in parallel
                await Promise.all(groupPromises);
            }

            // 3. Substrate Deduction (If provided)
            if (substrateId && estimatedVolume && estimatedVolume > 0) {
                // Determine source or target room ID for logging purposes (usually destination room context makes more sense for tracking, but could be source room).
                // Assuming `user?.id` is available and has organization context via JWT.
                const { error: stockError } = await supabase.rpc('execute_transplant_with_stock', {
                    _substrate_id: substrateId,
                    _estimated_volume: estimatedVolume,
                    _performed_by: user?.id,
                    _room_id: destinationRoomId,
                    _organization_id: getSelectedOrgId()
                });

                if (stockError) {
                    console.error("Error deducting substrate stock during transplant:", stockError);
                    showToast(`Transplante completado, pero ocurrió un error al descontar el sustrato: ${stockError.message}`, 'error');
                } else {
                    console.log(`Successfully deducted ${estimatedVolume}L of substrate ${substrateId}`);
                }
            }

            // Refresh
            if (id) await loadData(id);
        } catch (error) {
            console.error("Error transplanting batches:", error);
            alert("Hubo un error al mover los esquejes.");
        }
    };

    const handleUpdateBatch = async () => {
        if (!editBatchForm.id) return;
        setIsUpdatingBatch(true);
        try {
            // Reconstruct the full batch name by appending the formatted date
            const dateObj = editBatchForm.start_date ? new Date(editBatchForm.start_date + 'T12:00:00') : new Date();
            const formattedDate = format(dateObj, 'dd/MM/yy HH:mm');
            const fullName = `${editBatchForm.name} - ${formattedDate}`;

            await roomsService.updateBatch(editBatchForm.id, {
                name: fullName,
                quantity: Number(editBatchForm.quantity),
                notes: editBatchForm.notes,
                start_date: editBatchForm.start_date ? `${editBatchForm.start_date}T12:00:00.000Z` : undefined,
                current_room_id: room?.id
            }, user?.id, 'Edición Manual de Lote');

            // Refresh
            if (id) {
                // Silent refresh if possible, or just standard load
                await loadData(id, false, false);
            }
            closeEditBatchModal();
            showToast("Lote actualizado correctamente", 'success');
        } catch (error) {
            console.error("Error updating batch", error);
            showToast("Error al actualizar el lote", 'error');
        } finally {
            setIsUpdatingBatch(false);
        }
    };



    // Room Update Handler
    const handleUpdateRoom = async () => {
        if (!room || !editRoomName || !editRoomType) return;
        setIsUpdatingRoom(true); // Local loading state
        try {
            await roomsService.updateRoom(room.id, {
                name: editRoomName,
                type: editRoomType as any,
                start_date: editRoomStartDate ? `${editRoomStartDate}T12:00:00.000Z` : undefined
            });
            // Reload data without triggering global loading screen if possible, or accept short delay.
            // The flicker happened because setLoading(true) unmounted the modal/page content.
            if (id) await loadData(id);

            closeEditRoomModal();
        } catch (error) {
            console.error("Error updating room:", error);
            alert("Error al actualizar la sala");
        } finally {
            setIsUpdatingRoom(false);
        }
    };




    const handleCreateMap = async () => {
        if (!newMapName || !newMapRows || !newMapCols) {
            showToast("Por favor completa todos los campos.", 'error');
            return;
        }

        setIsCreatingMap(true); // Local loading
        try {
            const newMap = await roomsService.createCloneMap({
                room_id: room?.id || '',
                name: newMapName,
                grid_rows: Number(newMapRows),
                grid_columns: Number(newMapCols)
            });

            if (newMap) {
                setCloneMaps(prev => [...prev, newMap]);
                closeMapModal();
                setNewMapName('');
                setNewMapRows('');
                setNewMapCols('');
            }
        } catch (error) {
            console.error("Error creating map", error);
            alert("Error al crear el mapa.");
        } finally {
            setIsCreatingMap(false);
        }
    };


    const handleEditMapClick = (e: React.MouseEvent, map: CloneMap) => {
        e.stopPropagation();
        setEditingMapId(map.id);
        setEditMapName(map.name);
        setEditMapRows(map.grid_rows);
        setEditMapCols(map.grid_columns);
        setIsEditMapModalOpen(true);
    };

    const handleUpdateMap = async () => {
        if (!editingMapId || !editMapName) return;
        const rows = Number(editMapRows);
        const cols = Number(editMapCols);

        if (!rows || rows < 1 || !cols || cols < 1) {
            alert("Por favor ingresa filas y columnas válidas.");
            return;
        }

        setIsUpdatingMap(true); // Local loading
        try {
            const updatedMap = await roomsService.updateCloneMap(editingMapId, {
                name: editMapName,
                grid_rows: rows,
                grid_columns: cols
            });

            if (updatedMap) {
                setCloneMaps(prev => prev.map(m => m.id === editingMapId ? updatedMap : m));
                closeEditMapModal();
                setEditingMapId(null);
            }
        } catch (error) {
            console.error("Error updating map:", error);
            alert("Error al actualizar el mapa.");
        } finally {
            setIsUpdatingMap(false);
        }

    };

    const loadData = React.useCallback(async (roomId: string, isInitial = false, showLoading = true) => {
        if (showLoading) setLoading(true);
        try {


            const loadPromise = async () => {
                const [roomData, tasksData, usersData, stickiesData, mapsData, metricsResult, geneticsData, taskTypesData] = await Promise.all([
                    roomsService.getRoomById(roomId),
                    tasksService.getTasksByRoomId(roomId),
                    usersService.getUsers(),
                    stickiesService.getStickies(roomId),
                    roomsService.getCloneMaps(roomId),
                    roomsService.getCloneSuccessMetrics(roomId),
                    roomsService.getGenetics(), // Always fetch genetics to be safe or keep conditional?
                    tasksService.getTaskTypes()
                ]);
                return { roomData, tasksData, usersData, stickiesData, mapsData, metricsResult, geneticsData, taskTypesData };
            };

            const [results] = await Promise.all([
                loadPromise()
            ]);

            const { roomData, tasksData, usersData, stickiesData, mapsData, metricsResult, geneticsData, taskTypesData } = results;

            setRoom(roomData);
            setTasks(tasksData);
            setUsers(usersData);
            setStickies(stickiesData);
            setCloneMaps(mapsData);
            setMetricsData(metricsResult);
            setTaskTypes(taskTypesData);

            // Fetch insumos for substrate selection
            try {
                const fetchedInsumos = await getInsumos();
                setInsumos(fetchedInsumos);
            } catch (insumoError) {
                console.error("Error loading insumos:", insumoError);
            }

            // Conditional genetics set-reusing the fetched data if needed
            if (['clones', 'esquejes', 'esquejera', 'germinacion', 'germinación', 'germination', 'semillero', 'living_soil'].includes((roomData?.type as string)?.toLowerCase())) {
                setGenetics(geneticsData);
            }

        } catch (error) {
            console.error("Error loading room data", error);
        } finally {
            if (showLoading) setLoading(false);
        }
    }, []);

    const confirmDeleteMap = async () => {
        if (!mapIdToDelete) return;

        setIsDeletingMap(true); // Local loading
        try {
            await roomsService.deleteCloneMap(mapIdToDelete);
            if (id) {
                const maps = await roomsService.getCloneMaps(id);
                setCloneMaps(maps);
            }
            if (activeMapId === mapIdToDelete) setActiveMapId(null);

            closeDeleteMapModal();
            setMapIdToDelete(null);
            showToast("Mapa eliminado correctamente!", 'success');
        } catch (error) {
            console.error("Error deleting map", error);
            showToast("Error al eliminar el mapa.", 'error');
        } finally {
            setIsDeletingMap(false);
        }
    };




    const handleClearMap = async () => {
        if (!activeMapId) return;
        try {
            setLoading(true);
            await roomsService.clearMap(activeMapId);
            if (id) await loadData(id);
        } catch (error) {
            console.error("Error clearing map", error);
        } finally {
            setLoading(false);
            setIsClearMapConfirmOpen(false);
        }
    };




    const handleOpenHistory = async () => {
        if (!room) return;
        setHistoryLoading(true);
        setIsHistoryModalOpen(true);
        try {
            const history = await roomsService.getRoomMovements(room.id);
            setRoomHistory(history);
        } catch (error) {
            console.error("Error loading history", error);
        } finally {
            setHistoryLoading(false);
        }
    };

    // Click-to-Fill Handler & Plant Detail Trigger
    const handleEditBatchClick = (e: React.MouseEvent, batch: Batch) => {
        e.stopPropagation();
        handleOpenEditBatch(batch);
    };

    const handleDeleteBatchClick = async (e: React.MouseEvent, batch: Batch) => {
        e.stopPropagation();

        if (!batch.clone_map_id) {
            setAvailableBatchToDelete(batch);
            return;
        }

        // If it's already in the room, trigger the mortality reason modal
        setDeleteConfirm({ isOpen: true, batch });
    };

    const handleConfirmDeleteAvailable = async () => {
        if (!availableBatchToDelete) return;
        setIsDeletingAvailable(true);
        try {
            await roomsService.deleteBatch(availableBatchToDelete.id, 'Eliminado de Lotes Disponibles (No contabilizado en bajas)');
            if (id) await loadData(id, false, false);
            showToast("Lote disponible eliminado exitosamente", 'success');
        } catch (error) {
            console.error("Error al eliminar lote disponible:", error);
            showToast("Ocurrió un error al eliminar el lote disponible", 'error');
        } finally {
            setIsDeletingAvailable(false);
            setAvailableBatchToDelete(null);
        }
    };

    const [isBulkActionsOpen, setIsBulkActionsOpen] = useState(false); // Dropdown State

    // Living Soil States
    const [isSowingModalOpen, setIsSowingModalOpen] = useState(false);
    const [sowingPosition, setSowingPosition] = useState<string | null>(null);
    const [newSowingBatch, setNewSowingBatch] = useState({
        genetic_id: '',
        name: '', // Will be generated or manual
        quantity: 1, // Usually 1 per slot
        notes: ''
    });

    // ... existing handleBatchClick ...


    const handleBatchClick = async (batch: Batch | null, position?: string) => {
        // 1. RELOCATION LOGIC (Selection)
        if (isRelocatingSelection) {
            if (!position || !room || !activeMapId) return;

            // Execute Bulk Move
            const activeMap = cloneMaps.find(m => m.id === activeMapId);
            if (!activeMap) return;

            // Get selected batches objects
            const batchesToMove = room.batches?.filter(b => selectedBatchIds.has(b.id)) || [];
            if (batchesToMove.length === 0) return;

            // 1. Find Anchor (Top-Left)
            let minR = Infinity;
            let minC = Infinity;

            // Helper to parse "A1" -> r, c
            const parsePos = (posStr: string) => {
                const match = posStr.match(/^([A-Z]+)(\d+)$/);
                if (!match) return { r: 0, c: 0 };
                // Assume single letter row for now or use getRowIndex logic
                const r = match[1].charCodeAt(0) - 65;
                const c = parseInt(match[2], 10) - 1;
                return { r, c };
            };

            batchesToMove.forEach(b => {
                if (b.grid_position) {
                    const { r, c } = parsePos(b.grid_position);
                    if (r < minR) minR = r;
                    if (c < minC) minC = c;
                }
            });

            // Target Position
            const { r: targetR, c: targetC } = parsePos(position);

            const rowDelta = targetR - minR;
            const colDelta = targetC - minC;

            // 2. Calculate New Positions and Validate
            const updates: { id: string, newPos: string }[] = [];
            const occupiedPositions = new Set(
                room.batches?.filter(b => b.clone_map_id === activeMapId && !selectedBatchIds.has(b.id))
                    .map(b => b.grid_position)
            );

            for (const b of batchesToMove) {
                if (!b.grid_position) continue;
                const { r, c } = parsePos(b.grid_position);
                const newR = r + rowDelta;
                const newC = c + colDelta;

                // Check Bounds
                if (newR < 0 || newR >= activeMap.grid_rows || newC < 0 || newC >= activeMap.grid_columns) {
                    showToast("La reubicación excede los límites del mapa.", 'error');
                    return;
                }

                const newPosStr = `${getRowLabel(newR)}${newC + 1}`;

                // Check Collisions
                if (occupiedPositions.has(newPosStr)) {
                    showToast(`Conflicto en la posición ${newPosStr}. La celda está ocupada.`, 'error');
                    return;
                }

                updates.push({ id: b.id, newPos: newPosStr });
            }

            // 3. Execute
            setLoading(true);
            try {
                const promises = updates.map(u =>
                    roomsService.updateBatch(u.id, { grid_position: u.newPos }, user?.id, 'Reubicación en mapa')
                );
                await Promise.all(promises);

                showToast(`Se reubicaron ${updates.length} lotes corrextamente.`, 'success');
                setIsRelocatingSelection(false);
                setSelectedBatchIds(new Set()); // Clear selection? Or keep it? Clearing is safer visually.
                if (id) await loadData(id);

            } catch (error) {
                console.error("Relocation error", error);
                showToast("Error al reubicar lotes.", 'error');
            } finally {
                setLoading(false);
            }
            return;
        }

        // 2. Relocation Logic (Legacy Single Batch Moving)
        if (movingBatch) {
            if (batch) {
                showToast("La celda destino está ocupada. Selecciona una celda vacía.", 'error');
                return;
            }
            if (position && activeMapId) {
                setMoveConfirm({ isOpen: true, position });
            }
            return;
        }

        // 3. Normal Selection/Click Interaction
        if (!batch) {
            // Empty cell click
            // ... Sowing Logic ...
            if (position && activeMapId && (room?.type === 'living_soil' || room?.type === 'clones')) {
                setSowingPosition(position);
                setIsSowingModalOpen(true);
            }
            return;
        }

        // Bulk Edit Trigger (Only if NOT relocating)
        if (isSelectionMode && selectedBatchIds.has(batch.id) && selectedBatchIds.size > 1) {
            setBulkEditForm({ stage: '', notes: '' });
            setIsBulkEditModalOpen(true);
            return;
        }

        // Standard Single Batch Click
        setSelectedBatchId(batch.id);
        setPlantDetailModal({ isOpen: true, batch });
    };

    const handleBulkEditSubmit = async () => {
        if (selectedBatchIds.size === 0) return;

        setLoading(true);
        try {
            const updates: any = {};
            if (bulkEditForm.stage) updates.stage = bulkEditForm.stage;
            if (bulkEditForm.notes) {
                // We might want to append notes or replace? Replace typical for bulk.
                // Or maybe append? Let's replace for now based on user request "modificador de Etapa y notas".
                // If simple text, it's a replace/set.
                updates.notes = bulkEditForm.notes;
            }

            if (Object.keys(updates).length === 0) {
                setIsBulkEditModalOpen(false);
                setLoading(false);
                return;
            }

            // Execute Updates
            const promises = Array.from(selectedBatchIds).map(id =>
                roomsService.updateBatch(
                    id,
                    { ...updates, current_room_id: room?.id },
                    user?.id,
                    'Edición Masiva'
                )
            );

            await Promise.all(promises);

            if (id) await loadData(id);
            setIsBulkEditModalOpen(false);
            setSelectedBatchIds(new Set()); // Clear selection after edit? Usually yes.
            // setIsSelectionMode(false); // Maybe exit selection mode too?
            showToast(`Se actualizaron ${selectedBatchIds.size} lotes correctamente.`, 'success');

        } catch (error) {
            console.error("Error bulk updating:", error);
            showToast("Error al actualizar lotes.", 'error');
        } finally {
            setLoading(false);
        }
    };

    // Helper functions for Excel-style row labels (A, ..., Z, AA, AB, ...)
    const getRowLabel = (n: number) => {
        let s = "";
        while (n > 0) {
            let remainder = (n - 1) % 26;
            s = String.fromCharCode(65 + remainder) + s;
            n = Math.floor((n - 1) / 26);
        }
        return s;
    };

    const getRowIndex = (s: string) => {
        let n = 0;
        for (let i = 0; i < s.length; i++) {
            n = n * 26 + (s.charCodeAt(i) - 64);
        }
        return n;
    };

    const handleSowingSubmit = async () => {
        if (!room || !activeMapId || !sowingPosition || !newSowingBatch.genetic_id) {
            showToast("Faltan datos para realizar la siembra.", 'error');
            return;
        }

        try {
            setLoading(true);
            // 1. Get Genetic for Nomenclatura
            const genetic = genetics.find(g => g.id === newSowingBatch.genetic_id);
            const prefix = genetic?.nomenclatura || 'GEN';

            // 2. Generate Tracking Code (Simplified, or rely on backend triggers/service)
            // Ideally service handles this. Let's use createBatch logic from service if possible or custom manual one.
            // We'll use a specific service method or standard createBatch.
            // But standard createBatch doesn't auto-set tracking code easily without logic.
            // Let's rely on backend or service logic handling naming if passed empty?
            // For now, let's just pass basic info and let DB/Service handle naming if needed or generate here.

            // Getting next sequence is hard on client without query.
            // Let's use a simpler approach: Name = "S-POS" or similar temporarily, or assume service fixes it.
            // Actually `roomsService.batchAssignToMap` does logic. Maybe we can misuse it? No.

            // Let's use `roomsService.createBatch` but we need to generate unique name/code.
            // Ideally we'd have `roomsService.sowBatch(...)`.

            await roomsService.createBatch({
                name: `${prefix} -${sowingPosition} `, // Temporary name until better logic
                quantity: 1,
                stage: 'seedling', // Start as Seedling
                genetic_id: newSowingBatch.genetic_id,
                start_date: new Date().toISOString(),
                current_room_id: room.id,
                clone_map_id: activeMapId,
                grid_position: sowingPosition,
                notes: newSowingBatch.notes,
                tracking_code: Math.floor(1000 + Math.random() * 9000).toString()
            }, user?.id);

            await loadData(room.id);
            setIsSowingModalOpen(false);
            setSowingPosition(null);
            setNewSowingBatch({ genetic_id: '', name: '', quantity: 1, notes: '' });
            showToast("Siembra registrada con éxito.", 'success');

        } catch (error) {
            console.error(error);
            showToast("Error al registrar siembra.", 'error');
        } finally {
            setLoading(false);
        }
    };


    const handleOpenTransplant = (e: React.MouseEvent, room: Room, batch: Batch | null) => {
        e.stopPropagation();

        if (batch) {
            setTransplantForm({ ...transplantForm, batchId: batch.id, quantity: batch.quantity });
        } else {
            setTransplantForm({ batchId: '', quantity: 1 });
        }
        setIsTransplantModalOpen(true);
    };

    // Living Soil Handlers
    const handleUpdateStage = async (batch: Batch, newStage: any) => {
        try {
            await roomsService.updateBatchStage(batch.id, newStage, user?.id);
            if (id) await loadData(id);
            showToast(`Etapa actualizada a ${newStage} `, 'success');
            // Update the modal's internal batch if possible, or close it.
            setPlantDetailModal({ isOpen: false, batch: null });
        } catch (error) {
            console.error(error);
            showToast("Error al actualizar etapa", 'error');
        }
    };

    const handleSaveNotes = async (batch: Batch, notes: string) => {
        try {
            const hasContent = !!notes?.trim();
            const updates = {
                notes,
                has_alert: hasContent,
                current_room_id: room?.id // Ensure room context for history log
            };
            await roomsService.updateBatch(batch.id, updates, user?.id, 'Nota actualizada');

            // Update local state
            if (activeMapId && room?.batches) {
                const updatedBatches = room.batches.map(b => b.id === batch.id ? { ...b, ...updates } : b);
                setRoom(prev => prev ? { ...prev, batches: updatedBatches } : null);
            }
            showToast("Nota guardada", 'success');
        } catch (e) {
            console.error(e);
            showToast("Error al guardar nota", 'error');
        }
    };

    const handleConfirmMove = async () => {
        if (!moveConfirm.position || !room || !activeMapId || !movingBatch) return;

        try {
            // Correct signature: batchId, fromRoomId, toRoomId, notes, quantity, gridPosition, cloneMapId
            await roomsService.moveBatch(
                movingBatch.id,
                room.id,
                room.id,
                'Reubicación manual',
                undefined,
                moveConfirm.position || undefined,
                activeMapId || undefined
            );

            setMovingBatch(null);
            if (id) {
                const updatedRoom = await roomsService.getRoomById(id);
                if (updatedRoom) setRoom(updatedRoom);
            }
            showToast("Lote reubicado correctamente", 'success');
        } catch (error) {
            console.error("Move error", error);
            showToast("Error moviendo lote", 'error');
        } finally {
            setMoveConfirm({ isOpen: false, position: null });
            // Ensure moving state is cleared on error too if desired,
            // but keeping it might allow retry. The user requested "cancel" explicitly.
            setMovingBatch(null);
        }
    };

    const handleConfirmDelete = async (reason: string, notes: string) => {
        const { batch } = deleteConfirm;
        if (!batch) return;

        setIsDeletingBatch(true);
        try {
            const finalReason = notes ? `${reason} - ${notes}` : reason;
            await roomsService.deleteBatch(batch.id, finalReason);

            closeDeleteConfirm();
            if (id) {
                // Silent refresh if possible
                // Silent refresh if possible
                await loadData(id, false, false);
                // Remove from selection if present
                if (selectedBatchIds.has(batch.id)) {
                    const newSet = new Set(selectedBatchIds);
                    newSet.delete(batch.id);
                    setSelectedBatchIds(newSet);
                }
            }
        } catch (error) {
            console.error("Error deleting single plant", error);
            alert("Error al eliminar la planta");
        } finally {
            setIsDeletingBatch(false);
        }
    };

    /*
    // Legacy Toggle Alert - Removed in favor of Register Observation
    const handleToggleAlert = async () => { ... }
    */




    const handleDeleteBatchFromGroup = async (batch: Batch, quantityDiscard: number) => {
        // Validation
        if (quantityDiscard <= 0) return;

        const isFullDelete = quantityDiscard >= batch.quantity;

        if (window.confirm(`¿Estás seguro de descartar ${quantityDiscard} unidades de ${batch.tracking_code || 'este lote'}?`)) {
            try {
                if (isFullDelete) {
                    await roomsService.deleteBatch(batch.id);
                } else {
                    // Update Quantity (Partial Discard)
                    await roomsService.updateBatch(batch.id, {
                        ...batch,
                        quantity: batch.quantity - quantityDiscard,
                        // Append note about discard?
                        notes: (batch.notes || '') + `\n[${new Date().toLocaleDateString()}] Descartadas ${quantityDiscard} unidades.`
                    });
                }

                // Refresh
                if (id) await loadData(id);

            } catch (e) { console.error(e); alert("Error al descartar"); }
        }
    };

    const handleAutoAssign = async () => {
        const { batch, quantity } = assignModal;
        const qty = Number(quantity);
        if (!batch || !room || !activeMapId || qty <= 0) return;

        // 1. Find Empty Spots
        const map = cloneMaps.find(m => m.id === activeMapId);
        if (!map) return;

        const currentBatchesInMap = room.batches?.filter(b => b.clone_map_id === activeMapId) || [];
        const occupiedPositions = new Set(currentBatchesInMap.map(b => b.grid_position));

        const targetPositions: string[] = [];
        let assignedCount = 0;

        // Iterate grid to find empty spots (1-based: getRowLabel(1)='A', getRowLabel(2)='B', ...)
        for (let r = 1; r <= map.grid_rows; r++) {
            for (let c = 1; c <= map.grid_columns; c++) {
                if (assignedCount >= qty) break;
                const rowLabel = getRowLabel(r);
                const pos = `${rowLabel}${c}`;

                if (!occupiedPositions.has(pos)) {
                    targetPositions.push(pos);
                    assignedCount++;
                }
            }
        }

        if (targetPositions.length < qty) {
            alert(`No hay suficiente espacio libre en el mapa.Se necesitan ${qty} espacios, hay ${targetPositions.length}.`);
            return;
        }

        setIsAutoAssigning(true); // Local loading
        try {
            // Check if we are assigning a group (qty > 1 and batch has siblings in room)
            // Or just multiple batches from a group
            // We need to find 'qty' batches from the available stock that match the criteria

            // Available batches in this room, not in map, same genetic, same creation date (roughly) or just same genetic?
            // Sidebar grouping uses genetic_id and created_at.
            // batch is the root of the group (or one of them).



            if (qty === 1) {
                // Single unit assignment
                // Only use batchesToAssign if it was set, or just use batch.id directly if we are sure
                // Since batchesToAssign was declared but not used in the new logic, let's use batch.id directly
                // matching the original logic which used batchesToAssign[0] = batch.id
                await roomsService.batchAssignToMap(batch.id, activeMapId, targetPositions, room.id, user?.id);
            } else {
                // Multi-unit distribution logic
                const availableBatches = room.batches?.filter(b =>
                    !b.clone_map_id &&
                    b.quantity > 0 &&
                    b.current_room_id === room.id &&
                    (b.genetic_id === batch.genetic_id || b.name === batch.name)
                ) || [];

                availableBatches.sort((a, b) => a.tracking_code?.localeCompare(b.tracking_code || '') || a.id.localeCompare(b.id));

                const totalAvailableUnits = availableBatches.reduce((sum, b) => sum + b.quantity, 0);

                if (totalAvailableUnits < qty) {
                    alert(`No se encontraron suficientes lotes disponibles.Solicitados: ${qty}, Disponibles: ${totalAvailableUnits} `);
                    setIsAutoAssigning(false);
                    return;
                }

                let remainingQtyToAssign = qty;
                let positionIndex = 0;
                const promises = [];

                for (const b of availableBatches) {
                    if (remainingQtyToAssign <= 0) break;
                    const quantityFromThisBatch = Math.min(b.quantity, remainingQtyToAssign);
                    const positionsForThisBatch = targetPositions.slice(positionIndex, positionIndex + quantityFromThisBatch);

                    if (positionsForThisBatch.length > 0) {
                        promises.push(
                            roomsService.batchAssignToMap(b.id, activeMapId, positionsForThisBatch, room.id, user?.id)
                        );
                    }
                    remainingQtyToAssign -= quantityFromThisBatch;
                    positionIndex += quantityFromThisBatch;
                }
                await Promise.all(promises);
            }

            if (id) {
                const updatedRoom = await roomsService.getRoomById(id);
                if (updatedRoom) setRoom(updatedRoom);
            }
            handleCloseAssignModal();
            // Wait for modal to close partially before resetting
            setTimeout(() => setAssignModal({ isOpen: false, batch: null, quantity: 1 }), 300);

            showToast("Lotes asignados correctamente", 'success');

        } catch (error) {
            console.error("Auto assign error", error);
            showToast("Error asignando lotes", 'error');
        } finally {
            setIsAutoAssigning(false);
        }
    };
    // DnD Sensors
    const sensors = useSensors(
        useSensor(MouseSensor, { activationConstraint: { distance: 10 } }),
        useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 5 } })
    );

    const handleConfirmDistribution = async () => {
        if (!distributionData) return;

        setLoading(true);
        setIsDistributionConfirmOpen(false);

        try {
            const { batches, position, mapId, map } = distributionData;

            // Fix for AA+ rows
            const match = position.match(/^([A-Z]+)(\d+)$/);
            if (!match) {
                console.error("Invalid position format:", position);
                return;
            }
            const rowLabel = match[1];
            const colStr = match[2];

            const startRow = getRowIndex(rowLabel);
            const startCol = parseInt(colStr, 10);

            const success = await roomsService.bulkDistributeBatchesToMap(
                batches,
                mapId,
                startRow,
                startCol,
                map.grid_rows,
                map.grid_columns,
                user?.id
            );

            if (success) {
                if (id) await loadData(id);
            } else {
                alert("No se pudo completar la distribución. Verifica el espacio disponible.");
            }
        } catch (error) {
            console.error("Error distributing group", error);
            alert("Error al distribuir el grupo.");
        } finally {
            setLoading(false);
            setDistributionData(null);
        }
    };

    const [activeItem, setActiveItem] = useState<any>(null);

    // Memoized batches for LivingSoilGrid to prevent infinite loops
    const livingSoilBatches = useMemo(() => {
        return (room?.batches || []).filter(b => b.clone_map_id === activeMapId && b.quantity > 0);
    }, [room?.batches, activeMapId]);

    const handleDragStart = (event: any) => {
        setActiveItem(event.active.data.current);
    };

    const handleDragEnd = async (event: DragEndEvent) => {
        setActiveItem(null);
        const { active, over } = event;

        console.log("DRAG END:", { active, over, activeData: active?.data?.current, overId: over?.id });

        if (!over) {
            // Dropped outside
            return;
        }

        // If we are dropping on create-map-zone, we don't need an active map
        if (over.id !== 'create-map-zone' && !activeMapId) return;


        const targetId = over.id as string;

        if (targetId.startsWith('cell-')) {
            // Moved to a Grid Cell
            const position = active.data.current?.position || targetId.replace('cell-', '');

            // CHECK IF GENETIC DROP (New Batch Creation)
            if (active.data.current?.type === 'genetic') {
                const genetic = active.data.current?.genetic as Genetic;
                try {
                    let defaultStage: BatchStage = 'vegetation';
                    if (room?.type === 'flowering' || room?.type === 'living_soil') defaultStage = 'flowering';
                    else if (room?.type === 'drying') defaultStage = 'drying';
                    else if (room?.type === 'clones') defaultStage = 'clones';

                    await roomsService.createBatch({
                        name: genetic.name,
                        genetic_id: genetic.id,
                        quantity: 1,
                        stage: defaultStage, // Dynamic based on room
                        start_date: new Date().toISOString(),
                        current_room_id: room?.id,
                        clone_map_id: activeMapId || undefined,
                        grid_position: position,
                        notes: 'Creado desde Genetica',
                        tracking_code: Math.floor(1000 + Math.random() * 9000).toString()
                    });
                    if (id) await loadData(id);
                } catch (err) {
                    console.error("Error creating batch from genetic drop", err);
                }
                setLoading(false);
                return;
            }

            // CHECK IF BATCH GROUP DROP (Auto-Distribute Lote)
            if (active.data.current?.type === 'batch-group') {
                const group = active.data.current.group;
                if (!group || !activeMapId) return;

                const { root, children } = group;
                const allBatches = [root, ...(children || [])].filter(b => b.quantity > 0);

                if (allBatches.length === 0) return;

                const map = cloneMaps.find(m => m.id === activeMapId);
                if (!map) {
                    console.error("Map not found in cloneMaps");
                    return;
                }

                // Check Capacity
                const totalPlants = allBatches.reduce((a, b) => a + b.quantity, 0);

                // Calculate available slots from start position
                console.log("Distribution Debug-Target ID:", targetId);
                console.log("Distribution Debug-Resolved Position:", position);

                const match = position.match(/^([A-Z]+)(\d+)$/);
                if (!match) {
                    console.error("Invalid position for distribution:", position);
                    setLoading(false);
                    return;
                }
                const rowLabel = match[1];
                const colStr = match[2];

                const startRow = getRowIndex(rowLabel);
                const startCol = parseInt(colStr, 10);

                console.log("Distribution Debug-StartRow:", startRow, "StartCol:", startCol);

                const existingBatchesInMap = room?.batches?.filter(b => b.clone_map_id === activeMapId && b.quantity > 0) || [];
                const occupiedSet = new Set(existingBatchesInMap.map(b => b.grid_position));

                let freeSlots = 0;
                let r = startRow;
                let c = startCol;

                // Safety break to prevent infinite loops if logic fails
                let iterations = 0;
                const MAX_ITERATIONS = map.grid_rows * map.grid_columns * 2;

                while (r <= map.grid_rows) {
                    // Safety check
                    if (iterations++ > MAX_ITERATIONS) {
                        console.error("Infinite loop detected in distribution calculation");
                        break;
                    }

                    const pos = `${getRowLabel(r)}${c} `;
                    if (!occupiedSet.has(pos)) {
                        freeSlots++;
                    }

                    c++;
                    if (c > map.grid_columns) {
                        c = 1;
                        r++;
                    }
                }

                if (totalPlants > freeSlots) {
                    // Show Toast/Alert for capacity exceeded
                    // Using standard alert for now as requested "modal toast central" implies existing component or simple alert is better than nothing,
                    // and I'll use the existing ToastModal if available or simple alert.
                    // Given the constraint "debe desplegar un modal toast central flotante", I will use a custom state for a simple central modal if ToastModal isn't easy to hook up instantly without looking at it.
                    // But wait, there is a ToastModal imported. Let's try to use it if I can see how.
                    // Actually, for now, to ensure reliability, I will use window.alert but ideally I should check ToastModal usage.
                    // Improving: I will use a simple alert for now to ensure functionality, then can refine to ToastModal.
                    setToastState({
                        isOpen: true,
                        message: `Cantidad excedida: Tienes ${totalPlants} plantas pero solo ${freeSlots} espacios disponibles a partir de la celda seleccionada.`,
                        type: 'error'
                    });
                    setLoading(false);
                    return;
                }

                // logic to open custom modal instead of window.confirm
                setDistributionData({
                    batches: allBatches,
                    position: position,
                    mapId: activeMapId,
                    map: map
                });
                setIsDistributionConfirmOpen(true);
                setLoading(false);
                return;

            }

            const batch = active.data.current?.batch as Batch;
            const fromStock = active.data.current?.fromStock;

            const notes = `Movido a mapa ${activeMapId} celda ${position} `;

            // MAP MODE (VEGETATION / FLOWERING): DISTRIBUTE BATCH (INDIVIDUALIZATION)
            const isMapMode = ['vegetacion', 'vegetación', 'vegetation', 'flowering', 'floración', 'flora', 'living_soil'].includes((room?.type || '').toLowerCase());

            if (isMapMode && fromStock && batch.quantity > 0 && activeMapId) {
                // Open Custom Confirmation Modal instead of window.confirm
                setSingleDistributionData({
                    batchId: batch.id,
                    position: position,
                    quantity: batch.quantity,
                    mapId: activeMapId
                });
                setIsSingleDistributeConfirmOpen(true);
                setLoading(false);
                return;
            }

            // Logic for Splitting (Standard Esquejera Behavior)
            let splitQty = undefined;
            if (fromStock && batch.quantity > 1) {
                // Automatically split 1 unit when dragging from "Bulk" Stock
                splitQty = 1;
            }

            // If simply moving within map (no split, just move)
            if (!fromStock && batch.grid_position === position && batch.clone_map_id === activeMapId) {
                setLoading(false);
                return;
            }

            // API Call (Standard Move)
            try {
                await roomsService.moveBatch(batch.id, room?.id || null, room?.id || '', notes, splitQty, position, activeMapId || undefined, user?.id);
                // Refresh data to see the split result
                if (id) await loadData(id);
            } catch (err) {
                console.error(err);
                if (id) await loadData(id); // Revert on error
            } finally {
                setLoading(false);
            }
        }

        // CHECK IF DROPPED IN STOCK ZONE (Return to Stock)
        if (targetId === 'stock-zone') {
            const batch = active.data.current?.batch as Batch;
            if (!batch || !batch.clone_map_id) return; // Already in stock or invalid

            try {
                // Determine if we need to split?
                // Usually dragging back means "Unassign All of this batch" or "Unassign 1 unit"?
                // If it's a grid item (single), we move it back.

                // Move to room (null map, null position)
                await roomsService.moveBatch(batch.id, room?.id || null, room?.id || '', 'Devuelto a Stock', undefined, undefined, undefined, user?.id);
                if (id) await loadData(id);
            } catch (err) {
                console.error(err);
            } finally {
                setLoading(false);
            }
        }

        // CHECK IF DROPPED IN TRASH ZONE (Delete)
        if (targetId === 'trash-zone') {
            const batch = active.data.current?.batch as Batch;
            if (!batch) return;

            if (!window.confirm("¿Eliminar este lote permanentemente?")) {
                setLoading(false);
                return;
            }

            try {
                // Explicit reason for metrics
                await roomsService.deleteBatch(batch.id, 'Baja-Eliminado del Mapa', user?.id);
                if (id) await loadData(id);
            } catch (err) {
                console.error(err);
            } finally {
                setLoading(false);
            }
        }


        if (targetId === 'create-map-zone') {
            // CHECK IF BATCH GROUP
            if (active.data.current?.type === 'batch-group') {
                const group = active.data.current?.group;
                if (!group) return;

                const isMapCapable = ['clones', 'esquejes', 'esquejera', 'vegetacion', 'vegetación', 'vegetation', 'flowering', 'floración', 'flora', 'living_soil'].includes((room?.type || '').toLowerCase());
                if (!isMapCapable) return;

                setPendingMapGroup(group);
                setIsCreateMapFromGroupConfirmOpen(true);
                return;
            }

            const batch = active.data.current?.batch as Batch;
            const fromStock = active.data.current?.fromStock;

            // Only allow from stock and if it has quantity
            if (!fromStock || !batch || batch.quantity <= 0) {
                return;
            }

            const isMapCapable = ['clones', 'esquejes', 'esquejera', 'vegetacion', 'vegetación', 'vegetation', 'flowering', 'floración', 'flora', 'living_soil'].includes((room?.type || '').toLowerCase());

            if (!isMapCapable) return;

            // Trigger Custom Modal instead of window.confirm
            setPendingMapBatch(batch);
            setIsCreateMapConfirmOpen(true);
            return;
        }
    };

    const handleConfirmCreateMap = async () => {
        if (!pendingMapBatch || !room) return;

        setLoading(true);
        try {
            // 1. Calculate Grid Size (Square-ish)
            const total = pendingMapBatch.quantity;
            const cols = Math.ceil(Math.sqrt(total));
            const rows = Math.ceil(total / cols);

            // 2. Create Map
            // Fix timezone shift by extracting local date correctly from ISO string
            const d = new Date(pendingMapBatch.start_date || pendingMapBatch.created_at);
            d.setMinutes(d.getMinutes() + d.getTimezoneOffset());
            const displayDate = `${d.getDate().toString().padStart(2, '0')}/${(d.getMonth() + 1).toString().padStart(2, '0')}/${d.getFullYear()}`;

            // Try to extract genetic prefix if name is generic, or just use the whole name if it already looks like a prefix
            const cleanName = pendingMapBatch.name.replace("Lote ", "").split(" - ")[0];
            const prefix = cleanName.substring(0, 6).toUpperCase();

            const newMapName = `Lote ${prefix} ${displayDate}`;

            const newMap = await roomsService.createCloneMap({
                room_id: room.id,
                name: newMapName,
                grid_rows: rows,
                grid_columns: cols
            });

            if (!newMap) {
                console.error("Error creating map");
                throw new Error("Error creating map");
            }

            // 3. Distribute Batch
            const success = await roomsService.distributeBatchToMap(
                pendingMapBatch.id,
                newMap.id,
                1, 1, // Start at A1
                rows, cols,
                user?.id
            );

            if (success) {
                // Refresh
                if (id) await loadData(id);
            } else {
                alert("El mapa se creó pero hubo un error distribuyendo las plantas.");
            }

        } catch (e) {
            console.error(e);
            alert("Error al crear la mesa automática.");
        } finally {
            setLoading(false);
            setIsCreateMapConfirmOpen(false);
            setPendingMapBatch(null);
        }
    };

    const handleConfirmCreateMapFromGroup = async () => {
        if (!pendingMapGroup || !room) return;

        setIsCreatingMapFromGroup(true); // Local loading
        try {
            // 1. Calculate Total and Grid Size
            const { root, children } = pendingMapGroup;
            // Ensure we use all batches in the group
            const allBatches = [root, ...(children || [])];

            const totalQty = allBatches.reduce((acc: number, b: any) => acc + (b.quantity || 0), 0);

            // Default roughly square
            const cols = Math.ceil(Math.sqrt(totalQty));
            const rows = Math.ceil(totalQty / cols);

            // 2. Create Map
            // Use same naming as Sidebar Group
            let newMapName = '';

            if (root._virtualGroupName) {
                newMapName = root._virtualGroupName;
            } else {
                const geneticName = root.genetic?.name || root.name || 'Desconocida';
                const prefix = geneticName.substring(0, 6).toUpperCase();

                // Fix timezone shift by extracting local date correctly from ISO string
                const d = new Date(root.start_date || root.created_at);
                // Get the offset in minutes and add it back to get the actual local day
                d.setMinutes(d.getMinutes() + d.getTimezoneOffset());
                const displayDate = `${d.getDate().toString().padStart(2, '0')}/${(d.getMonth() + 1).toString().padStart(2, '0')}/${d.getFullYear()}`;

                newMapName = `Lote ${prefix} ${displayDate}`; // Consistent naming without hyphen
            }

            const newMap = await roomsService.createCloneMap({
                room_id: room.id,
                name: newMapName,
                grid_rows: rows,
                grid_columns: cols
            });

            if (!newMap) throw new Error("Could not create clone map");

            // 3. Assign Batches Sequentially (BULK OPTIMIZATION)
            await roomsService.bulkDistributeBatchesToMap(
                allBatches,
                newMap.id,
                1, // Start Row
                1, // Start Col
                rows,
                cols,
                user?.id
            );

            if (id) await loadData(id);

            closeCreateMapFromGroupModal();
            setIsCreatingMapFromGroup(false);
            setPendingMapGroup(null);

        } catch (e) {
            console.error(e);
            alert("Error al crear el mapa desde el grupo.");
            setIsCreatingMapFromGroup(false); // Stop loading on error
        }
    };



    // ... (keep existing Task Handlers) ...

    // Sticky Handlers


    const handleSaveSticky = async () => {
        if (!id) return;
        setIsSavingSticky(true);

        // No specific date-general room note
        const targetDate = undefined;

        try {
            if (selectedSticky) {
                // Update logic (if implemented in service/UI fully)
                // For now we just create new ones or delete old ones manually
            } else {
                // Pass undefined or null for targetDate to make it general
                await stickiesService.createSticky(stickyContent, stickyColor, id, targetDate);
            }

            const freshStickies = await stickiesService.getStickies(id);
            setStickies(freshStickies);
            handleCloseStickyModal();
            setStickyContent('');
        } catch (error) {
            console.error("Error saving sticky:", error);
            alert("Error al guardar la nota. Intente nuevamente.");
        } finally {
            setIsSavingSticky(false);
        }
    };



    const handleDeleteSticky = (stickyId: string) => {
        setStickyToDelete(stickyId);
    };

    const confirmDeleteSticky = async () => {
        if (stickyToDelete) {
            setIsDeletingSticky(true);
            try {
                await stickiesService.deleteSticky(stickyToDelete);
                if (id) {
                    const freshStickies = await stickiesService.getStickies(id);
                    setStickies(freshStickies);
                }
                handleCloseStickyDeleteModal();
            } catch (error) {
                console.error("Error deleting sticky:", error);
                alert("Error al eliminar la nota.");
            } finally {
                setIsDeletingSticky(false);
            }
        }
    };


    // Calendar rendering logic removed from here as it was misplaced.


    // --- Devices State ---
    const [trazappDevices, setTrazappDevices] = useState<any[]>([]);
    const [selectedDeviceForModal, setSelectedDeviceForModal] = useState<any | null>(null);
    const [deviceModalInitialView, setDeviceModalInitialView] = useState<'history' | 'config'>('history');
    const [isDeviceModalOpen, setIsDeviceModalOpen] = useState(false);

    const fetchTrazappDevices = async () => {
        try {
            const devs = await deviceService.getMyDevices();
            setTrazappDevices(devs);
        } catch (err) {
            console.error("Error fetching trazapp devices:", err);
        }
    };

    useEffect(() => {
        fetchTrazappDevices();
    }, []);

    // --- Observation Feature State ---
    const [isObservationModalOpen, setIsObservationModalOpen] = useState(false);
    const [isClosingObservation, setIsClosingObservation] = useState(false); // Animation State
    const [observationText, setObservationText] = useState('');

    const handleCloseObservation = () => {
        setIsClosingObservation(true);
        setTimeout(() => {
            setIsObservationModalOpen(false);
            setIsClosingObservation(false);
        }, 200);
    };

    const handleRegisterObservation = async () => {
        if (!selectedBatchIds.size) return;

        setLoading(true); // Add loading state
        try {
            const batchIds = Array.from(selectedBatchIds);
            const hasContent = !!observationText?.trim();

            console.log(`[Observation] Updating ${batchIds.length} batches.Notes: "${observationText}"`);

            // 1. Update via Service with Logging
            const updatePromises = batchIds.map(id =>
                roomsService.updateBatch(
                    id,
                    {
                        notes: observationText,
                        has_alert: hasContent,
                        current_room_id: room?.id
                    },
                    user?.id,
                    'Observación Registrada'
                )
            );

            await Promise.all(updatePromises);

            // 2. Update local state
            if (activeMapId && room?.batches) {
                const updatedBatches = room.batches.map(b => {
                    if (selectedBatchIds.has(b.id)) {
                        return { ...b, notes: observationText, has_alert: hasContent };
                    }
                    return b;
                });

                setRoom(prev => prev ? { ...prev, batches: updatedBatches } : null);
            }

            showToast(`Observación registrada en ${selectedBatchIds.size} lotes.`, 'success');

            // Clear selection logic-intentionally kept after success
            setObservationText('');
            setIsBulkActionsOpen(false);
            setSelectedBatchIds(new Set());

        } catch (error) {
            console.error('Error registering observation:', error);
            showToast('Error al registrar observación.', 'error');
        } finally {
            // Key Fix: Always close modal
            setIsObservationModalOpen(false);
            setLoading(false);
        }
    };

    // Batch Management Handlers-REMOVED


    // Calendar Handlers (Interactive)

    const handleCreateBatch = async () => {
        if (!newBatch.geneticId || !newBatch.quantity || !room) {
            showToast("Por favor completa todos los campos.", 'error');
            return;
        }

        setIsCreatingBatch(true);

        const selectedGenetic = genetics.find(m => m.id === newBatch.geneticId);
        // Prefix logic
        const prefix = (selectedGenetic?.name || 'GEN').substring(0, 3).toUpperCase().replace(/[^A-Z]/g, '');

        // Use the selected date (or current time if not changed) for consistency
        const dateObj = newBatch.date ? new Date(newBatch.date) : new Date();
        const formattedDate = format(dateObj, 'dd/MM/yy HH:mm');
        let batchName = `${prefix} - ${formattedDate}`;

        // Determine Stage based on Room Type
        let stage: 'seedling' | 'vegetation' | 'flowering' | 'drying' | 'curing' = 'vegetation';

        const rType = (room?.type || '').toLowerCase();
        if (['germinacion', 'germinación', 'germination', 'semillero'].includes(rType)) {
            stage = 'seedling';
            batchName = `SEM - ${prefix} - ${formattedDate}`;
        } else if (['clones', 'esquejes', 'esquejera'].includes(rType)) {
            stage = 'vegetation';
        }

        const batchData = {
            name: batchName,
            quantity: parseInt(newBatch.quantity),
            stage: stage,
            current_room_id: room.id,
            genetic_id: newBatch.geneticId,
            start_date: newBatch.date, // This matches dateObj
            parent_batch_id: undefined,
            tracking_code: Math.floor(1000 + Math.random() * 9000).toString()
        };

        try {
            const created = await roomsService.createBatch(batchData, user?.id);

            if (created) {
                // Silent reload (no full screen spinner)
                if (id) await loadData(id, false, false);

                // Close modal first
                closeCreateBatchModal();
                setNewBatch({
                    geneticId: '',
                    quantity: '',
                    date: new Date().toISOString().split('T')[0]
                });

                // Small delay before showing success toast to avoid modal overlap/ghosting
                setTimeout(() => {
                    showToast("Lote creado correctamente.", 'success');
                }, 300);
            } else {
                showToast("Error al crear el lote.", 'error');
            }
        } catch (error) {
            console.error(error);
            showToast("Error al crear el lote.", 'error');
        } finally {
            // Ensure loading state is off.
            // If component unmounted, this might warn, but usually fine in this structure.
            setIsCreatingBatch(false);
        }
    };

    const handleAddTask = (day: Date) => {
        // Allow admin or partner (owner) to add tasks
        if (user && (user.role === 'admin' || user.role === 'partner')) {
            setTaskForm({ title: '', type: 'info', due_date: format(day, 'yyyy-MM-dd'), description: '', assigned_to: '', crop_id: null, insumo_id: '', estimated_volume: '' });
            setSelectedTask(null);
            setRecurrenceEnabled(false);
            setRecurrenceConfig({ type: 'weekly', interval: 1, unit: 'week', daysOfWeek: [] });
            setIsTaskModalOpen(true);
        }
    };

    const handleDayDetails = (day: Date) => {
        setSelectedDayForSummary(day);
        setIsDaySummaryOpen(true);
    };

    const handleTaskClick = (e: React.MouseEvent, task: Task) => {
        e.stopPropagation();
        setSelectedTask(task);
        setTaskForm({
            title: task.title,
            type: task.type as any,
            due_date: task.due_date ? task.due_date.split('T')[0] : '',
            description: task.description || '',
            assigned_to: task.assigned_to || '',
            crop_id: task.crop_id || null,
            // Assuming task type might have insumo_id in the future or we fetch it
            insumo_id: '',
            estimated_volume: ''
        });
        if (task.recurrence) {
            setRecurrenceEnabled(true);
            setRecurrenceConfig(task.recurrence);
        } else {
            setRecurrenceEnabled(false);
            setRecurrenceConfig({ type: 'weekly', interval: 1, unit: 'week', daysOfWeek: [] });
        }
        setIsTaskModalOpen(true);
    };

    const handleSaveTask = async () => {
        if (!room) return;
        setIsSavingTask(true);
        try {
            const taskData: any = {
                title: taskForm.title,
                type: taskForm.type as any,
                due_date: taskForm.due_date ? `${taskForm.due_date}T12:00:00.000Z` : undefined,
                description: taskForm.description,
                room_id: room.id,
                assigned_to: taskForm.assigned_to || null,
                crop_id: taskForm.crop_id || null,
                recurrence: recurrenceEnabled ? recurrenceConfig : undefined,
                insumo_id: taskForm.insumo_id ? taskForm.insumo_id : undefined,
                estimated_volume: taskForm.estimated_volume ? parseFloat(taskForm.estimated_volume) : undefined
            };

            if (selectedTask) {
                // Update
                await tasksService.updateTask(selectedTask.id, taskData);
            } else {
                // Create
                // If it has stock deduction, we might need a specific RPC depending on how the backend handles it.
                // Assuming `createTask` or a trigger handles it, or we need a new service method.
                // For now, passing it in the payload.
                await tasksService.createTask(taskData);
            }

            // Refresh tasks
            if (id) {
                const updatedTasks = await tasksService.getTasksByRoomId(id);
                setTasks(updatedTasks);
            }
            closeTaskModal();
        } catch (error) {
            console.error("Error saving task", error);
        } finally {
            setIsSavingTask(false);
        }
    };

    const handleToggleTaskStatus = async (task: Task) => {
        if (!task) return;

        // Prevent completing future tasks
        if (task.due_date && task.status !== 'done') {
            const taskDate = new Date(task.due_date);
            const today = new Date();
            // Reset times to compare only dates
            taskDate.setHours(0, 0, 0, 0);
            today.setHours(0, 0, 0, 0);

            if (taskDate > today) {
                setToastState({ isOpen: true, message: 'No puedes completar tareas que no corresponden a la fecha actual o pasada.', type: 'error' });
                return;
            }
        }

        // If it's a virtual occurrence of a recurring task
        if (task.id.toString().startsWith('virtual')) {
            try {
                const parts = task.id.toString().split(' -');
                if (parts.length >= 3) {
                    const parentId = parts[1].trim();
                    const parentTask = tasks.find(t => t.id === parentId);

                    if (parentTask && parentTask.recurrence && task.due_date) {
                        const targetDate = task.due_date.split('T')[0];

                        // 1. Exclude this date from the parent's recurrence
                        const currentExceptions = parentTask.recurrence.exceptions || [];
                        const newRecurrence = {
                            ...parentTask.recurrence,
                            exceptions: [...currentExceptions, targetDate]
                        };

                        // 2. Create a new single-occurrence task for this date marked as "done"
                        const newTaskPayload: Partial<Task> = {
                            title: parentTask.title,
                            description: parentTask.description,
                            type: parentTask.type,
                            status: 'done',
                            due_date: targetDate,
                            room_id: parentTask.room_id,
                            crop_id: parentTask.crop_id,
                            assigned_to: parentTask.assigned_to,
                            // Do not pass recurrence so it's a standalone task
                        };

                        // Optimistic UI for virtual status change
                        // Remove the virtual task, update parent, add new "done" task
                        setTasks(prevTasks => {
                            const withoutVirtual = prevTasks.filter(t => t.id !== task.id);
                            const updatedParent = withoutVirtual.map(t =>
                                t.id === parentId ? { ...t, recurrence: newRecurrence } : t
                            );
                            const mockCompletedTask: Task = {
                                ...newTaskPayload,
                                id: `temp-${Date.now()}`,
                                created_at: new Date().toISOString()
                            } as Task;
                            return [...updatedParent, mockCompletedTask];
                        });

                        try {
                            await Promise.all([
                                tasksService.updateTask(parentId, { recurrence: newRecurrence }),
                                tasksService.createTask(newTaskPayload as any)
                            ]);

                            // Re-fetch to get real ID of the new completed task
                            if (id) {
                                const updatedTasks = await tasksService.getTasksByRoomId(id);
                                setTasks(updatedTasks);
                            }
                            setToastState({ isOpen: true, message: 'Ocurrencia completada individualmente', type: 'success' });
                        } catch (err) {
                            console.error("Error toggling virtual task:", err);
                            // Re-fetch to revert optimistic update on failure
                            if (id) {
                                const originalTasks = await tasksService.getTasksByRoomId(id);
                                setTasks(originalTasks);
                            }
                            setToastState({ isOpen: true, message: 'Ocurrió un error', type: 'error' });
                        }
                    }
                }
            } catch (err) {
                console.error("Error parsing virtual task for completion:", err);
                setToastState({ isOpen: true, message: 'Ocurrió un error al completar', type: 'error' });
            }
            return;
        }

        const newStatus = task.status === 'done' ? 'pending' : 'done';
        const originalStatus = task.status;

        // Optimistic UI Update
        setTasks(prevTasks => prevTasks.map(t =>
            t.id === task.id ? { ...t, status: newStatus } : t
        ));

        try {
            await tasksService.updateTask(task.id, { status: newStatus } as any);

            // Stock Deduction on Task Completion
            if (newStatus === 'done' && task.insumo_id && task.estimated_volume && task.estimated_volume > 0) {
                const { error: stockError } = await supabase.rpc('execute_task_with_stock', {
                    _task_id: task.id,
                    _insumo_id: task.insumo_id,
                    _estimated_volume: task.estimated_volume,
                    _performed_by: user?.id,
                    _room_id: room?.id
                });

                if (stockError) {
                    console.error("Error deducting stock for task:", stockError);
                    setToastState({ isOpen: true, message: `Tarea completada, pero fallo al descontar insumo: ${stockError.message}`, type: 'error' });
                } else {
                    setToastState({ isOpen: true, message: `Tarea completada y stock descontado`, type: 'success' });
                }
            }
            // No need to re-fetch if successful, state is already correct
        } catch (error) {
            console.error("Error updating task status:", error);
            // Revert on error
            setTasks(prevTasks => prevTasks.map(t =>
                t.id === task.id ? { ...t, status: originalStatus } : t
            ));
            setToastState({ isOpen: true, message: 'No se pudo actualizar el estado de la tarea', type: 'error' });
        }
    };

    const handleDeleteTask = () => {
        if (selectedTask) {
            setIsDeleteConfirmOpen(true);
        }
    };

    const confirmDeleteTask = async () => {
        if (!selectedTask) return;

        // Check if it's a virtual occurrence of a recurring task
        if (selectedTask.id.toString().startsWith('virtual')) {
            try {
                // Parse virtual task details: ID format is "virtual - {parentId} - {timestamp}"
                const parts = selectedTask.id.toString().split(' -');
                if (parts.length >= 3) {
                    const parentId = parts[1].trim();

                    // Find the original task to update its exceptions
                    const parentTask = tasks.find(t => t.id === parentId);

                    if (parentTask && parentTask.recurrence && selectedTask.due_date) {
                        const dateToExclude = selectedTask.due_date.split('T')[0]; // Ensure YYYY-MM-DD

                        // Add date to exceptions array
                        const currentExceptions = parentTask.recurrence.exceptions || [];
                        if (!currentExceptions.includes(dateToExclude)) {
                            const newRecurrence = {
                                ...parentTask.recurrence,
                                exceptions: [...currentExceptions, dateToExclude]
                            };

                            // Update the database
                            const success = await tasksService.updateTask(parentId, { recurrence: newRecurrence });

                            if (success) {
                                setToastState({ isOpen: true, message: 'Ocurrencia eliminada del calendario', type: 'success' });
                                // Update local state to trigger re-calculation without full reload
                                setTasks(prev => prev.map(t => t.id === parentId ? { ...t, recurrence: newRecurrence } : t));
                            } else {
                                setToastState({ isOpen: true, message: 'Error al eliminar ocurrencia recurrente', type: 'error' });
                            }
                        }
                    }
                }
            } catch (err) {
                console.error("Error excluyendo ocurrencia:", err);
                setToastState({ isOpen: true, message: 'Ocurrió un error al eliminar ocurrencia', type: 'error' });
            } finally {
                setIsDeleteConfirmOpen(false);
                setIsTaskModalOpen(false);
            }
            return;
        }

        // Standard task deletion
        try {
            const success = await tasksService.deleteTask(selectedTask.id);
            if (success) {
                setToastState({ isOpen: true, message: 'Tarea eliminada correctamente', type: 'success' });
                if (id) {
                    const updatedTasks = await tasksService.getTasksByRoomId(id);
                    setTasks(updatedTasks);
                }
            } else {
                setToastState({ isOpen: true, message: 'Ocurrió un error al eliminar la tarea', type: 'error' });
            }
        } catch (err) {
            console.error("Error confirmDeleteTask:", err);
            setToastState({ isOpen: true, message: 'Ocurrió un error al eliminar la tarea', type: 'error' });
        } finally {
            setIsDeleteConfirmOpen(false);
            setIsTaskModalOpen(false);
        }
    };



    useEffect(() => {
        if (id) {
            loadData(id, true);
        }
    }, [id, loadData]);

    // Handle click outside map/toolbar to deselect (Moved here to access all modal states)
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            // Do not deselect if we are in relocation mode
            if (isRelocatingSelection) return;

            // Do not deselect if any modal is open
            if (
                isChangePhaseModalOpen ||
                isBulkEditModalOpen ||
                isEditBatchModalOpen ||
                isObservationModalOpen ||
                isDeleteMapModalOpen ||
                isHistoryModalOpen ||
                isCreateMapFromGroupConfirmOpen ||
                isDeleteConfirmOpen ||
                isTaskModalOpen ||
                isBulkDeleteConfirmOpen ||
                isSowingModalOpen ||
                plantDetailModal.isOpen ||
                isEditModalOpen ||
                isMapModalOpen ||
                isEditMapModalOpen ||
                isCreateBatchModalOpen ||
                isMetricsModalOpen ||
                isGroupDetailModalOpen
            ) return;

            const isOutsideMap = mapContainerRef.current && !mapContainerRef.current.contains(event.target as Node);
            const isOutsideToolbar = toolbarRef.current && !toolbarRef.current.contains(event.target as Node);

            if (isOutsideMap && isOutsideToolbar) {
                // If clicking outside map container AND toolbar, clear selection
                if (selectedBatchIds.size > 0 || isSelectionMode) {
                    setSelectedBatchIds(new Set());
                    setIsSelectionMode(false);
                }
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [
        selectedBatchIds,
        isSelectionMode,
        isRelocatingSelection,
        isChangePhaseModalOpen,
        isBulkEditModalOpen,
        isEditBatchModalOpen,
        isObservationModalOpen,
        isDeleteMapModalOpen,
        isHistoryModalOpen,
        isCreateMapFromGroupConfirmOpen,
        isDeleteConfirmOpen,
        isTaskModalOpen,
        isBulkDeleteConfirmOpen,
        isSowingModalOpen,
        plantDetailModal.isOpen,
        isEditModalOpen,
        isMapModalOpen,
        isEditMapModalOpen,
        isCreateBatchModalOpen,
        isMetricsModalOpen,
        isGroupDetailModalOpen
    ]);

    // Memoized selection for Transplant Modal to prevent infinite loops
    // We must declare this at the top level, not conditionally
    const memoizedTransplantBatchIds = useMemo(() => {
        if (transplantForm.batchId) return [transplantForm.batchId];
        return Array.from(selectedBatchIds);
    }, [transplantForm.batchId, selectedBatchIds]);

    // Calendar Rendering Logic

    // Tables calc removed

    if (loading) return <LoadingSpinner fullScreen duration={3000} />

    return (
        <RoomDetailContainer>
            <Container>
                <GlobalPrintStyles />
                <RoomHeaderContainer className="no-print">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1.25rem', flexWrap: 'wrap' }}>
                        <ShadcnButton
                            variant="secondary"
                            size="sm"
                            onClick={() => navigate(room?.spot_id ? `/crops/${room.spot_id}` : '/rooms')}
                        >
                            <LucideArrowLeft size={15} /> Volver
                        </ShadcnButton>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                            <h1 style={{
                                fontSize: 'clamp(1.5rem, 3vw, 2.1rem)',
                                fontWeight: 800,
                                letterSpacing: '-0.025em',
                                color: '#f8fafc',
                                margin: 0,
                                background: 'linear-gradient(135deg, #ffffff 0%, #cbd5e1 100%)',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent'
                            }}>
                                {room?.name}
                            </h1>
                            <ShadcnBadge
                                variant={
                                    room?.type === 'flowering' ? 'purple' :
                                    room?.type === 'vegetation' ? 'emerald' :
                                    (room?.type === 'drying' || room?.type === 'curing') ? 'amber' :
                                    'sky'
                                }
                                ping
                            >
                                {room?.type === 'vegetation' ? 'Vegetación' :
                                 room?.type === 'flowering' ? 'Floración' :
                                 (room?.type === 'drying' || room?.type === 'curing') ? 'Secado' :
                                 room?.type === 'living_soil' ? 'Agro / Living Soil' :
                                 room?.type || 'Sala'}
                            </ShadcnBadge>
                        </div>
                    </div>
                </RoomHeaderContainer>

                {/* Room Summary Header */}
                {room && (
                    <HeaderGrid className="no-print">
                        {/* Plants Count */}
                        <StatCard>
                            <h3><LucideSprout size={16} style={{ color: '#34d399' }} /> {
                                room.type === 'germination' ? 'Total Semillas' :
                                    room.type === 'flowering' ? 'Total Plantas en Floración' :
                                        room.type === 'drying' ? 'Total Plantas Secandose' :
                                            ['clones', 'esquejes', 'esquejera'].includes((room.type || '').toLowerCase()) ? 'Total Esquejes' :
                                                'Total Plantas'
                            }</h3>
                            <div className="value" style={{ color: '#34d399' }}>
                                {room.batches?.reduce((sum: any, b: any) => sum + b.quantity, 0) || 0}
                            </div>
                            <div className="sub">
                                En {(() => {
                                    if (!room.batches) return 0;
                                    const uniqueBatches = new Set();
                                    room.batches.forEach(b => {
                                        if (b.quantity > 0) {
                                            // Group by Genetic + Date (Minute Precision) to match visual list
                                            const date = b.created_at ? new Date(b.created_at).toISOString().slice(0, 16) : 'unknown';
                                            const key = `${b.genetic_id || b.name}-${date}`;
                                            uniqueBatches.add(key);
                                        }
                                    });
                                    return uniqueBatches.size;
                                })()} lotes activos
                            </div>
                        </StatCard>

                        {/* Total Maps Stat */}
                        {room.type !== 'germination' && room.type !== 'drying' && (
                            <StatCard>
                                <h3><LucideLayers size={16} style={{ color: '#38bdf8' }} /> {
                                    room.type === 'flowering' ? 'Total mesas de floracion' :
                                        ['vegetation', 'vegetación', 'vegetacion'].includes((room.type || '').toLowerCase()) ? 'Total Mesas Vegetación' :
                                            room.type === 'living_soil' ? 'Total Camas' :
                                                'Total de Esquejeras'
                                }</h3>
                                <div className="value" style={{ color: '#38bdf8' }}>
                                    {cloneMaps.length}
                                </div>
                                <div className="sub">Mapas activos</div>
                            </StatCard>
                        )}

                        {/* Environment */}
                        <StatCard>
                            <h3><LucideThermometer size={16} style={{ color: '#fbbf24' }} /> Ambiente</h3>
                            <div className="value" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <div style={{ color: '#f87171' }}>{room.current_temperature || '--'}°</div>
                                <span style={{ fontSize: '1.2rem', color: '#64748b', paddingBottom: '0.1rem' }}>/</span>
                                <div style={{ color: '#38bdf8' }}>{room.current_humidity || '--'}%</div>
                            </div>
                            <div className="sub">
                                Temp & Humedad
                            </div>
                        </StatCard>

                        {/* Date-CHANGED to Start Date */}
                        <StatCard>
                            <h3><LucideCalendar size={16} style={{ color: '#c084fc' }} /> Fecha Inicio</h3>
                            <div className="value" style={{ textTransform: 'capitalize' }}>
                                {room.start_date
                                    ? format(new Date(room.start_date + 'T00:00:00'), "d MMM yyyy", { locale: es })
                                    : '--'}
                            </div>
                            <div className="sub">
                                {room.start_date
                                    ? `Creado hace ${differenceInDays(new Date(), new Date(room.start_date))} días`
                                    : 'Sin fecha'}
                            </div>
                        </StatCard>
                    </HeaderGrid>
                )}


                {/*
            <div style={{ marginBottom: '2rem' }} className="no-print">
                <TuyaManager mode="sensors" roomId={room?.id} />
            </div>
            */}


                {/* Stickies Wall Section */}
                <div style={{ marginBottom: '2rem' }} className="no-print">
                    <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '1rem' }} className="notes-header-container">
                        <h2 style={{ fontSize: '1.2rem', color: '#f8fafc', fontWeight: 700, letterSpacing: '-0.02em', display: 'flex', alignItems: 'center', gap: '0.5rem', margin: 0 }} className="notes-title">
                            <LucideStickyNote size={17} style={{ color: '#fbbf24' }} /> Pizarra de Notas
                        </h2>
                    </div>

                    {stickies.length > 0 ? (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '1rem' }}>
                            {stickies.map(s => (
                                <div key={s.id} style={{
                                    background: s.color === 'yellow' ? 'rgba(236, 201, 75, 0.1)' : s.color === 'blue' ? 'rgba(66, 153, 225, 0.1)' : s.color === 'pink' ? 'rgba(245, 101, 101, 0.1)' : 'rgba(72, 187, 120, 0.1)',
                                    backdropFilter: 'blur(12px)',
                                    WebkitBackdropFilter: 'blur(12px)',
                                    border: `1px solid ${s.color === 'yellow' ? 'rgba(236, 201, 75, 0.3)' : s.color === 'blue' ? 'rgba(66, 153, 225, 0.3)' : s.color === 'pink' ? 'rgba(245, 101, 101, 0.3)' : 'rgba(72, 187, 120, 0.3)'}`,
                                    padding: '1rem', borderRadius: '0.75rem',
                                    boxShadow: '0 4px 6px -1px rgba(0,0,0,0.3)',
                                    position: 'relative',
                                    display: 'flex', flexDirection: 'column', gap: '0.5rem'
                                }}>
                                    <p style={{ fontSize: '1rem', color: '#f8fafc', whiteSpace: 'pre-wrap', lineHeight: '1.4' }}>{s.content}</p>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 'auto', paddingTop: '0.5rem', borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
                                        <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{format(new Date(s.created_at), 'd MMM', { locale: es })}</span>
                                        <button
                                            onClick={() => handleDeleteSticky(s.id)}
                                            style={{ background: 'none', border: 'none', color: '#f87171', cursor: 'pointer', opacity: 0.8, padding: '2px', transition: 'opacity 0.2s' }}
                                            title="Eliminar"
                                            onMouseEnter={e => e.currentTarget.style.opacity = '1'}
                                            onMouseLeave={e => e.currentTarget.style.opacity = '0.8'}
                                        >
                                            <LucideTrash2 size={12} />
                                        </button>
                                    </div>
                                </div>
                            ))}
                            {/* Add New Note Placeholder Card */}
                            <div
                                onClick={() => {
                                    setStickyContent('');
                                    setStickyColor('yellow');
                                    setIsStickyModalOpen(true);
                                }}
                                style={{
                                    border: '2px dashed rgba(255, 255, 255, 0.2)',
                                    borderRadius: '0.75rem',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: '#94a3b8',
                                    cursor: 'pointer',
                                    minHeight: '120px',
                                    transition: 'all 0.2s ease',
                                    background: 'rgba(15, 23, 42, 0.4)',
                                    backdropFilter: 'blur(12px)'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.borderColor = '#4ade80';
                                    e.currentTarget.style.color = '#4ade80';
                                    e.currentTarget.style.background = 'rgba(74, 222, 128, 0.1)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                                    e.currentTarget.style.color = '#94a3b8';
                                    e.currentTarget.style.background = 'rgba(15, 23, 42, 0.4)';
                                }}
                            >
                                <LucidePlus size={24} style={{ marginBottom: '0.5rem' }} />
                                <span style={{ fontSize: '0.9rem', fontWeight: 600 }}>Click aqui + para agregar nota</span>
                            </div>
                        </div>
                    ) : (

                        <EmptyStickyState
                            onClick={() => {
                                setStickyContent('');
                                setStickyColor('yellow');
                                setIsStickyModalOpen(true);
                            }}
                        >
                            <DashedStickyCircle>
                                <LucidePlus />
                            </DashedStickyCircle>
                            <div style={{ textAlign: 'left' }}>
                                <p style={{ fontSize: '1rem', fontWeight: 600, color: 'inherit', margin: 0 }}>No hay notas fijadas.</p>
                                <p style={{ color: 'inherit', opacity: 0.8, fontSize: '0.85rem', margin: 0 }}>Toca aquí para agregar una nota</p>
                            </div>
                        </EmptyStickyState>
                    )}
                </div>

                {/* Esquejera View or Standard View */}
                {
                    ['clones', 'esquejes', 'esquejera', 'vegetacion', 'vegetación', 'vegetation', 'flowering', 'floración', 'flora', 'germination', 'germinacion', 'germinación', 'semillero', 'living_soil'].includes((room?.type as string)?.toLowerCase()) ? (
                        <div style={{ padding: '2rem' }} className="room-actions-wrapper">
                            <div className="no-print" style={{ marginBottom: '2rem', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', flexWrap: 'wrap' }}>

                                <div className="action-buttons-container tour-change-stage" style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', justifyContent: 'flex-end', alignItems: 'center' }}>
                                    {/* Hide "Nueva Tarea" for Clones/Esquejes/Germination rooms */}
                                    {!['clones', 'esquejes', 'esquejera', 'germination'].includes((room?.type as string)?.toLowerCase()) && (
                                        <StyledActionButton
                                            onClick={() => setIsTaskModalOpen(true)}
                                            $variant="secondary"
                                        >
                                            <LucideCheckSquare size={15} style={{ color: '#94a3b8' }} /> Nueva Tarea
                                        </StyledActionButton>
                                    )}
                                    <StyledActionButton
                                        onClick={handleOpenHistory}
                                        $variant="secondary"
                                    >
                                        <LucideHistory size={15} style={{ color: '#94a3b8' }} /> Historial
                                    </StyledActionButton>
                                    {room?.type !== 'living_soil' ? (
                                        <StyledActionButton
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                if (room) {
                                                    if (room.type === 'flowering') {
                                                        setIsHarvestModalOpen(true);
                                                    } else {
                                                        setTransplantForm({ batchId: '', quantity: 1 }); // Reset form
                                                        handleOpenTransplant(e, room, null); // Open without specific batch
                                                    }
                                                }
                                            }}
                                            $variant={room?.type === 'flowering' ? 'gold' : 'primary'}
                                        >
                                            {room?.type === 'flowering' ? <LucideScissors size={15} /> : <LucideRefreshCw size={15} />}
                                            {room?.type === 'flowering' ? 'Cosechar' : 'Transplantar'}
                                        </StyledActionButton>
                                    ) : (
                                        <StyledActionButton
                                            disabled={!room?.batches?.some(b => b.stage === 'completed')}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setIsHarvestModalOpen(true);
                                            }}
                                            $variant="gold"
                                            title={!room?.batches?.some(b => b.stage === 'completed') ? "No hay plantas listas para cosechar (Estado Corte)" : "Cosechar plantas finalizadas"}
                                        >
                                            <LucideScissors size={15} /> Cosechar
                                        </StyledActionButton>
                                    )}
                                    <StyledActionButton
                                        onClick={() => setIsEditModalOpen(true)}
                                        $variant="secondary"
                                    >
                                        <LucideSettings size={15} style={{ color: '#94a3b8' }} /> Editar Sala
                                    </StyledActionButton>
                                </div>
                            </div>

                            {/* DndContext WRAPPER FOR BOTH VIEWS */}
                            {/* Moved Map Header (Toolbar) Here for Full Width */}
                            {
                                activeMapId && activeMap && (
                                    <div className="no-print-map-header" ref={toolbarRef} style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        gap: '1.5rem',
                                        marginBottom: '2rem',
                                        padding: '0.5rem',
                                        width: '100%',
                                        boxSizing: 'border-box',
                                        position: 'relative',
                                        zIndex: 40
                                    }}>
                                        {/* LEFT: Map Title & Info */}
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexShrink: 0, overflow: 'hidden' }}>
                                            <h3 style={{ margin: 0, fontSize: '1.5rem', color: '#f8fafc', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontWeight: 700 }}>
                                                {activeMap.name}
                                            </h3>
                                            <span style={{ color: '#4ade80', border: '1px solid #4ade80', padding: '0.2rem 0.6rem', borderRadius: '999px', fontSize: '0.9rem', fontWeight: 700, whiteSpace: 'nowrap', flexShrink: 0 }}>
                                                {activeMap.grid_rows} x {activeMap.grid_columns}
                                            </span>
                                        </div>

                                        {/* CENTER: Selection Status (New) */}
                                        {isSelectionMode && selectedBatchIds.size > 0 && (
                                            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                                <span style={{
                                                    fontSize: '0.9rem',
                                                    color: '#4ade80',
                                                    fontWeight: 700,
                                                    whiteSpace: 'nowrap',
                                                    background: 'rgba(74, 222, 128, 0.15)',
                                                    padding: '0.35rem 1rem',
                                                    borderRadius: '999px',
                                                    border: '1px solid rgba(74, 222, 128, 0.3)',
                                                    transition: 'all 0.2s',
                                                    boxShadow: '0 0 10px rgba(74, 222, 128, 0.1)'
                                                }}>
                                                    {selectedBatchIds.size} seleccionados
                                                </span>
                                            </div>
                                        )}

                                        {/* RIGHT: Actions Toolbar */}
                                        <div className="no-print-map-controls" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.75rem', justifyContent: 'center' }}>

                                            <button
                                                onClick={() => handlePrintMap()}
                                                title="Imprimir Mapa"
                                                style={{
                                                    background: 'rgba(15, 23, 42, 0.4)',
                                                    backdropFilter: 'blur(8px)',
                                                    border: '1px solid rgba(255, 255, 255, 0.1)',
                                                    borderRadius: '0.375rem',
                                                    padding: '0.5rem 0.75rem',
                                                    fontSize: '0.85rem',
                                                    cursor: 'pointer',
                                                    color: '#cbd5e1',
                                                    fontWeight: 600,
                                                    display: 'flex', alignItems: 'center', gap: '0.5rem',
                                                    transition: 'all 0.2s'
                                                }}
                                                onMouseEnter={e => {
                                                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                                                    e.currentTarget.style.color = '#f8fafc';
                                                }}
                                                onMouseLeave={e => {
                                                    e.currentTarget.style.background = 'rgba(15, 23, 42, 0.4)';
                                                    e.currentTarget.style.color = '#cbd5e1';
                                                }}
                                            >
                                                <Printer /> Imprimir
                                            </button>

                                            <button
                                                onClick={() => setActiveMapId(null)}
                                                style={{
                                                    background: 'rgba(15, 23, 42, 0.4)',
                                                    backdropFilter: 'blur(8px)',
                                                    border: '1px solid rgba(255, 255, 255, 0.1)',
                                                    padding: '0.5rem 0.75rem',
                                                    borderRadius: '0.5rem',
                                                    color: '#f8fafc',
                                                    fontWeight: 600,
                                                    cursor: 'pointer',
                                                    display: 'flex', alignItems: 'center', gap: '0.5rem',
                                                    transition: 'all 0.2s'
                                                }}
                                                onMouseEnter={e => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)'}
                                                onMouseLeave={e => e.currentTarget.style.background = 'rgba(15, 23, 42, 0.4)'}
                                            >
                                                <ArrowLeft /> Volver
                                            </button>

                                            {/* 1. SELECTION ACTIONS */}
                                            {selectedBatchIds.size > 0 && (
                                                <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: '0.5rem', paddingLeft: '0.5rem', borderLeft: '1px solid rgba(255,255,255,0.1)', flexShrink: 0 }}>

                                                    {/* BULK ACTIONS DROPDOWN */}
                                                    <div style={{ position: 'relative' }}>
                                                        <button
                                                            onClick={(e) => { e.stopPropagation(); setIsBulkActionsOpen(!isBulkActionsOpen); }}
                                                            style={{
                                                                background: 'rgba(74, 222, 128, 0.2)',
                                                                color: '#4ade80',
                                                                border: '1px solid rgba(74, 222, 128, 0.4)',
                                                                borderRadius: '0.375rem',
                                                                padding: '0.5rem 1rem',
                                                                fontSize: '0.85rem',
                                                                fontWeight: 600,
                                                                cursor: 'pointer',
                                                                display: 'flex', alignItems: 'center', gap: '0.5rem',
                                                                transition: 'all 0.2s'
                                                            }}
                                                            onMouseEnter={e => {
                                                                e.currentTarget.style.background = 'rgba(74, 222, 128, 0.3)';
                                                            }}
                                                            onMouseLeave={e => {
                                                                e.currentTarget.style.background = 'rgba(74, 222, 128, 0.2)';
                                                            }}
                                                        >
                                                            Acciones <ChevronDown size={10} />
                                                        </button>

                                                        {isBulkActionsOpen && (
                                                            <div style={{
                                                                position: 'absolute',
                                                                top: '120%',
                                                                right: 0,
                                                                background: 'rgba(15, 23, 42, 0.95)',
                                                                backdropFilter: 'blur(16px)',
                                                                WebkitBackdropFilter: 'blur(16px)',
                                                                border: '1px solid rgba(255, 255, 255, 0.1)',
                                                                borderRadius: '0.5rem',
                                                                boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.5)',
                                                                zIndex: 50,
                                                                minWidth: '220px',
                                                                overflow: 'hidden',
                                                                animation: 'fadeIn 0.1s ease-out'
                                                            }}>
                                                                <div style={{ padding: '0.75rem 1rem', background: 'rgba(255, 255, 255, 0.05)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                                                                    <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#94a3b8', letterSpacing: '0.05em' }}>ACCIONES DE LOTE</div>
                                                                </div>

                                                                {/* 1. IMPRIMIR ETIQUETAS */}
                                                                <button
                                                                    onClick={() => {
                                                                        setIsBulkActionsOpen(false);
                                                                        handlePrintLabels();
                                                                    }}
                                                                    style={{
                                                                        width: '100%',
                                                                        display: 'flex', alignItems: 'center', gap: '0.75rem',
                                                                        padding: '0.75rem 1rem',
                                                                        color: '#f8fafc',
                                                                        fontSize: '0.85rem',
                                                                        fontWeight: 500,
                                                                        background: 'transparent',
                                                                        border: 'none',
                                                                        cursor: 'pointer',
                                                                        transition: 'all 0.2s',
                                                                        textAlign: 'left'
                                                                    }}
                                                                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)'}
                                                                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                                                                >
                                                                    <Printer color="#94a3b8" /> Imprimir Etiquetas
                                                                </button>

                                                                {/* 2. EDITAR */}
                                                                <button
                                                                    onClick={() => {
                                                                        setIsBulkActionsOpen(false);
                                                                        if (selectedBatchIds.size === 1) {
                                                                            const batchId = Array.from(selectedBatchIds)[0];
                                                                            const batch = room?.batches?.find(b => b.id === batchId);
                                                                            if (batch) handleOpenEditBatch(batch);
                                                                        } else {
                                                                            // Bulk Edit
                                                                            setBulkEditForm({ stage: '', notes: '' });
                                                                            setIsBulkEditModalOpen(true);
                                                                        }
                                                                    }}
                                                                    style={{ width: '100%', textAlign: 'left', padding: '0.75rem 1rem', background: 'transparent', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.75rem', color: '#cbd5e1', fontSize: '0.9rem', transition: 'background 0.1s' }}
                                                                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)'}
                                                                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                                                                >
                                                                    <div style={{ background: 'rgba(74, 222, 128, 0.1)', padding: '0.4rem', borderRadius: '0.375rem', color: '#4ade80', display: 'flex' }}><Pencil size={12} /></div>
                                                                    <span>Editar</span>
                                                                </button>

                                                                {/* 1.5. CAMBIAR FASE (New) */}
                                                                {room?.type === 'living_soil' && (
                                                                    <button
                                                                        onClick={() => {
                                                                            setIsBulkActionsOpen(false);
                                                                            // Pre-fill logic
                                                                            let initialStage = '';
                                                                            let initialNotes = '';
                                                                            if (selectedBatchIds.size === 1) {
                                                                                const batchId = Array.from(selectedBatchIds)[0];
                                                                                const batch = room?.batches?.find(b => b.id === batchId);
                                                                                if (batch) {
                                                                                    initialStage = batch.stage || '';
                                                                                    initialNotes = batch.notes || '';
                                                                                }
                                                                            }
                                                                            setChangePhaseForm({ stage: initialStage as BatchStage, notes: initialNotes });
                                                                            setIsChangePhaseModalOpen(true);
                                                                        }}
                                                                        style={{ width: '100%', textAlign: 'left', padding: '0.75rem 1rem', background: 'transparent', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.75rem', color: '#cbd5e1', fontSize: '0.9rem', transition: 'background 0.1s' }}
                                                                        onMouseEnter={e => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)'}
                                                                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                                                                    >
                                                                        <div style={{ background: 'rgba(74, 222, 128, 0.1)', padding: '0.4rem', borderRadius: '0.375rem', color: '#4ade80', display: 'flex' }}><LucideLeaf size={12} /></div>
                                                                        <span>Cambiar Fase de Cultivo</span>
                                                                    </button>
                                                                )}

                                                                {/* 2. REUBICAR */}
                                                                <button
                                                                    onClick={() => {
                                                                        setIsBulkActionsOpen(false);
                                                                        // In-Map Relocation Logic
                                                                        if (selectedBatchIds.size > 0) {
                                                                            setIsRelocatingSelection(true);
                                                                        }
                                                                    }}
                                                                    style={{ width: '100%', textAlign: 'left', padding: '0.75rem 1rem', background: 'transparent', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.75rem', color: '#cbd5e1', fontSize: '0.9rem', transition: 'background 0.1s' }}
                                                                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)'}
                                                                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                                                                >
                                                                    <div style={{ background: 'rgba(56, 189, 248, 0.1)', padding: '0.4rem', borderRadius: '0.375rem', color: '#38bdf8', display: 'flex' }}><ArrowLeftRight size={12} /></div>
                                                                    <span>Reubicar en Mapa</span>
                                                                </button>

                                                                {/* 3. OBSERVACION */}
                                                                <button
                                                                    onClick={() => {
                                                                        setIsBulkActionsOpen(false);
                                                                        const firstSelectedBatch = room?.batches?.find(b => selectedBatchIds.has(b.id) && b.notes);
                                                                        setObservationText(firstSelectedBatch?.notes || '');
                                                                        setIsObservationModalOpen(true);
                                                                    }}
                                                                    style={{ width: '100%', textAlign: 'left', padding: '0.75rem 1rem', background: 'transparent', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.75rem', color: '#cbd5e1', fontSize: '0.9rem', transition: 'background 0.1s' }}
                                                                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)'}
                                                                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                                                                >
                                                                    <div style={{ background: 'rgba(236, 201, 75, 0.1)', padding: '0.4rem', borderRadius: '0.375rem', color: '#facc15', display: 'flex' }}><LucideAlertTriangle size={12} /></div>
                                                                    <span>Observación</span>
                                                                </button>

                                                                <div style={{ borderTop: '1px solid rgba(255, 255, 255, 0.1)', margin: '0.25rem 0' }}></div>

                                                                {/* 4. ELIMINAR */}
                                                                <button
                                                                    onClick={() => { setIsBulkActionsOpen(false); handleBulkDeleteFirstStep(); }}
                                                                    style={{ width: '100%', textAlign: 'left', padding: '0.75rem 1rem', background: 'transparent', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.75rem', color: '#f87171', fontSize: '0.9rem', transition: 'background 0.1s' }}
                                                                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)'}
                                                                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                                                                >
                                                                    <div style={{ background: 'rgba(239, 68, 68, 0.1)', padding: '0.4rem', borderRadius: '0.375rem', color: '#f87171', display: 'flex' }}><LucideTrash2 size={12} /></div>
                                                                    <span>Eliminar Selección</span>
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            )}

                                            {/* Overlay for Dropdown */}
                                            {isBulkActionsOpen && (
                                                <div
                                                    style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 40 }}
                                                    onClick={() => setIsBulkActionsOpen(false)}
                                                />
                                            )}

                                            {/* 2. PERSISTENT ACTIONS */}
                                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                                                {selectedBatchIds.size > 0 && (
                                                    <button
                                                        onClick={() => {
                                                            setSelectedBatchIds(new Set());
                                                            setIsSelectionMode(false);
                                                        }}
                                                        title="Deseleccionar todo"
                                                        style={{
                                                            background: '#e53e3e',
                                                            color: 'white',
                                                            border: 'none',
                                                            borderRadius: '0.375rem',
                                                            padding: '0.5rem 0.75rem',
                                                            fontSize: '0.85rem',
                                                            cursor: 'pointer',
                                                            display: 'flex', alignItems: 'center', gap: '0.5rem',
                                                            fontWeight: 600,
                                                            whiteSpace: 'nowrap'
                                                        }}
                                                    >
                                                        <LucideX /> Deseleccionar ({selectedBatchIds.size})
                                                    </button>
                                                )}

                                                {/* Print button moved to specific map detail title block */}

                                            </div>

                                        </div>
                                    </div >
                                )
                            }
                            <DndContext sensors={sensors} collisionDetection={pointerWithin} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
                                <div className="map-and-sidebar-container" style={{ display: 'flex', gap: '2rem', alignItems: 'flex-start' }}>

                                    {/* MAIN CONTENT AREA */}
                                    <div className="main-content-wrapper" style={{ flex: 3, minWidth: 0 }}>
                                        {!activeMapId ? (
                                            /* MAPS LIST VIEW */
                                            <div className="map-list-container">
                                                <div className={`mobile-maps-list-wrapper ${cloneMaps.length === 0 ? 'empty-maps' : ''}`}>
                                                    <h3 className="no-print" style={{ color: '#718096', fontSize: '0.9rem', marginBottom: '0.5rem' }}>Mesas Activas (Arrastra lotes aquí para crear nuevas)</h3>

                                                    <div className="empty-map-state" style={{
                                                        display: 'flex', gap: '1.5rem',
                                                        marginBottom: '2rem', justifyContent: 'center'
                                                    }}>
                                                        {cloneMaps.length === 0 ? (
                                                            <div style={{ color: '#a0aec0', fontSize: '0.9rem', textAlign: 'center', width: '100%' }}>
                                                                {room?.type === 'vegetation' ? 'No hay cultivos en vegetación actualmente' :
                                                                    room?.type === 'germination' ? 'No hay semillas en germinación.' :
                                                                        room?.type === 'flowering' ? 'No hay plantas en floracion actualmente' :
                                                                            room?.type === 'living_soil' ? 'No hay camas/cultivos activos.' :
                                                                                'No hay mesas de esquejes creadas.'}
                                                            </div>
                                                        ) : (
                                                            <div style={{
                                                                display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '1.25rem', width: '100%'
                                                            }}>
                                                                {cloneMaps.map(map => {
                                                                    const mapBatches = room?.batches?.filter(b => b.clone_map_id === map.id && b.quantity > 0) || [];
                                                                    const totalPlants = mapBatches.reduce((acc, b) => acc + b.quantity, 0);
                                                                    const uniqueGenetics = new Set(mapBatches.map(b => b.genetic_id)).size;

                                                                    return (
                                                                        <DroppableMapCard key={map.id} map={map} onClick={() => setActiveMapId(map.id)}>
                                                                            <div style={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
                                                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.85rem' }}>
                                                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                                                                        <div style={{
                                                                                            width: '36px',
                                                                                            height: '36px',
                                                                                            borderRadius: '0.5rem',
                                                                                            background: 'rgba(56, 189, 248, 0.15)',
                                                                                            border: '1px solid rgba(56, 189, 248, 0.3)',
                                                                                            display: 'flex',
                                                                                            alignItems: 'center',
                                                                                            justifyContent: 'center',
                                                                                            color: '#38bdf8',
                                                                                            flexShrink: 0
                                                                                        }}>
                                                                                            <LucideMap size={18} />
                                                                                        </div>
                                                                                        <div>
                                                                                            <h3 style={{ margin: 0, fontSize: '1.1rem', color: '#f8fafc', fontWeight: 600 }}>{map.name}</h3>
                                                                                            <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{map.grid_rows || 0} x {map.grid_columns || 0} celdas</span>
                                                                                        </div>
                                                                                    </div>
                                                                                    <div style={{ display: 'flex', gap: '0.4rem' }}>
                                                                                        {!isSelectionMode && (
                                                                                            <button
                                                                                                onClick={(e) => { e.stopPropagation(); handleEditMapClick(e, map); }}
                                                                                                title="Editar Mapa"
                                                                                                style={{ background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)', color: '#94a3b8', cursor: 'pointer', padding: '0.35rem', borderRadius: '0.375rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                                                                            >
                                                                                                <Pencil size={14} />
                                                                                            </button>
                                                                                        )}
                                                                                        <button
                                                                                            onClick={(e) => { e.stopPropagation(); setMapIdToDelete(map.id); setIsDeleteMapModalOpen(true); }}
                                                                                            title="Eliminar Mapa"
                                                                                            style={{ background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.2)', color: '#f87171', cursor: 'pointer', padding: '0.35rem', borderRadius: '0.375rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                                                                        >
                                                                                            <LucideTrash2 size={14} />
                                                                                        </button>
                                                                                    </div>
                                                                                </div>

                                                                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                                                                                    <div style={{ background: 'rgba(56, 189, 248, 0.1)', border: '1px solid rgba(56, 189, 248, 0.2)', padding: '0.6rem 0.5rem', borderRadius: '0.5rem', textAlign: 'center' }}>
                                                                                        <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#38bdf8' }}>{totalPlants}</div>
                                                                                        <div style={{ fontSize: '0.75rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Plantas</div>
                                                                                    </div>
                                                                                    <div style={{ background: 'rgba(74, 222, 128, 0.1)', border: '1px solid rgba(74, 222, 128, 0.2)', padding: '0.6rem 0.5rem', borderRadius: '0.5rem', textAlign: 'center' }}>
                                                                                        <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#4ade80' }}>{uniqueGenetics}</div>
                                                                                        <div style={{ fontSize: '0.75rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Variedades</div>
                                                                                    </div>
                                                                                </div>

                                                                                {/* Telemetría y Vincular Dispositivo a esta Mesa */}
                                                                                {(() => {
                                                                                    const linkedDev = trazappDevices.find(d =>
                                                                                        (d.bunker_name === map.name || (!d.bunker_name && d.room_id === room?.id && cloneMaps.length === 1)) && d.is_active
                                                                                    );
                                                                                    return (
                                                                                        <div style={{
                                                                                            marginTop: '0.75rem',
                                                                                            padding: '0.5rem 0.75rem',
                                                                                            background: 'rgba(15, 23, 42, 0.7)',
                                                                                            border: '1px solid rgba(255, 255, 255, 0.08)',
                                                                                            borderRadius: '0.5rem',
                                                                                            display: 'flex',
                                                                                            alignItems: 'center',
                                                                                            justifyContent: 'space-between',
                                                                                            fontSize: '0.8rem'
                                                                                        }}>
                                                                                            {linkedDev ? (
                                                                                                <>
                                                                                                    <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                                                                                                        <span style={{ color: linkedDev.device_type === 'sense_7in' ? '#38bdf8' : '#00E080', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                                                                                                            {linkedDev.device_type === 'sense_7in' ? '🖥️' : <Wifi size={12} />} {linkedDev.alias || linkedDev.device_id}
                                                                                                        </span>
                                                                                                        <span style={{ color: '#ef4444', fontWeight: 600 }}>
                                                                                                            <Thermometer size={11} /> {linkedDev.last_reading?.sensors?.temp_c !== undefined ? `${linkedDev.last_reading.sensors.temp_c.toFixed(1)}°C` : '--'}
                                                                                                        </span>
                                                                                                        <span style={{ color: '#3b82f6', fontWeight: 600 }}>
                                                                                                            <Droplets size={11} /> {linkedDev.last_reading?.sensors?.hum_pct !== undefined ? `${linkedDev.last_reading.sensors.hum_pct.toFixed(1)}%` : '--'}
                                                                                                        </span>
                                                                                                    </div>
                                                                                                    <button
                                                                                                        onClick={(e) => {
                                                                                                            e.stopPropagation();
                                                                                                            setSelectedDeviceForModal(linkedDev);
                                                                                                            setDeviceModalInitialView('config');
                                                                                                            setIsDeviceModalOpen(true);
                                                                                                        }}
                                                                                                        style={{ background: 'rgba(59, 130, 246, 0.2)', border: '1px solid rgba(59, 130, 246, 0.4)', color: '#60a5fa', padding: '0.25rem 0.6rem', borderRadius: '0.375rem', fontSize: '0.75rem', cursor: 'pointer', fontWeight: 600 }}
                                                                                                    >
                                                                                                        Configurar
                                                                                                    </button>
                                                                                                </>
                                                                                            ) : (
                                                                                                <button
                                                                                                    onClick={(e) => {
                                                                                                        e.stopPropagation();
                                                                                                        const availableDev = trazappDevices.find(d => !d.bunker_name || d.room_id !== room?.id) || trazappDevices[0];
                                                                                                        if (availableDev) {
                                                                                                            setSelectedDeviceForModal({
                                                                                                                ...availableDev,
                                                                                                                room_id: room?.id,
                                                                                                                bunker_name: map.name
                                                                                                            });
                                                                                                            setDeviceModalInitialView('config');
                                                                                                            setIsDeviceModalOpen(true);
                                                                                                        } else {
                                                                                                            Swal.fire('Sin Dispositivos', 'No hay dispositivos TrazApp registrados para vincular.', 'info');
                                                                                                        }
                                                                                                    }}
                                                                                                    style={{ background: 'rgba(16, 185, 129, 0.15)', border: '1px dashed rgba(16, 185, 129, 0.4)', color: '#34d399', padding: '0.35rem 0.75rem', borderRadius: '0.375rem', fontSize: '0.75rem', cursor: 'pointer', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem', fontWeight: 600 }}
                                                                                                >
                                                                                                    <LucidePlus size={10} /> Vincular Monitor / Sensor a {map.name}
                                                                                                </button>
                                                                                            )}
                                                                                        </div>
                                                                                    );
                                                                                })()}

                                                                                {/* Visual CTA button to enter map */}
                                                                                <div style={{
                                                                                    marginTop: '0.85rem',
                                                                                    padding: '0.55rem 0.85rem',
                                                                                    background: 'rgba(56, 189, 248, 0.12)',
                                                                                    border: '1px solid rgba(56, 189, 248, 0.25)',
                                                                                    borderRadius: '0.5rem',
                                                                                    color: '#38bdf8',
                                                                                    fontSize: '0.82rem',
                                                                                    fontWeight: 600,
                                                                                    display: 'flex',
                                                                                    alignItems: 'center',
                                                                                    justifyContent: 'space-between',
                                                                                    transition: 'all 0.2s'
                                                                                }}>
                                                                                    <span style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                                                                                        <LucideGrid size={13} /> {room?.type === 'living_soil' ? 'Ver Cama de Cultivo' : 'Ver Mapa de Plantas'}
                                                                                    </span>
                                                                                    <ChevronRight size={15} />
                                                                                </div>

                                                                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginTop: '0.6rem', color: '#64748b', fontSize: '0.75rem' }}>
                                                                                    <LucideCalendar size={10} />
                                                                                    <span>Creada: {map.created_at ? format(new Date(map.created_at), "d 'de' MMM yyyy", { locale: es }) : '-'}</span>
                                                                                </div>
                                                                            </div>
                                                                        </DroppableMapCard>
                                                                    );
                                                                })}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Render CreateCard completely separate so mobile order can flex correctly */}
                                                <div className="create-map-card-wrapper">
                                                    <style>{`
                                                        @media (max-width: 768px) {
                                                            .mobile-compact-create-card {
                                                                min-height: 60px !important;
                                                                flex-direction: row !important;
                                                                padding: 0.75rem 1rem !important;
                                                                gap: 0.75rem !important;
                                                            }
                                                            .mobile-compact-create-card .dashed-circle-icon {
                                                                width: 32px !important;
                                                                height: 32px !important;
                                                                font-size: 1rem !important;
                                                            }
                                                            .mobile-compact-create-card p, .mobile-compact-create-card span {
                                                                font-size: 0.95rem !important;
                                                                margin: 0;
                                                            }
                                                            .map-drop-zone-wrapper {
                                                                margin-top: 0.5rem !important; /* Reduce gap to dropzone */
                                                            }
                                                        }
                                                    `}</style>
                                                    <CreateCard
                                                        className="mobile-compact-create-card"
                                                        onClick={() => setIsMapModalOpen(true)}
                                                        style={{ ...(cloneMaps.length > 0 ? { minHeight: '160px' } : {}) }}
                                                    >
                                                        <DashedCircle className="dashed-circle-icon">
                                                            <LucidePlus />
                                                        </DashedCircle>
                                                        {cloneMaps.length === 0 ? (
                                                            <p style={{ fontWeight: 600, fontSize: '1.1rem', margin: 0 }}>
                                                                {room?.type === 'vegetation' ? '+ Agregar cultivo a Vegetación' :
                                                                    room?.type === 'germination' ? 'Germinar Semillas' :
                                                                        room?.type === 'flowering' ? 'Agregar plantas a floración' :
                                                                            ['clones', 'esquejes', 'esquejera'].includes((room?.type || '').toLowerCase()) ? 'Crear Mesa de Esquejes' :
                                                                                room?.type === 'living_soil' ? 'Nueva Cama/Cultivo' :
                                                                                    'Crear Primera Mesa'}
                                                            </p>
                                                        ) : (
                                                            <span style={{ fontWeight: 600 }}>{room?.type === 'living_soil' ? 'Nueva Cama/Cultivo' : 'Nueva Mesa'}</span>
                                                        )}
                                                    </CreateCard>
                                                </div>

                                                {/* Create Map Drop Zone */}
                                                <div className="map-drop-zone-wrapper">
                                                    <CreateMapDropZone>
                                                        <style>{`
                                                            .dropzone-content {
                                                                text-align: center;
                                                                padding: 2rem;
                                                                color: #a0aec0;
                                                                display: flex;
                                                                flex-direction: column;
                                                                align-items: center;
                                                                justify-content: center;
                                                            }
                                                            .dropzone-icon {
                                                                font-size: 2rem;
                                                                margin-bottom: 0.5rem;
                                                                opacity: 0.5;
                                                            }
                                                            .dropzone-text {
                                                                margin: 0;
                                                            }
                                                            @media (max-width: 768px) {
                                                                .dropzone-content {
                                                                    flex-direction: row;
                                                                    padding: 1rem;
                                                                    text-align: left;
                                                                    gap: 1rem;
                                                                }
                                                                .dropzone-icon {
                                                                    font-size: 1.5rem;
                                                                    margin-bottom: 0;
                                                                    flex-shrink: 0;
                                                                }
                                                                .dropzone-text {
                                                                    font-size: 0.85rem;
                                                                    overflow: hidden;
                                                                    text-overflow: ellipsis;
                                                                    white-space: nowrap;
                                                                }
                                                            }
                                                        `}</style>
                                                        <div className="dropzone-content">
                                                            <LucideMap className="dropzone-icon" />
                                                            <p className="dropzone-text">{room?.type === 'living_soil' ? 'Crea una nueva cama/cultivo para sembrar o transplantar' : 'Arrastra lote aquí para crear nuevo mapa'}</p>
                                                        </div>
                                                    </CreateMapDropZone>
                                                </div>

                                                {/* Modals are kept here within main flow or moved to root? They are safer here for now */}
                                                {/* Create Map Modal */}




                                            </div>
                                        ) : (
                                            /* ACTIVE MAP VIEW */
                                            (() => {
                                                const activeMap = cloneMaps.find(m => m.id === activeMapId);
                                                if (!activeMap) return (
                                                    <div style={{
                                                        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                                                        padding: '4rem 2rem', background: 'rgba(15, 23, 42, 0.4)', backdropFilter: 'blur(12px)', borderRadius: '1rem', border: '1px dashed rgba(255, 255, 255, 0.2)',
                                                        color: '#94a3b8', textAlign: 'center'
                                                    }}>
                                                        <h3 style={{ fontSize: '1.25rem', color: '#f8fafc', marginBottom: '0.5rem' }}>Mapa No Encontrado</h3>
                                                        <p style={{ marginBottom: '1.5rem' }}>El mapa que intentas ver no existe o ha sido eliminado.</p>
                                                        <button
                                                            onClick={() => setActiveMapId(null)}
                                                            style={{
                                                                background: 'rgba(56, 189, 248, 0.2)', color: '#38bdf8', padding: '0.5rem 1rem', borderRadius: '0.5rem',
                                                                border: '1px solid rgba(56, 189, 248, 0.5)', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem',
                                                                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.2)'
                                                            }}
                                                        >
                                                            <ArrowLeft /> Volver a la Lista
                                                        </button>
                                                    </div>
                                                );
                                                return (
                                                    <div className="active-map-container" ref={mapContainerRef}>

                                                        {/* Print-only title since toolbar is hidden on print */}
                                                        <div className="printable-map-details" style={{
                                                            marginBottom: '2rem',
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            alignItems: 'center',
                                                            gap: '1rem'
                                                        }}>
                                                            <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'center', gap: '0.75rem' }}>
                                                                <h2 style={{ margin: 0, fontSize: '1.75rem', color: '#f8fafc', fontWeight: 700 }}>
                                                                    {activeMap?.name} {new Date(activeMap?.created_at || Date.now()).toLocaleDateString()}
                                                                </h2>
                                                                <span style={{
                                                                    background: 'transparent',
                                                                    color: '#4ade80',
                                                                    border: '1px solid #4ade80',
                                                                    padding: '0.2rem 0.6rem',
                                                                    borderRadius: '9999px',
                                                                    fontSize: '1rem',
                                                                    fontWeight: 600
                                                                }}>
                                                                    {activeMap?.grid_rows} x {activeMap?.grid_columns}
                                                                </span>
                                                            </div>

                                                            <div className="no-print" style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                                                                <button
                                                                    onClick={() => handlePrintMap()}
                                                                    title="Imprimir Mapa"
                                                                    style={{
                                                                        background: 'rgba(15, 23, 42, 0.4)',
                                                                        backdropFilter: 'blur(8px)',
                                                                        border: '1px solid rgba(255, 255, 255, 0.1)',
                                                                        borderRadius: '0.5rem',
                                                                        padding: '0.5rem 1rem',
                                                                        fontSize: '0.9rem',
                                                                        cursor: 'pointer',
                                                                        color: '#e2e8f0',
                                                                        fontWeight: 600,
                                                                        display: 'flex', alignItems: 'center', gap: '0.5rem',
                                                                        transition: 'all 0.2s'
                                                                    }}
                                                                    onMouseEnter={e => {
                                                                        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                                                                    }}
                                                                    onMouseLeave={e => {
                                                                        e.currentTarget.style.background = 'rgba(15, 23, 42, 0.4)';
                                                                    }}
                                                                >
                                                                    <Printer /> Imprimir
                                                                </button>

                                                                <button
                                                                    onClick={() => setActiveMapId(null)}
                                                                    style={{
                                                                        background: 'rgba(15, 23, 42, 0.4)',
                                                                        backdropFilter: 'blur(8px)',
                                                                        border: '1px solid rgba(255, 255, 255, 0.1)',
                                                                        padding: '0.5rem 1rem',
                                                                        borderRadius: '0.5rem',
                                                                        color: '#e2e8f0',
                                                                        fontWeight: 600,
                                                                        cursor: 'pointer',
                                                                        display: 'flex', alignItems: 'center', gap: '0.5rem',
                                                                        transition: 'all 0.2s'
                                                                    }}
                                                                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)'}
                                                                    onMouseLeave={e => e.currentTarget.style.background = 'rgba(15, 23, 42, 0.4)'}
                                                                >
                                                                    <ArrowLeft /> Volver
                                                                </button>
                                                            </div>
                                                        </div>

                                                        <div className={`printable-map-grid ${room?.type === 'living_soil' ? 'hide-on-map-print' : ''}`}>
                                                            <div
                                                                className="print-debug"
                                                                style={{ display: 'none', color: 'red', fontWeight: 'bold', fontSize: '14px', border: '1px solid red', padding: '5px', marginBottom: '10px' }}
                                                            >
                                                                DEBUG INFO:<br />
                                                                Room Type: {room?.type}<br />
                                                                Map ID: {activeMapId}<br />
                                                                Rows: {cloneMaps.find(m => m.id === activeMapId)?.grid_rows}<br />
                                                                Cols: {cloneMaps.find(m => m.id === activeMapId)?.grid_columns}<br />
                                                                Batches: {livingSoilBatches.filter(b => b.clone_map_id === activeMapId).length}
                                                            </div>
                                                            {isRelocatingSelection && (
                                                                <div style={{
                                                                    background: 'rgba(56, 189, 248, 0.1)',
                                                                    backdropFilter: 'blur(8px)',
                                                                    border: '2px dashed rgba(56, 189, 248, 0.5)',
                                                                    borderRadius: '0.5rem',
                                                                    padding: '1rem',
                                                                    marginBottom: '1rem',
                                                                    textAlign: 'center',
                                                                    color: '#38bdf8',
                                                                    fontWeight: 'bold',
                                                                    display: 'flex',
                                                                    justifyContent: 'center',
                                                                    alignItems: 'center',
                                                                    gap: '1rem',
                                                                    boxShadow: '0 4px 6px rgba(0,0,0,0.3)'
                                                                }}>
                                                                    <ArrowLeftRight />
                                                                    <span>MODO REUBICACIÓN: Selecciona la nueva posición (celda vacía) para los {selectedBatchIds.size} lotes seleccionados.</span>
                                                                    <button
                                                                        onClick={() => setIsRelocatingSelection(false)}
                                                                        style={{
                                                                            background: 'rgba(56, 189, 248, 0.2)',
                                                                            border: '1px solid rgba(56, 189, 248, 0.5)',
                                                                            color: '#38bdf8',
                                                                            padding: '0.25rem 0.75rem',
                                                                            borderRadius: '0.25rem',
                                                                            cursor: 'pointer',
                                                                            fontSize: '0.9rem'
                                                                        }}
                                                                    >
                                                                        Cancelar
                                                                    </button>
                                                                </div>
                                                            )}
                                                            {room?.type === 'living_soil' ? (
                                                                <LivingSoilGrid
                                                                    rows={(() => {
                                                                        const activeMap = cloneMaps.find(m => m.id === activeMapId);
                                                                        return activeMap?.grid_rows || 20;
                                                                    })()}
                                                                    cols={(() => {
                                                                        const activeMap = cloneMaps.find(m => m.id === activeMapId);
                                                                        return activeMap?.grid_columns || 10;
                                                                    })()}
                                                                    batches={livingSoilBatches}
                                                                    mapId={activeMapId || undefined}
                                                                    onBatchClick={handleBatchClick}
                                                                    selectedBatchIds={selectedBatchIds}
                                                                    isSelectionMode={isSelectionMode}
                                                                    onToggleSelectionMode={setIsSelectionMode}
                                                                    onSelectionChange={(newSet) => { if (!isRelocatingSelection) setSelectedBatchIds(newSet); }}
                                                                    isRelocating={!!movingBatch || isRelocatingSelection}
                                                                />
                                                            ) : (
                                                                <EsquejeraGrid
                                                                    rows={(() => {
                                                                        const activeMap = cloneMaps.find(m => m.id === activeMapId);
                                                                        return activeMap?.grid_rows || 20;
                                                                    })()}
                                                                    cols={(() => {
                                                                        const activeMap = cloneMaps.find(m => m.id === activeMapId);
                                                                        return activeMap?.grid_columns || 10;
                                                                    })()}
                                                                    batches={livingSoilBatches.filter(b => b.clone_map_id === activeMapId)}
                                                                    onBatchClick={handleBatchClick}
                                                                    selectedBatchIds={selectedBatchIds}
                                                                    selectionMode={isSelectionMode}
                                                                    onSelectionChange={(newSet) => { if (!isRelocatingSelection) setSelectedBatchIds(newSet); }}
                                                                    isRelocating={!!movingBatch || isRelocatingSelection}
                                                                />
                                                            )}

                                                            {/* SOWING MODAL */}
                                                            {isSowingModalOpen && (
                                                                <PortalModalOverlay>
                                                                    <ModalContent>
                                                                        <h2 style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                                                            <Sprout color="#48bb78" /> Nueva Siembra ({sowingPosition})
                                                                        </h2>

                                                                        <FormGroup>
                                                                            <label>Genética</label>
                                                                            <select
                                                                                value={newSowingBatch.genetic_id}
                                                                                onChange={e => setNewSowingBatch({ ...newSowingBatch, genetic_id: e.target.value })}
                                                                            >
                                                                                <option value="">Seleccionar Genética...</option>
                                                                                {genetics.map(g => (
                                                                                    <option key={g.id} value={g.id}>{g.name} ({g.type === 'photoperiodic' ? 'Foto' : 'Auto'})</option>
                                                                                ))}
                                                                            </select>
                                                                        </FormGroup>

                                                                        <FormGroup>
                                                                            <label>Notas</label>
                                                                            <textarea
                                                                                value={newSowingBatch.notes}
                                                                                onChange={e => setNewSowingBatch({ ...newSowingBatch, notes: e.target.value })}
                                                                                placeholder="Observaciones..."
                                                                            />
                                                                        </FormGroup>

                                                                        <div style={{ display: 'flex', gap: '1rem', marginTop: '2rem' }}>
                                                                            <CancelButton onClick={() => setIsSowingModalOpen(false)}>Cancelar</CancelButton>
                                                                            <ActionButton $variant="success" onClick={handleSowingSubmit} disabled={!newSowingBatch.genetic_id || loading}>
                                                                                {loading ? 'Sembrando...' : 'Sembrar'}
                                                                            </ActionButton>
                                                                        </div>
                                                                    </ModalContent>
                                                                </PortalModalOverlay>
                                                            )}
                                                        </div>

                                                        <div className="printable-map-details">
                                                            <h4 style={{ margin: '2rem 0 1rem 0', borderBottom: '2px solid rgba(255, 255, 255, 0.2)', paddingBottom: '0.5rem', color: '#f8fafc' }}>Detalle de Lotes</h4>
                                                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem', color: '#94a3b8' }}>
                                                                <thead>
                                                                    <tr style={{ background: 'rgba(15, 23, 42, 0.6)', textAlign: 'left' }}>
                                                                        <th style={{ padding: '0.5rem', borderBottom: '2px solid rgba(255, 255, 255, 0.1)' }}>Posición</th>
                                                                        <th style={{ padding: '0.5rem', borderBottom: '2px solid rgba(255, 255, 255, 0.1)' }}>Genética / Nombre</th>
                                                                        <th style={{ padding: '0.5rem', borderBottom: '2px solid rgba(255, 255, 255, 0.1)' }}>Código (ID)</th>
                                                                        <th style={{ padding: '0.5rem', borderBottom: '2px solid rgba(255, 255, 255, 0.1)' }}>Fecha Ingreso</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {(room?.batches?.filter(b => b.clone_map_id === activeMapId) || [])
                                                                        .sort((a, b) => {
                                                                            // Simple alphanumeric sort for positions like A1, A2, B1
                                                                            return (a.grid_position || '').localeCompare(b.grid_position || '', undefined, { numeric: true, sensitivity: 'base' });
                                                                        })
                                                                        .map(batch => (
                                                                            <tr key={batch.id} style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}>
                                                                                <td style={{ padding: '0.4rem', borderRight: '1px solid rgba(255, 255, 255, 0.05)', fontWeight: 'bold', textAlign: 'center', color: '#f8fafc' }}>{batch.grid_position || '-'}</td>
                                                                                <td style={{ padding: '0.4rem', borderRight: '1px solid rgba(255, 255, 255, 0.05)', color: '#cbd5e1' }}>{batch.genetic?.name || batch.name}</td>
                                                                                <td style={{ padding: '0.4rem', borderRight: '1px solid rgba(255, 255, 255, 0.05)', fontFamily: 'monospace', color: '#cbd5e1' }}>{batch.tracking_code || '-'}</td>
                                                                                <td style={{ padding: '0.4rem', color: '#cbd5e1' }}>
                                                                                    {batch.created_at ? format(new Date(batch.created_at), 'dd/MM/yyyy', { locale: es }) : '-'}
                                                                                </td>
                                                                            </tr>
                                                                        ))}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    </div>
                                                );
                                            })()
                                        )
                                        }
                                    </div>

                                    {/* SIDEBAR (Shared) */}
                                    <div className={`no-print sidebar-container ${cloneMaps.length === 0 ? 'empty-maps' : ''}`} style={{
                                        flex: 1,
                                        minWidth: '250px',
                                        background: 'rgba(15, 23, 42, 0.4)',
                                        backdropFilter: 'blur(12px)',
                                        padding: '0.75rem',
                                        borderRadius: '0.5rem',
                                        border: '1px solid rgba(255, 255, 255, 0.1)',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        maxHeight: 'calc(100vh - 160px)',
                                        position: 'sticky',
                                        top: '1rem',
                                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.3)'
                                    }}>
                                        <div style={{ flexShrink: 0 }}>
                                            <h4 style={{ margin: '0 0 1rem 0', color: '#f8fafc', fontSize: '1rem' }}>Lotes Disponibles</h4>
                                            <ActionButton className="tour-add-batch" onClick={() => {
                                                setNewBatch({
                                                    geneticId: '',
                                                    quantity: '',
                                                    date: new Date().toISOString()
                                                });
                                                setIsCreateBatchModalOpen(true);
                                            }} $variant="success" style={{ width: '100%', marginBottom: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                                                <LucidePlus /> Nuevo Lote
                                            </ActionButton>
                                        </div>
                                        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                                            {(() => {
                                                const available = room?.batches?.filter(b => !b.clone_map_id && b.quantity > 0 && b.current_room_id === room.id && !b.discarded_at) || [];
                                                const grouped = groupBatchesByGeneticDate(available);
                                                if (grouped.length === 0) return <div style={{ fontSize: '0.85rem', color: '#a0aec0', padding: '1rem', textAlign: 'center' }}>No hay lotes en espera.</div>;
                                                return grouped.map(group => (
                                                    <SidebarBatchGroup
                                                        key={group.root.id}
                                                        group={group}
                                                        expanded={expandedSidebarGroups.has(group.root.id)}
                                                        onToggleExpand={() => toggleSidebarGroupExpansion(group.root.id)}
                                                        onBatchGroupClick={handleBatchClick}
                                                        renderHeaderActions={(b) => (
                                                            <>
                                                                <BatchActionButton onClick={(e) => { e.stopPropagation(); handleEditBatchClick(e, b); }} title="Editar Lote"><Pencil size={12} /></BatchActionButton>
                                                                <BatchActionButton $variant="delete" onClick={(e) => { e.stopPropagation(); handleDeleteBatchClick(e, b); }} title="Eliminar Lote"><LucideTrash2 size={12} /></BatchActionButton>
                                                            </>
                                                        )}
                                                        childrenRender={(b) => (
                                                            <div key={b.id} style={{
                                                                display: 'flex',
                                                                justifyContent: 'space-between',
                                                                alignItems: 'center',
                                                                padding: '0.25rem 0.5rem',
                                                                margin: '0.25rem 0',
                                                                border: '1px solid #e2e8f0',
                                                                borderRadius: '0.5rem',
                                                                transition: 'all 0.2s',
                                                                background: selectedBatchId === b.id ? '#ebf8ff' : 'transparent'
                                                            }}>
                                                                <div style={{ flex: 1 }}>
                                                                    <DraggableStockBatch batch={b} onClick={() => handleBatchClick(b)} />
                                                                </div>
                                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginLeft: '0.5rem' }}>
                                                                    <BatchActionButton onClick={(e) => { e.stopPropagation(); handleEditBatchClick(e, b); }} title="Editar Lote"><Pencil size={12} /></BatchActionButton>
                                                                    <BatchActionButton $variant="delete" onClick={(e) => { e.stopPropagation(); handleDeleteBatchClick(e, b); }} title="Eliminar Lote"><LucideTrash2 size={12} /></BatchActionButton>
                                                                </div>
                                                            </div>
                                                        )}
                                                    />
                                                ));
                                            })()}
                                        </div>
                                    </div>
                                </div >

                                <DragOverlay>
                                    {activeItem ? (
                                        activeItem.type === 'batch' ? (
                                            <div style={{ transform: 'none' }}>
                                                <DraggableStockBatch batch={activeItem.batch} />
                                            </div>
                                        ) : activeItem.type === 'batch-group' ? (
                                            <div style={{ background: 'rgba(56, 189, 248, 0.1)', backdropFilter: 'blur(8px)', padding: '0.5rem', borderRadius: '0.5rem', border: '1px solid rgba(56, 189, 248, 0.5)', boxShadow: '0 4px 6px rgba(0,0,0,0.3)', width: '250px', color: '#f8fafc' }}>
                                                <strong>{activeItem.group.root.name}</strong>
                                                <div style={{ fontSize: '0.8rem', color: '#cbd5e1' }}>Arrastrando grupo...</div>
                                            </div>
                                        ) : activeItem.type === 'genetic' ? (
                                            <div style={{ transform: 'none' }}>
                                                <DraggableGenetic genetic={activeItem.genetic} />
                                            </div>
                                        ) : null
                                    ) : null}
                                </DragOverlay>
                            </DndContext >
                        </div >
                    ) : ['drying', 'secado', 'curing', 'curado'].includes((room?.type || '').toLowerCase()) ? (
                        /* DRYING ROOM VIEW */
                        <div className="no-print">
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                                {groupBatchesByGeneticDate(room?.batches || [], true).map(group => {
                                    const { root, children } = group;
                                    const isExpanded = expandedDryingGroups.has(root.id);
                                    const totalQty = root.quantity + children.reduce((acc: number, c: any) => acc + c.quantity, 0);
                                    const groupName = root.genetic?.name || root.name; // Logic from SidebarBatchGroup

                                    let displayName = '';

                                    // 1. Try Virtual Group Name
                                    if (root._virtualGroupName) {
                                        displayName = root._virtualGroupName;
                                    } else {
                                        // 2. Same prioritization as HarvestModal Map/Parent association
                                        const mapName = root.clone_map_id && room?.clone_maps ? room.clone_maps.find((m: any) => m.id === root.clone_map_id)?.name : null;
                                        const parentName = root.parent_batch?.name;

                                        if (mapName || parentName) {
                                            displayName = mapName || parentName || '';
                                        } else {
                                            // 3. Fallback: Forcefully rebuild the exact string format used in HarvestModal
                                            const geneticStr = root.genetic?.name || root.name || 'Desconocida';
                                            const prefix = geneticStr.substring(0, 6).toUpperCase();
                                            const d = new Date(root.start_date || root.created_at);
                                            d.setMinutes(d.getMinutes() + d.getTimezoneOffset());
                                            const displayDate = `${d.getDate().toString().padStart(2, '0')}/${(d.getMonth() + 1).toString().padStart(2, '0')}/${d.getFullYear()}`;
                                            displayName = `Lote ${prefix} ${displayDate}`;
                                        }
                                    }

                                    return (
                                        <div key={root.id} style={{
                                            background: 'rgba(15, 23, 42, 0.4)', border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: '0.5rem',
                                            boxShadow: '0 4px 6px -1px rgba(0,0,0,0.2)', overflow: 'hidden', backdropFilter: 'blur(8px)'
                                        }}>
                                            {/* Group Header */}
                                            <div
                                                className="drying-group-header"
                                                onClick={() => toggleDryingGroupExpansion(root.id)}
                                                style={{
                                                    padding: '1rem',
                                                    cursor: 'pointer',
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    background: isExpanded ? 'rgba(30, 41, 59, 0.8)' : 'transparent',
                                                    borderBottom: isExpanded ? '1px solid rgba(255, 255, 255, 0.1)' : 'none',
                                                    transition: 'background 0.2s'
                                                }}
                                            >
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                                                    <div style={{
                                                        width: '24px', height: '24px', borderRadius: '50%',
                                                        background: 'rgba(74, 222, 128, 0.2)', color: '#4ade80',
                                                        border: '1px solid rgba(74, 222, 128, 0.5)',
                                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                        fontSize: '0.8rem'
                                                    }}>
                                                        {isExpanded ? '▼' : '▶'}
                                                    </div>
                                                    <div>
                                                        <h3 style={{ margin: 0, fontSize: '1.1rem', color: '#f8fafc' }}>{displayName}</h3>
                                                        <span style={{ fontSize: '0.9rem', color: '#94a3b8' }}>{totalQty} plantas en total</span>
                                                    </div>
                                                </div>
                                                <div className="drying-group-actions" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                                    <HoverButton
                                                        onClick={(e) => handleOpenGroupFinalize(e, [root, ...children])}
                                                        style={{ background: 'rgba(237, 137, 54, 0.2)', color: '#ed8936', border: '1px solid rgba(237, 137, 54, 0.5)' }}
                                                    >
                                                        Finalizar Grupo
                                                    </HoverButton>
                                                    <Badge stage="drying">Secado</Badge>
                                                </div>
                                            </div>

                                            {/* Expanded Content: Individual Batches - SMOOTH ANIMATION */}
                                            <ExpandableContainer $expanded={isExpanded}>
                                                <ExpandableInner>
                                                    <div style={{
                                                        padding: '0.75rem', // Reduced padding
                                                        display: 'grid',
                                                        gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', // Smaller cards
                                                        gap: '0.75rem', // Reduced gap
                                                        background: 'rgba(15, 23, 42, 0.2)',
                                                        opacity: isExpanded ? 1 : 0,
                                                        transition: 'opacity 0.4s ease-in-out'
                                                    }}>
                                                        {[root, ...children].map(b => {
                                                            const daysInDrying = differenceInDays(new Date(), new Date(b.created_at));
                                                            return (
                                                                <div key={b.id} style={{
                                                                    background: 'rgba(30, 41, 59, 0.6)', border: '1px solid rgba(255, 255, 255, 0.05)', borderRadius: '0.5rem',
                                                                    padding: '0.5rem', // Compact padding
                                                                    position: 'relative',
                                                                    display: 'flex', flexDirection: 'column', gap: '0.25rem' // Tight layout
                                                                }}>
                                                                    <div className="drying-batch-card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                                        <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#cbd5e1' }}>{b.tracking_code || 'S/C'}</span>
                                                                        <span style={{ fontSize: '0.7rem', color: '#64748b' }}>{format(new Date(b.created_at), 'd MMM', { locale: es })}</span>
                                                                    </div>

                                                                    <h4 style={{ margin: 0, fontSize: '0.85rem', color: '#f8fafc', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={b.name}>{b.name}</h4>

                                                                    <div style={{ display: 'flex', gap: '0.75rem', fontSize: '0.75rem', color: '#94a3b8' }}>
                                                                        <span><strong style={{ color: '#cbd5e1' }}>{b.quantity}</strong> u.</span>
                                                                        <span><strong style={{ color: '#cbd5e1' }}>{daysInDrying}</strong> días</span>
                                                                    </div>

                                                                    <button style={{
                                                                        width: '100%',
                                                                        marginTop: '0.25rem',
                                                                        background: 'rgba(237, 137, 54, 0.2)', color: '#ed8936', border: '1px solid rgba(237, 137, 54, 0.5)',
                                                                        padding: '0.25rem', borderRadius: '0.25rem', cursor: 'pointer',
                                                                        fontWeight: 600, fontSize: '0.75rem',
                                                                        transition: 'all 0.2s',
                                                                        backdropFilter: 'blur(4px)'
                                                                    }}
                                                                        onMouseEnter={e => { e.currentTarget.style.background = 'rgba(237, 137, 54, 0.3)'; }}
                                                                        onMouseLeave={e => { e.currentTarget.style.background = 'rgba(237, 137, 54, 0.2)'; }}
                                                                        onClick={(e) => { e.stopPropagation(); handleOpenFinalize(b); }}>
                                                                        Finalizar
                                                                    </button>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </ExpandableInner>
                                            </ExpandableContainer>
                                        </div>
                                    );
                                })}

                                {(!room?.batches || room.batches.length === 0) && (
                                    <div style={{ textAlign: 'center', padding: '3rem', color: '#a0aec0', border: '2px dashed #cbd5e0', borderRadius: '1rem' }}>
                                        <Thermometer style={{ fontSize: '3rem', marginBottom: '1rem', opacity: 0.5 }} />
                                        <p>La sala de secado está vacía.</p>
                                        <p style={{ fontSize: '0.9rem' }}>Realiza una cosecha desde Floración para enviar plantas aquí.</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : null
                }



                {/* Calendar Section (Hidden for Clones/Germination) */}
                {
                    !['clones', 'esquejes', 'esquejera', 'drying', 'secado', 'curing', 'curado', 'germination'].includes((room?.type as string)?.toLowerCase()) && (
                        <CalendarContainer className="no-print">
                            {/* Calendar Header */}
                            <CalendarHeader>
                                <StyledActionButton onClick={() => setCurrentDate(subMonths(currentDate, 1))} $variant="secondary" style={{ padding: '0.5rem 1rem' }}>
                                    <ChevronLeft /> Anterior
                                </StyledActionButton>
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem' }}>
                                    <h2 style={{ fontSize: '1.5rem', color: '#f8fafc', textTransform: 'capitalize', margin: 0 }}>
                                        {format(currentDate, 'MMMM yyyy', { locale: es })}
                                    </h2>
                                    <StyledActionButton onClick={handlePrintCalendar} $variant="primary" style={{ fontSize: '0.8rem', padding: '0.4rem 0.8rem' }}>
                                        <Printer /> Imprimir Mes Horizontal
                                    </StyledActionButton>
                                </div>
                                <StyledActionButton onClick={() => setCurrentDate(addMonths(currentDate, 1))} $variant="secondary" style={{ padding: '0.5rem 1rem' }}>
                                    Siguiente <ChevronRight />
                                </StyledActionButton>
                            </CalendarHeader>

                            {/* Calendar Grid */}
                            <CalendarScrollWrapper>
                                <CalendarGrid>
                                    {['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'].map(d => (
                                        <div key={d} style={{ background: 'rgba(15, 23, 42, 0.6)', padding: '0.75rem', textAlign: 'center', fontWeight: 'bold', color: '#94a3b8', fontSize: '0.85rem' }}>{d}</div>
                                    ))}

                                    {(() => {
                                        const monthStart = startOfMonth(currentDate);
                                        const monthEnd = endOfMonth(monthStart);
                                        const startDate = startOfWeek(monthStart);
                                        const endDate = endOfWeek(monthEnd);
                                        const dateFormat = "d";


                                        const calendarDays = eachDayOfInterval({ start: startDate, end: endDate });

                                        return calendarDays.map((dayItem, idx) => {
                                            const isCurrentMonth = isSameMonth(dayItem, monthStart);
                                            const dateStr = format(dayItem, 'yyyy-MM-dd');
                                            const dayTasks = allTasksForView.filter(t => t.due_date && t.due_date.split('T')[0] === dateStr);

                                            // Determine Background Gradient based on Task Distribution
                                            let dayBg = 'transparent';

                                            if (dayTasks.length > 0) {
                                                // Helper to get pastel color by task type
                                                const getTaskColor = (t: Task) => {
                                                    const customType = taskTypes.find(tt => tt.name.toLowerCase() === t.type.toLowerCase());
                                                    if (customType && customType.color) return customType.color;

                                                    const lowerType = t.type.toLowerCase();
                                                    if (lowerType.includes('riego')) return 'rgba(56, 189, 248, 0.2)'; // Blue
                                                    if (lowerType.includes('fertiliz') || lowerType.includes('enmienda') || lowerType.includes('compost')) return 'rgba(74, 222, 128, 0.2)'; // Green
                                                    if (lowerType.includes('poda') || lowerType.includes('danger') || lowerType.includes('alerta') || lowerType.includes('warning')) return 'rgba(239, 68, 68, 0.2)'; // Red
                                                    if (lowerType.includes('defoliacion') || lowerType.includes('esqueje')) return 'rgba(249, 115, 22, 0.2)'; // Orange
                                                    if (lowerType.includes('hst') || lowerType.includes('lst') || lowerType.includes('entrenamiento')) return 'rgba(6, 182, 212, 0.25)'; // Cyan Tech

                                                    return 'rgba(148, 163, 184, 0.2)'; // Gray (Info)
                                                };

                                                if (dayTasks.length === 1) {
                                                    dayBg = getTaskColor(dayTasks[0]);
                                                } else {
                                                    // Build linear gradient
                                                    const step = 100 / dayTasks.length;
                                                    const stops = dayTasks.map((t, i) => {
                                                        const color = getTaskColor(t);
                                                        return `${color} ${i * step}% ${(i + 1) * step}% `;
                                                    }).join(', ');
                                                    dayBg = `linear-gradient(135deg, ${stops})`;
                                                }
                                            }

                                            // Phase Calculation Logic
                                            let phaseBar = null;



                                            if (room?.start_date && isCurrentMonth) {
                                                // ... existing logic ...
                                                const [y, m, d] = room.start_date.split('T')[0].split('-').map(Number);
                                                const roomStart = new Date(y, m - 1, d);
                                                const dayTime = new Date(dayItem).setHours(0, 0, 0, 0);
                                                const startTime = roomStart.setHours(0, 0, 0, 0);

                                                if (dayTime >= startTime) {
                                                    const weekNum = Math.floor((dayTime - startTime) / (7 * 24 * 60 * 60 * 1000)) + 1;
                                                    let isFloweringPhase = false;
                                                    const activeBatch = room.batches?.find(b => b.genetic);
                                                    const geneticVegWeeks = activeBatch?.genetic?.vegetative_weeks;

                                                    if (geneticVegWeeks !== undefined && weekNum > geneticVegWeeks) {
                                                        isFloweringPhase = true;
                                                    } else if (room.type === 'flowering') {
                                                        isFloweringPhase = true;
                                                    }

                                                    const color = isFloweringPhase ? '#fcd34d' : '#4ade80';

                                                    // Only show label on the numeric day of the week that the crop started, or strictly on day 1 of month if it's the start, or on the absolute start day
                                                    const showLabel = dayItem.getDay() === new Date(startTime).getDay() || dayTime === startTime;

                                                    phaseBar = (
                                                        <>
                                                            <div style={{
                                                                position: 'absolute', bottom: 0, left: 0, right: 0,
                                                                height: '6px', background: color, opacity: 0.7
                                                            }} title={`Semana ${weekNum} del ciclo ${isFloweringPhase ? '(Floración)' : '(Vegetativo)'} `}></div>
                                                            {showLabel && (
                                                                <div style={{
                                                                    position: 'absolute', bottom: '8px', right: '2px',
                                                                    fontSize: '0.65rem', fontWeight: 'bold', color: isFloweringPhase ? '#fef3c7' : '#f0fdf4',
                                                                    background: 'rgba(15, 23, 42, 0.8)', padding: '0 4px', borderRadius: '4px', border: `1px solid ${color}`
                                                                }}>Sem {weekNum}</div>
                                                            )}
                                                        </>
                                                    );
                                                }
                                            }

                                            return (
                                                <div
                                                    key={dayItem.toString()}
                                                    className="calendar-day calendar-day-hover"
                                                    style={{
                                                        background: dayBg, // Applied dynamic background
                                                        minHeight: '100px',
                                                        padding: '0.5rem',
                                                        position: 'relative',
                                                        opacity: isCurrentMonth ? 1 : 0.4,
                                                        cursor: user && (user.role === 'admin' || user.role === 'partner') ? 'pointer' : 'default',
                                                        transition: 'all 0.2s',
                                                        border: isSameDay(dayItem, new Date()) ? '2px solid #38bdf8' : '1px solid transparent'
                                                    }}
                                                    onClick={() => handleDayDetails(dayItem)}
                                                >
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                        <span style={{ fontWeight: isSameDay(dayItem, new Date()) ? 'bold' : 'normal', color: isSameDay(dayItem, new Date()) ? '#7dd3fc' : '#f8fafc' }}>
                                                            {format(dayItem, dateFormat)}
                                                        </span>
                                                        {/* Actions: Add Task / Sticky */}
                                                        {user && (user.role === 'admin' || user.role === 'partner') && (
                                                            <div style={{ display: 'flex', gap: '4px' }}>

                                                                <span
                                                                    style={{ color: '#94a3b8', fontSize: '0.8rem', opacity: 0.8, cursor: 'pointer', padding: '2px' }}
                                                                    title="Agregar Tarea"
                                                                    onClick={(e) => { e.stopPropagation(); handleAddTask(dayItem); }}
                                                                >
                                                                    <LucidePlus />
                                                                </span>
                                                            </div>
                                                        )}
                                                    </div>

                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', marginTop: '4px' }}>
                                                        {dayTasks.map(t => {
                                                            const isVirtual = t.id.startsWith('virtual-');
                                                            return (
                                                                <div
                                                                    key={t.id}
                                                                    onClick={(e) => {
                                                                        if (isVirtual) {
                                                                            e.stopPropagation();
                                                                            setIsProjectionAlertOpen(true);
                                                                            return;
                                                                        }
                                                                        handleTaskClick(e, t);
                                                                    }}
                                                                    style={{
                                                                        fontSize: '0.7rem', padding: '2px 4px', borderRadius: '3px',
                                                                        background: isVirtual ? 'rgba(255,255,255,0.4)' : 'rgba(255,255,255,0.6)',
                                                                        color: '#2d3748',
                                                                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                                                                        borderLeft: `3px solid ${t.status === 'done' ? '#48bb78' : isVirtual ? '#a0aec0' : '#718096'} `,
                                                                        cursor: isVirtual ? 'default' : 'pointer',
                                                                        boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                                                                        opacity: isVirtual ? 0.7 : 1,
                                                                        fontStyle: isVirtual ? 'italic' : 'normal',
                                                                        border: isVirtual ? '1px dashed #cbd5e0' : 'none',
                                                                        textDecoration: t.status === 'done' ? 'line-through' : 'none'
                                                                    }}
                                                                    title={isVirtual ? "Proyección futura (Virtual)" : t.title}
                                                                >
                                                                    {t.crop_id ? `[${room?.clone_maps?.find(m => m.id === t.crop_id)?.name || room?.batches?.find(b => b.id === t.crop_id)?.name || 'Mapa Eliminado'}] ` : ''}
                                                                    {t.title.replace(' (Proyectada)', '')}
                                                                </div>
                                                            );
                                                        })}
                                                    </div>

                                                    {phaseBar}
                                                </div>
                                            );
                                        });
                                    })()}
                                </CalendarGrid>
                            </CalendarScrollWrapper>
                        </CalendarContainer>
                    )
                }




                {/* Task Modal-Interactive */}
                {/* Task Modal-Interactive */}
                {
                    (isTaskModalOpen || isClosingTaskModal) && (
                        <PortalModalOverlay isClosing={isClosingTaskModal}>
                            <TaskModalContent isClosing={isClosingTaskModal}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem', paddingBottom: '1.25rem', borderBottom: '1px solid rgba(255, 255, 255, 0.08)' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.85rem' }}>
                                        <div style={{
                                            width: '42px',
                                            height: '42px',
                                            borderRadius: '0.75rem',
                                            background: 'rgba(74, 222, 128, 0.12)',
                                            border: '1px solid rgba(74, 222, 128, 0.25)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: '#4ade80'
                                        }}>
                                            <LucideClipboardList size={20} />
                                        </div>
                                        <div>
                                            <h3 style={{ fontSize: '1.35rem', fontWeight: 700, margin: 0, color: '#f8fafc', letterSpacing: '-0.02em' }}>
                                                {selectedTask ? 'Detalle de Tarea' : 'Nueva Tarea'}
                                            </h3>
                                            <p style={{ margin: '0.2rem 0 0 0', fontSize: '0.85rem', color: '#94a3b8' }}>
                                                {selectedTask ? 'Consulta o actualiza la información de la tarea asignada' : `Planifica y asigna actividades operativas en ${room?.name || 'la sala'}`}
                                            </p>
                                        </div>
                                    </div>
                                    <button
                                        onClick={closeTaskModal}
                                        style={{
                                            background: 'rgba(255, 255, 255, 0.05)',
                                            border: '1px solid rgba(255, 255, 255, 0.1)',
                                            cursor: 'pointer',
                                            color: '#94a3b8',
                                            width: '36px',
                                            height: '36px',
                                            borderRadius: '0.5rem',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            transition: 'all 0.2s'
                                        }}
                                        onMouseEnter={e => {
                                            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                                            e.currentTarget.style.color = '#f8fafc';
                                        }}
                                        onMouseLeave={e => {
                                            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                                            e.currentTarget.style.color = '#94a3b8';
                                        }}
                                    >
                                        <LucideX size={18} />
                                    </button>
                                </div>

                                {user && (user.role === 'admin' || user.role === 'partner') ? (
                                    <>
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2rem', flex: 1 }}>
                                            {/* Left Column: Primary Info */}
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', flex: '1 1 300px' }}>
                                                <FormGroup>
                                                    <label style={{ color: '#cbd5e1', fontWeight: 600, fontSize: '0.88rem', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                                                        <LucideTag size={13} style={{ color: '#38bdf8' }} /> Tipo de Tarea
                                                    </label>
                                                    <CustomSelect
                                                        value={taskForm.type}
                                                        onChange={(value) => {
                                                            setTaskForm({ ...taskForm, type: value, title: value });
                                                        }}
                                                        options={
                                                            taskTypes.length > 0
                                                                ? taskTypes.map(t => ({ value: t.name, label: t.name }))
                                                                : [{ value: "info", label: "Info" }, { value: "riego", label: "Riego" }] // Fallback if empty
                                                        }
                                                        placeholder="Seleccionar Tipo"
                                                    />
                                                </FormGroup>

                                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem' }}>
                                                    <FormGroup style={{ flex: '1 1 150px' }}>
                                                        <label style={{ color: '#cbd5e1', fontWeight: 600, fontSize: '0.88rem', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                                                            <LucideCalendar size={13} style={{ color: '#c084fc' }} /> Fecha
                                                        </label>
                                                        <CustomDatePicker
                                                            selected={taskForm.due_date ? new Date(taskForm.due_date.replace(/-/g, '/') + ' 12:00:00') : new Date()}
                                                            onChange={(date) => {
                                                                if (date) {
                                                                    setTaskForm({ ...taskForm, due_date: format(date, 'yyyy-MM-dd') });
                                                                }
                                                            }}
                                                        />
                                                    </FormGroup>
                                                    <FormGroup style={{ flex: '1 1 150px' }}>
                                                        <label style={{ color: '#cbd5e1', fontWeight: 600, fontSize: '0.88rem', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                                                            <LucideLayers size={13} style={{ color: '#38bdf8' }} /> Asignar a Mapa
                                                        </label>
                                                        <CustomSelect
                                                            value={taskForm.crop_id || ""}
                                                            onChange={(value) => setTaskForm({ ...taskForm, crop_id: value || null })}
                                                            options={[
                                                                { value: "", label: "-- General (Sala) --" },
                                                                ...(room?.clone_maps?.map((map: any) => ({ value: map.id, label: map.name })) || [])
                                                            ]}
                                                            placeholder="Seleccionar Mapa..."
                                                        />
                                                    </FormGroup>
                                                </div>
                                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem' }}>
                                                    <FormGroup style={{ flex: '1 1 150px' }}>
                                                        <label style={{ color: '#cbd5e1', fontWeight: 600, fontSize: '0.88rem', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                                                            <LucidePackage size={13} style={{ color: '#fbbf24' }} /> Insumo (Opcional)
                                                        </label>
                                                        <CustomSelect
                                                            value={taskForm.insumo_id || ""}
                                                            onChange={(value) => setTaskForm({ ...taskForm, insumo_id: value })}
                                                            options={[
                                                                { value: "", label: "-- Sin Insumo --" },
                                                                ...insumos.map((i: Insumo) => ({ value: i.id, label: i.nombre }))
                                                            ]}
                                                            placeholder="Seleccionar Insumo..."
                                                        />
                                                    </FormGroup>
                                                    <FormGroup style={{ flex: '1 1 150px' }}>
                                                        <label style={{ color: '#cbd5e1', fontWeight: 600, fontSize: '0.88rem', marginBottom: '0.5rem', display: 'block' }}>Volumen Estimado</label>
                                                        <input
                                                            type="number"
                                                            step="0.01"
                                                            value={taskForm.estimated_volume || ""}
                                                            onChange={(e) => setTaskForm({ ...taskForm, estimated_volume: e.target.value })}
                                                            placeholder={`Ej: 1.5 (${insumos.find((i: Insumo) => i.id === taskForm.insumo_id)?.unit_of_measurement || 'ml'})`}
                                                            disabled={!taskForm.insumo_id}
                                                            style={{
                                                                padding: '0.75rem',
                                                                borderRadius: '0.5rem',
                                                                border: '1px solid rgba(255, 255, 255, 0.12)',
                                                                background: 'rgba(15, 23, 42, 0.6)',
                                                                color: '#f8fafc',
                                                                width: '100%',
                                                                boxSizing: 'border-box',
                                                                outline: 'none',
                                                                opacity: !taskForm.insumo_id ? 0.6 : 1
                                                            }}
                                                        />
                                                    </FormGroup>
                                                    <FormGroup style={{ flex: '1 1 150px' }}>
                                                        <label style={{ color: '#cbd5e1', fontWeight: 600, fontSize: '0.88rem', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                                                            <LucideUser size={13} style={{ color: '#4ade80' }} /> Asignar a Usuario
                                                        </label>
                                                        <CustomSelect
                                                            value={taskForm.assigned_to}
                                                            onChange={(value) => setTaskForm({ ...taskForm, assigned_to: value })}
                                                            options={[
                                                                { value: "", label: "-- Sin Asignar --" },
                                                                ...users.map(u => ({ value: u.id, label: u.full_name || u.email || 'Usuario' }))
                                                            ]}
                                                            placeholder="Asignar a..."
                                                        />
                                                    </FormGroup>
                                                </div>

                                                {/* Recurrence Section */}
                                                <div style={{ padding: '1.1rem', background: 'rgba(15, 23, 42, 0.5)', borderRadius: '0.75rem', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
                                                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', fontWeight: 600, color: '#f8fafc', cursor: 'pointer', marginBottom: recurrenceEnabled ? '1rem' : 0, fontSize: '0.92rem', transition: 'margin-bottom 0.3s ease-out' }}>
                                                        <input
                                                            type="checkbox"
                                                            checked={recurrenceEnabled}
                                                            onChange={e => setRecurrenceEnabled(e.target.checked)}
                                                            style={{ width: '1.2rem', height: '1.2rem', accentColor: '#10b981', cursor: 'pointer' }}
                                                        />
                                                        Repetir Tarea (Periodicidad)
                                                    </label>

                                                    <div style={{
                                                        display: 'grid',
                                                        gridTemplateRows: recurrenceEnabled ? '1fr' : '0fr',
                                                        transition: 'grid-template-rows 0.3s ease-out'
                                                    }}>
                                                        <div style={{ overflow: 'hidden', minHeight: 0 }}>
                                                            <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
                                                                <div style={{ flex: 1 }}>
                                                                    <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '0.5rem', color: '#94a3b8' }}>Repetir cada:</label>
                                                                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                                                                        <input
                                                                            type="number"
                                                                            min="1"
                                                                            value={recurrenceConfig.interval}
                                                                            onChange={e => {
                                                                                const val = e.target.value;
                                                                                setRecurrenceConfig(prev => ({
                                                                                    ...prev,
                                                                                    interval: val === '' ? '' : Math.max(1, parseInt(val))
                                                                                }));
                                                                            }}
                                                                            style={{ width: '75px', padding: '0.55rem', borderRadius: '0.375rem', border: '1px solid rgba(255, 255, 255, 0.12)', fontSize: '0.9rem', background: 'rgba(15, 23, 42, 0.6)', color: '#f8fafc', colorScheme: 'dark', outline: 'none' }}
                                                                        />
                                                                        <div style={{ flex: 1 }}>
                                                                            <CustomSelect
                                                                                value={recurrenceConfig.unit}
                                                                                onChange={(value) => setRecurrenceConfig(prev => ({ ...prev, unit: value as any, type: 'custom' }))}
                                                                                options={[
                                                                                    { value: "day", label: "Días" },
                                                                                    { value: "week", label: "Semanas" },
                                                                                    { value: "month", label: "Meses" }
                                                                                ]}
                                                                                placeholder="Unidad"
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>

                                                            {recurrenceConfig.unit === 'week' && (
                                                                <div>
                                                                    <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '0.5rem', color: '#94a3b8' }}>Se repite el:</label>
                                                                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                                                                        {['D', 'L', 'M', 'M', 'J', 'V', 'S'].map((day, idx) => {
                                                                            const isSelected = recurrenceConfig.daysOfWeek?.includes(idx);
                                                                            return (
                                                                                <button
                                                                                    key={idx}
                                                                                    type="button"
                                                                                    onClick={() => {
                                                                                        setRecurrenceConfig(prev => {
                                                                                            const days = prev.daysOfWeek || [];
                                                                                            if (days.includes(idx)) return { ...prev, daysOfWeek: days.filter(d => d !== idx) };
                                                                                            return { ...prev, daysOfWeek: [...days, idx] };
                                                                                        });
                                                                                    }}
                                                                                    style={{
                                                                                        width: '34px', height: '34px', borderRadius: '50%', border: '1px solid',
                                                                                        borderColor: isSelected ? 'rgba(74, 222, 128, 0.5)' : 'rgba(255, 255, 255, 0.12)',
                                                                                        background: isSelected ? 'rgba(74, 222, 128, 0.2)' : 'rgba(255, 255, 255, 0.05)',
                                                                                        color: isSelected ? '#4ade80' : '#94a3b8',
                                                                                        fontWeight: 700, cursor: 'pointer', fontSize: '0.82rem',
                                                                                        boxShadow: isSelected ? '0 0 12px rgba(74, 222, 128, 0.25)' : 'none',
                                                                                        transition: 'all 0.2s'
                                                                                    }}
                                                                                >
                                                                                    {day}
                                                                                </button>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Right Column: Instructions & Files */}
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', flex: '1 1 300px' }}>
                                                <FormGroup style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                                                    <label style={{ color: '#cbd5e1', fontWeight: 600, fontSize: '0.88rem', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                                                        <LucideFileText size={13} style={{ color: '#38bdf8' }} /> Instrucciones
                                                    </label>
                                                    <textarea
                                                        value={taskForm.description}
                                                        onChange={e => setTaskForm({ ...taskForm, description: e.target.value })}
                                                        placeholder="Escribe aquí los detalles precisos (ej: '5ml/L de CalMag', 'Limpieza de bandejas', etc.)..."
                                                        style={{
                                                            flex: 1,
                                                            padding: '1rem',
                                                            borderRadius: '0.5rem',
                                                            border: '1px solid rgba(255, 255, 255, 0.12)',
                                                            fontSize: '0.92rem',
                                                            resize: 'none',
                                                            minHeight: '190px',
                                                            background: 'rgba(15, 23, 42, 0.6)',
                                                            color: '#f8fafc',
                                                            lineHeight: '1.5',
                                                            outline: 'none',
                                                            boxSizing: 'border-box'
                                                        }}
                                                    />
                                                </FormGroup>

                                                <div>
                                                    <label style={{ display: 'block', fontWeight: 600, color: '#cbd5e1', fontSize: '0.88rem', marginBottom: '0.5rem' }}>Adjuntar Evidencia o Archivo</label>
                                                    <button
                                                        type="button"
                                                        onClick={() => alert("Funcionalidad de subida de fotos en desarrollo. Podés grabar notas de voz a continuación.")}
                                                        style={{
                                                            background: 'rgba(255, 255, 255, 0.03)',
                                                            border: '1px dashed rgba(255, 255, 255, 0.18)',
                                                            padding: '0.85rem',
                                                            width: '100%',
                                                            borderRadius: '0.65rem',
                                                            cursor: 'pointer',
                                                            color: '#cbd5e1',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            gap: '0.5rem',
                                                            transition: 'all 0.2s',
                                                            fontWeight: 500,
                                                            fontSize: '0.88rem',
                                                            marginBottom: '1rem'
                                                        }}
                                                        onMouseEnter={e => {
                                                            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.06)';
                                                            e.currentTarget.style.borderColor = 'rgba(74, 222, 128, 0.4)';
                                                            e.currentTarget.style.color = '#f8fafc';
                                                        }}
                                                        onMouseLeave={e => {
                                                            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.03)';
                                                            e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.18)';
                                                            e.currentTarget.style.color = '#cbd5e1';
                                                        }}
                                                    >
                                                        <LucideUploadCloud size={16} style={{ color: '#38bdf8' }} /> Adjuntar Foto / Archivo
                                                    </button>

                                                    <label style={{ display: 'block', fontWeight: 600, color: '#cbd5e1', fontSize: '0.88rem', marginBottom: '0.5rem' }}>Nota de Voz (Instrucción de Audio)</label>
                                                    <AudioRecorderWidget
                                                        title="Nota de Voz de Cultivo"
                                                        description="Graba instrucciones operativas rápidas para el equipo de sala (riego, nutrientes, poda)."
                                                        onAudioRecorded={(url) => {
                                                            if (url) {
                                                                setTaskForm(prev => ({
                                                                    ...prev,
                                                                    description: prev.description ? `${prev.description}\n[Audio Nota]: ${url}` : `[Audio Nota]: ${url}`
                                                                }));
                                                            }
                                                        }}
                                                        orgId={getSelectedOrgId() || 'task'}
                                                        patientId={user?.id || 'room'}
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        {/* Footer Actions */}
                                        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '0.75rem', marginTop: '2rem', paddingTop: '1.25rem', borderTop: '1px solid rgba(255, 255, 255, 0.08)' }}>
                                            {selectedTask && (
                                                <ShadcnButton
                                                    variant="destructive"
                                                    onClick={handleDeleteTask}
                                                >
                                                    <LucideTrash2 size={15} /> Eliminar
                                                </ShadcnButton>
                                            )}
                                            <ShadcnButton
                                                variant="secondary"
                                                onClick={closeTaskModal}
                                            >
                                                Cancelar
                                            </ShadcnButton>
                                            <ShadcnButton
                                                variant="default"
                                                onClick={handleSaveTask}
                                                disabled={isSavingTask}
                                            >
                                                {isSavingTask ? <Loader2 className="spin" /> : <LucideCheck size={16} />}
                                                {selectedTask ? 'Guardar Cambios' : 'Crear Tarea'}
                                            </ShadcnButton>
                                        </div>
                                    </>
                                ) : (
                                    // Read Only View for Employees (Execution Mode)
                                    <div>
                                        <div style={{
                                            background: 'rgba(255, 255, 255, 0.03)',
                                            border: '1px solid rgba(255, 255, 255, 0.08)',
                                            borderRadius: '0.75rem',
                                            padding: '1.25rem',
                                            marginBottom: '1.5rem'
                                        }}>
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                                                <h4 style={{ margin: 0, color: '#f8fafc', fontSize: '1.1rem', fontWeight: 700 }}>
                                                    {selectedTask?.title}
                                                </h4>
                                                <ShadcnBadge variant={selectedTask?.status === 'done' ? 'emerald' : 'amber'}>
                                                    {selectedTask?.status === 'done' ? 'Completada' : 'Pendiente'}
                                                </ShadcnBadge>
                                            </div>
                                            <p style={{ color: '#94a3b8', fontSize: '0.92rem', margin: '0 0 1rem 0', lineHeight: 1.5 }}>
                                                {selectedTask?.description || 'Sin instrucciones adicionales.'}
                                            </p>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem', color: '#64748b' }}>
                                                <LucideUser size={14} style={{ color: '#4ade80' }} />
                                                <span>Asignado a: <strong style={{ color: '#cbd5e1' }}>{users.find(u => u.id === selectedTask?.assigned_to)?.full_name || 'Nadie'}</strong></span>
                                            </div>
                                        </div>

                                        {/* Observations Field for Execution */}
                                        <FormGroup>
                                            <label style={{ color: '#cbd5e1', fontWeight: 600, fontSize: '0.88rem', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                                                <LucideFileText size={13} style={{ color: '#38bdf8' }} /> Observaciones al completar
                                            </label>
                                            <textarea
                                                placeholder="Registra observaciones puntuales al completar (ej: pH medido, plaga controlada)..."
                                                value={taskForm.description}
                                                onChange={e => setTaskForm({ ...taskForm, description: e.target.value })}
                                                style={{
                                                    width: '100%',
                                                    padding: '1rem',
                                                    borderRadius: '0.5rem',
                                                    border: '1px solid rgba(255, 255, 255, 0.12)',
                                                    fontSize: '0.92rem',
                                                    resize: 'none',
                                                    minHeight: '100px',
                                                    background: 'rgba(15, 23, 42, 0.6)',
                                                    color: '#f8fafc',
                                                    lineHeight: '1.5',
                                                    outline: 'none',
                                                    boxSizing: 'border-box',
                                                    marginBottom: '1rem'
                                                }}
                                            />
                                        </FormGroup>

                                        <div style={{ marginBottom: '1.5rem' }}>
                                            <button
                                                type="button"
                                                onClick={() => alert("Subir foto de evidencia (En desarrollo)")}
                                                style={{
                                                    background: 'rgba(255, 255, 255, 0.03)',
                                                    border: '1px dashed rgba(255, 255, 255, 0.18)',
                                                    padding: '0.85rem',
                                                    width: '100%',
                                                    borderRadius: '0.65rem',
                                                    cursor: 'pointer',
                                                    color: '#cbd5e1',
                                                    fontSize: '0.88rem',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    gap: '0.5rem',
                                                    transition: 'all 0.2s',
                                                    fontWeight: 500
                                                }}
                                                onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(74, 222, 128, 0.4)'; e.currentTarget.style.color = '#f8fafc'; e.currentTarget.style.background = 'rgba(255, 255, 255, 0.06)'; }}
                                                onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.18)'; e.currentTarget.style.color = '#cbd5e1'; e.currentTarget.style.background = 'rgba(255, 255, 255, 0.03)'; }}
                                            >
                                                <LucideUploadCloud size={16} style={{ color: '#38bdf8' }} /> Subir Foto de Evidencia
                                            </button>
                                        </div>

                                        <ShadcnButton
                                            variant={selectedTask?.status === 'done' ? 'secondary' : 'default'}
                                            onClick={async () => {
                                                if (selectedTask) {
                                                    const isCurrentlyDone = selectedTask.status === 'done';

                                                    // Prevent completing future tasks
                                                    if (!isCurrentlyDone) {
                                                        const dateToCheck = taskForm.due_date || selectedTask.due_date;
                                                        if (dateToCheck) {
                                                            const taskDate = new Date(dateToCheck);
                                                            const today = new Date();
                                                            taskDate.setHours(0, 0, 0, 0);
                                                            today.setHours(0, 0, 0, 0);

                                                            if (taskDate > today) {
                                                                setToastState({ isOpen: true, message: 'No puedes completar tareas que no corresponden a la fecha actual o pasada.', type: 'error' });
                                                                return;
                                                            }
                                                        }
                                                    }

                                                    const newStatus = isCurrentlyDone ? 'pending' : 'done';

                                                    await tasksService.updateTask(selectedTask.id, {
                                                        observations: taskForm.description,
                                                        status: newStatus
                                                    } as any);

                                                    if (id) {
                                                        const updatedTasks = await tasksService.getTasksByRoomId(id);
                                                        setTasks(updatedTasks);
                                                    }
                                                    setIsTaskModalOpen(false);
                                                }
                                            }}
                                            disabled={isSavingTask}
                                            style={{ width: '100%' }}
                                        >
                                            {isSavingTask ? <Loader2 className="spin" /> : <LucideCheck size={16} />}
                                            {selectedTask?.status === 'done' ? 'Marcar como Pendiente' : 'Completar y Guardar Tarea'}
                                        </ShadcnButton>
                                    </div>
                                )}
                            </TaskModalContent>
                        </PortalModalOverlay>
                    )
                }

                {/* Modern Shadcn Day Summary Modal */}
                <DaySummaryModal
                    isOpen={isDaySummaryOpen}
                    onClose={() => setIsDaySummaryOpen(false)}
                    date={selectedDayForSummary}
                    tasks={selectedDayForSummary
                        ? allTasksForView.filter(t => t.due_date && t.due_date.split('T')[0] === format(selectedDayForSummary, 'yyyy-MM-dd'))
                        : []
                    }
                    onToggleTaskStatus={handleToggleTaskStatus}
                    onEditTask={(task) => {
                        setIsDaySummaryOpen(false);
                        const parentTaskOption = task.id.toString().startsWith('virtual')
                            ? tasks.find(pt => pt.id === task.id.toString().split(' -')[1].trim())
                            : task;

                        if (parentTaskOption) {
                            handleTaskClick({ stopPropagation: () => { } } as any, parentTaskOption);
                        }
                    }}
                    room={room}
                    users={users}
                />

                {/* Modern Shadcn Sticky Note Modal */}
                <StickyNoteModal
                    isOpen={isStickyModalOpen}
                    onClose={handleCloseStickyModal}
                    onSave={async (content, color) => {
                        if (!id) return;
                        setIsSavingSticky(true);
                        try {
                            await stickiesService.createSticky(content, color, id, undefined);
                            const freshStickies = await stickiesService.getStickies(id);
                            setStickies(freshStickies);
                            handleCloseStickyModal();
                        } catch (error) {
                            console.error("Error saving sticky:", error);
                            alert("Error al guardar la nota. Intente nuevamente.");
                        } finally {
                            setIsSavingSticky(false);
                        }
                    }}
                    title="Nueva Nota"
                    subtitle={`Fija un recordatorio para la sala ${room?.name || ''}`}
                    placeholder="Escribe recordatorios técnicos, observaciones, alertas o tareas..."
                    isSaving={isSavingSticky}
                />

                {/* Sticky Delete Confirmation Modal */}
                {
                    stickyToDelete && (
                        <PortalModalOverlay isClosing={isStickyDeleteModalClosing}>
                            <ModalContent isClosing={isStickyDeleteModalClosing} style={{ maxWidth: '400px', textAlign: 'center' }}>
                                <div style={{ color: '#e53e3e', fontSize: '3rem', marginBottom: '1rem' }}>
                                    <LucideAlertTriangle />
                                </div>
                                <h3 style={{ fontSize: '1.25rem', color: '#2d3748', marginBottom: '0.5rem' }}>¿Eliminar esta nota?</h3>
                                <p style={{ color: '#718096', marginBottom: '1.5rem' }}>
                                    Esta acción no se puede deshacer.
                                </p>
                                <div style={{ display: 'flex', gap: '1rem' }}>
                                    <CancelButton onClick={() => setStickyToDelete(null)} disabled={isDeletingSticky}>
                                        Cancelar
                                    </CancelButton>
                                    <ActionButton
                                        onClick={confirmDeleteSticky}
                                        $variant="danger"
                                        disabled={isDeletingSticky}
                                        style={{ opacity: isDeletingSticky ? 0.7 : 1 }}
                                    >
                                        {isDeletingSticky ? 'Eliminando...' : 'Eliminar'}
                                    </ActionButton>
                                </div>
                            </ModalContent>
                        </PortalModalOverlay>
                    )
                }

                {/* Projection Alert Modal */}
                {
                    isProjectionAlertOpen && (
                        <PortalModalOverlay>
                            <ModalContent style={{ maxWidth: '400px', textAlign: 'center' }}>
                                <div style={{ color: '#3182ce', fontSize: '3rem', marginBottom: '1rem' }}>
                                    <Clock />
                                </div>
                                <h3 style={{ fontSize: '1.25rem', color: '#2d3748', marginBottom: '0.5rem' }}>Tarea Proyectada</h3>
                                <p style={{ color: '#718096', marginBottom: '1.5rem', lineHeight: '1.5' }}>
                                    Esta es una proyección futura. Para activarla, primero debes completar la tarea anterior.
                                </p>
                                <ActionButton onClick={() => setIsProjectionAlertOpen(false)} $variant="primary">
                                    Entendido
                                </ActionButton>
                            </ModalContent>
                        </PortalModalOverlay>
                    )
                }

                {/* Delete Confirmation Modal (Tasks) */}
                {
                    isDeleteConfirmOpen && (
                        <PortalModalOverlay>
                            <ModalContent style={{ maxWidth: '400px', textAlign: 'center' }}>
                                <div style={{ color: '#e53e3e', fontSize: '3rem', marginBottom: '1rem' }}>
                                    <LucideAlertTriangle />
                                </div>
                                <h3 style={{ fontSize: '1.25rem', color: '#2d3748', marginBottom: '0.5rem' }}>¿Eliminar esta tarea?</h3>
                                <p style={{ color: '#718096', marginBottom: '1.5rem' }}>
                                    Esta acción no se puede deshacer. La tarea se eliminará permanentemente.
                                </p>
                                <div style={{ display: 'flex', gap: '1rem' }}>
                                    <CancelButton onClick={() => setIsDeleteConfirmOpen(false)}>
                                        Cancelar
                                    </CancelButton>
                                    <ActionButton onClick={confirmDeleteTask} $variant="danger">
                                        Sí, Eliminar
                                    </ActionButton>
                                </div>
                            </ModalContent>
                        </PortalModalOverlay>
                    )
                }

                {/* Genetics Modal */}
                {
                    isGeneticsModalOpen && room && (
                        <PortalModalOverlay>
                            <ModalContent style={{ maxWidth: '600px' }}>
                                <h3 style={{ fontSize: '1.25rem', color: '#2d3748', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                    <Dna style={{ color: '#2b6cb0' }} /> Todas las Genéticas en Sala
                                </h3>

                                <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                                    {(() => {
                                        const geneticCounts: Record<string, number> = {};
                                        room.batches?.forEach(b => {
                                            const name = b.genetic?.name || b.strain || 'Desconocida';
                                            geneticCounts[name] = (geneticCounts[name] || 0) + b.quantity;
                                        });
                                        const entries = Object.entries(geneticCounts);

                                        return (
                                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '1rem' }}>
                                                {entries.map(([name, count]) => (
                                                    <div key={name} style={{
                                                        background: '#ebf8ff',
                                                        border: '1px solid #bee3f8',
                                                        borderRadius: '0.5rem',
                                                        padding: '1rem',
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        alignItems: 'center'
                                                    }}>
                                                        <span style={{ fontWeight: 600, color: '#2b6cb0' }}>{name}</span>
                                                        <span style={{
                                                            background: 'white',
                                                            color: '#2b6cb0',
                                                            fontWeight: 800,
                                                            padding: '0.2rem 0.6rem',
                                                            borderRadius: '999px',
                                                            fontSize: '0.9rem',
                                                            boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                                                        }}>
                                                            {count}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        );
                                    })()}
                                </div>

                                <div style={{ marginTop: '2rem', display: 'flex', justifyContent: 'flex-end' }}>
                                    <ActionButton onClick={() => setIsGeneticsModalOpen(false)} style={{ maxWidth: '150px' }}>
                                        Cerrar
                                    </ActionButton>
                                </div>
                            </ModalContent>
                        </PortalModalOverlay>
                    )
                }

                {/* New Map Modal */}
                {
                    (isMapModalOpen || isClosingMapModal) && (
                        <PortalModalOverlay isClosing={isClosingMapModal}>
                            <ModalContent style={{ maxWidth: '440px' }} isClosing={isClosingMapModal}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.75rem', borderBottom: '1px solid rgba(255, 255, 255, 0.08)', paddingBottom: '1rem' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.85rem' }}>
                                        <div style={{
                                            width: 42,
                                            height: 42,
                                            borderRadius: '10px',
                                            background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.2) 0%, rgba(5, 150, 105, 0.1) 100%)',
                                            border: '1px solid rgba(16, 185, 129, 0.3)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: '#34d399',
                                            boxShadow: '0 0 15px rgba(16, 185, 129, 0.15)'
                                        }}>
                                            <LucideGrid size={20} />
                                        </div>
                                        <div>
                                            <h3 style={{ margin: 0, fontSize: '1.2rem', fontWeight: 700, color: '#f8fafc', letterSpacing: '-0.02em' }}>
                                                {room?.type === 'living_soil' ? 'Nueva Cama / Cultivo' : 'Nuevo Mapa de Esquejes'}
                                            </h3>
                                            <span style={{ fontSize: '0.8rem', color: '#94a3b8' }}>
                                                Configura el nombre y la cuadrícula de la mesa
                                            </span>
                                        </div>
                                    </div>
                                    <button
                                        onClick={closeMapModal}
                                        style={{
                                            background: 'rgba(255, 255, 255, 0.05)',
                                            border: '1px solid rgba(255, 255, 255, 0.1)',
                                            borderRadius: '0.5rem',
                                            width: 32,
                                            height: 32,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            cursor: 'pointer',
                                            color: '#94a3b8',
                                            transition: 'all 0.2s ease'
                                        }}
                                        onMouseOver={(e) => {
                                            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                                            e.currentTarget.style.color = '#ffffff';
                                        }}
                                        onMouseOut={(e) => {
                                            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                                            e.currentTarget.style.color = '#94a3b8';
                                        }}
                                    >
                                        <LucideX size={16} />
                                    </button>
                                </div>

                                <FormGroup style={{ marginBottom: '1.25rem' }}>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.5rem', fontWeight: 600, fontSize: '0.88rem', color: '#cbd5e1' }}>
                                        <LucideTag size={14} style={{ color: '#38bdf8' }} /> {room?.type === 'living_soil' ? 'Nombre (ej: Cama 1)' : 'Nombre (ej: Bandeja 1)'}
                                    </label>
                                    <input
                                        autoFocus
                                        value={newMapName}
                                        onChange={e => setNewMapName(e.target.value)}
                                        placeholder="Nombre del mapa o mesa..."
                                        style={{
                                            width: '100%',
                                            padding: '0.75rem 0.85rem',
                                            background: 'rgba(15, 23, 42, 0.6)',
                                            border: '1px solid rgba(255, 255, 255, 0.12)',
                                            color: '#f8fafc',
                                            borderRadius: '0.5rem',
                                            fontSize: '0.92rem',
                                            outline: 'none',
                                            transition: 'all 0.2s ease'
                                        }}
                                    />
                                </FormGroup>
                                <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem' }}>
                                    <FormGroup style={{ flex: 1, marginBottom: 0 }}>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.5rem', fontWeight: 600, fontSize: '0.88rem', color: '#cbd5e1' }}>
                                            <LucideLayers size={14} style={{ color: '#34d399' }} /> Filas (Máx 26)
                                        </label>
                                        <input
                                            type="number"
                                            min="1"
                                            max="26"
                                            value={newMapRows}
                                            onChange={e => setNewMapRows(e.target.value === '' ? '' : Number(e.target.value))}
                                            style={{
                                                width: '100%',
                                                padding: '0.75rem 0.85rem',
                                                background: 'rgba(15, 23, 42, 0.6)',
                                                border: '1px solid rgba(255, 255, 255, 0.12)',
                                                color: '#f8fafc',
                                                borderRadius: '0.5rem',
                                                fontSize: '0.92rem',
                                                outline: 'none'
                                            }}
                                        />
                                    </FormGroup>
                                    <FormGroup style={{ flex: 1, marginBottom: 0 }}>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.5rem', fontWeight: 600, fontSize: '0.88rem', color: '#cbd5e1' }}>
                                            <LucideGrid size={14} style={{ color: '#a78bfa' }} /> Columnas (Máx 50)
                                        </label>
                                        <input
                                            type="number"
                                            min="1"
                                            max="50"
                                            value={newMapCols}
                                            onChange={e => setNewMapCols(e.target.value === '' ? '' : Number(e.target.value))}
                                            style={{
                                                width: '100%',
                                                padding: '0.75rem 0.85rem',
                                                background: 'rgba(15, 23, 42, 0.6)',
                                                border: '1px solid rgba(255, 255, 255, 0.12)',
                                                color: '#f8fafc',
                                                borderRadius: '0.5rem',
                                                fontSize: '0.92rem',
                                                outline: 'none'
                                            }}
                                        />
                                    </FormGroup>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '1.5rem', paddingTop: '1.25rem', borderTop: '1px solid rgba(255, 255, 255, 0.08)' }}>
                                    <ShadcnButton variant="secondary" onClick={closeMapModal} disabled={isCreatingMap}>
                                        Cancelar
                                    </ShadcnButton>
                                    <ShadcnButton
                                        variant="default"
                                        onClick={handleCreateMap}
                                        disabled={isCreatingMap}
                                    >
                                        {isCreatingMap ? <Loader2 className="spin" /> : <LucidePlus size={16} />}
                                        {isCreatingMap ? 'Creando...' : 'Crear Mapa'}
                                    </ShadcnButton>
                                </div>
                            </ModalContent>
                        </PortalModalOverlay>
                    )
                }

                {/* Edit Map Modal */}
                {
                    (isEditMapModalOpen || isClosingEditMap) && (
                        <PortalModalOverlay isClosing={isClosingEditMap}>
                            <ModalContent onClick={e => e.stopPropagation()} isClosing={isClosingEditMap} style={{ maxWidth: '440px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.75rem', borderBottom: '1px solid rgba(255, 255, 255, 0.08)', paddingBottom: '1rem' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.85rem' }}>
                                        <div style={{
                                            width: 42,
                                            height: 42,
                                            borderRadius: '10px',
                                            background: 'linear-gradient(135deg, rgba(56, 189, 248, 0.2) 0%, rgba(14, 165, 233, 0.1) 100%)',
                                            border: '1px solid rgba(56, 189, 248, 0.3)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: '#38bdf8',
                                            boxShadow: '0 0 15px rgba(56, 189, 248, 0.15)'
                                        }}>
                                            <LucideGrid size={20} />
                                        </div>
                                        <div>
                                            <h3 style={{ margin: 0, fontSize: '1.2rem', fontWeight: 700, color: '#f8fafc', letterSpacing: '-0.02em' }}>
                                                Editar Mapa
                                            </h3>
                                            <span style={{ fontSize: '0.8rem', color: '#94a3b8' }}>
                                                Modifica las dimensiones o el nombre de la mesa
                                            </span>
                                        </div>
                                    </div>
                                    <button
                                        onClick={closeEditMapModal}
                                        style={{
                                            background: 'rgba(255, 255, 255, 0.05)',
                                            border: '1px solid rgba(255, 255, 255, 0.1)',
                                            borderRadius: '0.5rem',
                                            width: 32,
                                            height: 32,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            cursor: 'pointer',
                                            color: '#94a3b8',
                                            transition: 'all 0.2s ease'
                                        }}
                                        onMouseOver={(e) => {
                                            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                                            e.currentTarget.style.color = '#ffffff';
                                        }}
                                        onMouseOut={(e) => {
                                            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                                            e.currentTarget.style.color = '#94a3b8';
                                        }}
                                    >
                                        <LucideX size={16} />
                                    </button>
                                </div>

                                <FormGroup style={{ marginBottom: '1.25rem' }}>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.5rem', fontWeight: 600, fontSize: '0.88rem', color: '#cbd5e1' }}>
                                        <LucideTag size={14} style={{ color: '#38bdf8' }} /> Nombre
                                    </label>
                                    <input
                                        autoFocus
                                        value={editMapName}
                                        onChange={e => setEditMapName(e.target.value)}
                                        placeholder="Nombre del mapa..."
                                        style={{
                                            width: '100%',
                                            padding: '0.75rem 0.85rem',
                                            background: 'rgba(15, 23, 42, 0.6)',
                                            border: '1px solid rgba(255, 255, 255, 0.12)',
                                            color: '#f8fafc',
                                            borderRadius: '0.5rem',
                                            fontSize: '0.92rem',
                                            outline: 'none'
                                        }}
                                    />
                                </FormGroup>
                                <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem' }}>
                                    <FormGroup style={{ flex: 1, marginBottom: 0 }}>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.5rem', fontWeight: 600, fontSize: '0.88rem', color: '#cbd5e1' }}>
                                            <LucideLayers size={14} style={{ color: '#34d399' }} /> Filas (Máx 26)
                                        </label>
                                        <input
                                            type="number"
                                            min="1"
                                            max="26"
                                            value={editMapRows}
                                            onChange={e => setEditMapRows(e.target.value === '' ? '' : Number(e.target.value))}
                                            style={{
                                                width: '100%',
                                                padding: '0.75rem 0.85rem',
                                                background: 'rgba(15, 23, 42, 0.6)',
                                                border: '1px solid rgba(255, 255, 255, 0.12)',
                                                color: '#f8fafc',
                                                borderRadius: '0.5rem',
                                                fontSize: '0.92rem',
                                                outline: 'none'
                                            }}
                                        />
                                    </FormGroup>
                                    <FormGroup style={{ flex: 1, marginBottom: 0 }}>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.5rem', fontWeight: 600, fontSize: '0.88rem', color: '#cbd5e1' }}>
                                            <LucideGrid size={14} style={{ color: '#a78bfa' }} /> Columnas (Máx 50)
                                        </label>
                                        <input
                                            type="number"
                                            min="1"
                                            max="50"
                                            value={editMapCols}
                                            onChange={e => setEditMapCols(e.target.value === '' ? '' : Number(e.target.value))}
                                            style={{
                                                width: '100%',
                                                padding: '0.75rem 0.85rem',
                                                background: 'rgba(15, 23, 42, 0.6)',
                                                border: '1px solid rgba(255, 255, 255, 0.12)',
                                                color: '#f8fafc',
                                                borderRadius: '0.5rem',
                                                fontSize: '0.92rem',
                                                outline: 'none'
                                            }}
                                        />
                                    </FormGroup>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '1.5rem', paddingTop: '1.25rem', borderTop: '1px solid rgba(255, 255, 255, 0.08)' }}>
                                    <ShadcnButton variant="secondary" onClick={closeEditMapModal} disabled={isUpdatingMap}>
                                        Cancelar
                                    </ShadcnButton>
                                    <ShadcnButton
                                        variant="default"
                                        onClick={handleUpdateMap}
                                        disabled={isUpdatingMap}
                                    >
                                        {isUpdatingMap ? <Loader2 className="spin" /> : <LucideCheck size={16} />}
                                        {isUpdatingMap ? 'Guardando...' : 'Guardar Cambios'}
                                    </ShadcnButton>
                                </div>
                            </ModalContent>
                        </PortalModalOverlay>
                    )
                }

                {
                    (isTransplantModalOpen || isClosingTransplant) && room && (
                        <TransplantModal
                            isOpen={isTransplantModalOpen}
                            onClose={closeTransplantModal}
                            currentRoom={room}
                            rooms={allRooms}
                            insumos={insumos}
                            cloneMaps={room.clone_maps || []}
                            onConfirm={handleTransplant}
                            initialMapId={activeMapId || undefined}
                            initialSelectedBatchIds={memoizedTransplantBatchIds}
                            isClosing={isClosingTransplant}
                        />
                    )
                }

                <ConfirmationModal
                    isOpen={isDeleteMapModalOpen || isClosingDeleteMap}
                    title="Eliminar Mapa"
                    message="¿Estás seguro de que deseas eliminar este mapa? Los lotes asociados perderán su posición (no se eliminarán los lotes, solo el mapa)."
                    onConfirm={confirmDeleteMap}
                    onCancel={closeDeleteMapModal}
                    confirmText={isDeletingMap ? "Eliminando..." : "Eliminar"}
                    cancelText="Cancelar"
                    isDestructive={true}
                    isClosing={isClosingDeleteMap}
                    isLoading={isDeletingMap}
                />

                <ConfirmationModal
                    isOpen={isClearMapConfirmOpen}
                    title="Limpiar Mapa"
                    message="¿Estás seguro de que deseas ELIMINAR TODOS los esquejes de este mapa? Esta acción no se puede deshacer."
                    onConfirm={handleClearMap}
                    onCancel={() => setIsClearMapConfirmOpen(false)}
                    confirmText="ELIMINAR TODO"
                    cancelText="Cancelar"
                    isDestructive={true}
                />


                {
                    assignModal.isOpen && assignModal.batch && (
                        <PortalModalOverlay isClosing={isAssignModalClosing}>
                            <ModalContent isClosing={isAssignModalClosing}>
                                <h3>Asignar Lote al Mapa</h3>
                                <p style={{ marginBottom: '1rem', color: '#718096' }}>
                                    Lote: <strong>{(assignModal.batch as any)._displayName || assignModal.batch.name}</strong><br />
                                    Disponibles: {(assignModal.batch as any)._totalQuantity || assignModal.batch.quantity}
                                </p>

                                <FormGroup>
                                    <label>¿Cuántas unidades deseas enviar al mapa?</label>
                                    <input
                                        type="number"
                                        min="1"
                                        max={(assignModal.batch as any)._totalQuantity || assignModal.batch.quantity}
                                        autoFocus
                                        value={assignModal.quantity}
                                        onChange={e => setAssignModal({ ...assignModal, quantity: e.target.value })}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') handleAutoAssign();
                                        }}
                                    />
                                    <small style={{ display: 'block', marginTop: '0.5rem', color: '#718096' }}>
                                        Se llenarán automáticamente los primeros espacios vacíos disponibles.
                                    </small>
                                </FormGroup>

                                <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem' }}>
                                    <CancelButton onClick={handleCloseAssignModal}>Cancelar</CancelButton>
                                    <ActionButton
                                        onClick={handleAutoAssign}
                                        $variant="success"
                                        disabled={
                                            Number(assignModal.quantity) < 1 ||
                                            Number(assignModal.quantity) > ((assignModal.batch as any)._totalQuantity || assignModal.batch.quantity) ||
                                            isAutoAssigning
                                        }
                                        style={{
                                            opacity: isAutoAssigning ? 0.7 : 1,
                                            cursor: isAutoAssigning ? 'not-allowed' : 'pointer',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem'
                                        }}
                                    >
                                        {isAutoAssigning ? (
                                            <>
                                                <Loader2 className="icon-spin" /> Asignando...
                                            </>
                                        ) : (
                                            'Asignar Automáticamente'
                                        )}
                                    </ActionButton>
                                </div>
                            </ModalContent>
                        </PortalModalOverlay>
                    )
                }



                {/* Plant Detail Modal (Unified for Map and Stock) */}
                {
                    (plantDetailModal.isOpen || isClosingPlantDetail) && plantDetailModal.batch && (
                        (room?.type === 'living_soil') ? (
                            <LivingSoilBatchModal
                                isOpen={plantDetailModal.isOpen}
                                onClose={closePlantDetailModal}
                                batch={plantDetailModal.batch}
                                onUpdateStage={handleUpdateStage}
                                onSaveNotes={handleSaveNotes}
                                onDeleteBatch={(b) => {
                                    closePlantDetailModal();
                                    setDeleteConfirm({ isOpen: true, batch: b });
                                }}
                            />
                        ) : (
                            <LocationActionModal
                                isOpen={plantDetailModal.isOpen}
                                isClosing={isClosingPlantDetail}
                                onClose={closePlantDetailModal}
                                batch={plantDetailModal.batch}
                                onRelocate={(b) => {
                                    closePlantDetailModal();
                                    setMovingBatch(b);
                                }}
                                onAssignToMap={(b) => {
                                    closePlantDetailModal();
                                    setAssignModal({ isOpen: true, batch: b, quantity: (b as any)._totalQuantity || b.quantity || 1 });
                                }}
                                onAddObservation={(b) => {
                                    closePlantDetailModal();
                                    setNotePromptModal({ isOpen: true, batch: b });
                                }}
                                onEdit={(b) => {
                                    closePlantDetailModal();
                                    handleOpenEditBatch(b);
                                }}
                                onDelete={(b) => {
                                    closePlantDetailModal();
                                    setDeleteConfirm({ isOpen: true, batch: b });
                                }}
                            />
                        )
                    )
                }



                {/* Move Confirmation Modal */}
                {
                    moveConfirm.isOpen && movingBatch && (
                        <PortalModalOverlay>
                            <ModalContent style={{ maxWidth: '400px', textAlign: 'center' }}>
                                <div style={{ color: '#3182ce', fontSize: '3rem', marginBottom: '1rem' }}>
                                    <Move />
                                </div>
                                <h3 style={{ fontSize: '1.25rem', color: '#2d3748', marginBottom: '0.5rem' }}>Confirmar Movimiento</h3>
                                <p style={{ color: '#718096', marginBottom: '1.5rem' }}>
                                    ¿Mover <strong>{movingBatch.tracking_code || movingBatch.name}</strong> a la posición <strong>{moveConfirm.position}</strong>?
                                </p>
                                <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
                                    <CancelButton onClick={() => setMoveConfirm({ isOpen: false, position: null })}>
                                        Cancelar
                                    </CancelButton>
                                    <ActionButton onClick={handleConfirmMove}>
                                        Confirmar Mover
                                    </ActionButton>
                                </div>
                            </ModalContent>
                        </PortalModalOverlay>
                    )
                }


                {/* Note Prompt Modal */}
                {notePromptModal.isOpen && notePromptModal.batch && (
                    <PromptModal
                        isOpen={notePromptModal.isOpen}
                        title={`Observación - ${notePromptModal.batch.tracking_code || notePromptModal.batch.name}`}
                        placeholder="Escribe una observación para esta planta..."
                        initialValue={notePromptModal.batch.notes || ''}
                        onClose={() => setNotePromptModal({ isOpen: false, batch: null })}
                        onConfirm={(notes) => {
                            if (notePromptModal.batch) {
                                handleSaveNotes(notePromptModal.batch, notes);
                            }
                        }}
                        confirmButtonColor="primary"
                    />
                )}

                {/* Custom Delete Confirmation Modal */}
                {
                    <DeleteReasonModal
                        isOpen={deleteConfirm.isOpen || isClosingDeleteConfirm}
                        itemName={deleteConfirm.batch?.tracking_code || deleteConfirm.batch?.name || ''}
                        onConfirm={handleConfirmDelete}
                        onCancel={closeDeleteConfirm}
                        isSubmitting={isDeletingBatch}
                    />
                }

                {/* Edit Batch Modal */}
                {
                    (isEditBatchModalOpen || isClosingEditBatch) && (
                        <PortalModalOverlay isClosing={isClosingEditBatch}>
                            <ModalContent isClosing={isClosingEditBatch} style={{ maxWidth: '480px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.75rem', borderBottom: '1px solid rgba(255, 255, 255, 0.08)', paddingBottom: '1rem' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.85rem' }}>
                                        <div style={{
                                            width: 42,
                                            height: 42,
                                            borderRadius: '10px',
                                            background: 'linear-gradient(135deg, rgba(56, 189, 248, 0.2) 0%, rgba(14, 165, 233, 0.1) 100%)',
                                            border: '1px solid rgba(56, 189, 248, 0.3)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: '#38bdf8',
                                            boxShadow: '0 0 15px rgba(56, 189, 248, 0.15)'
                                        }}>
                                            <LucideEdit3 size={20} />
                                        </div>
                                        <div>
                                            <h3 style={{ margin: 0, fontSize: '1.2rem', fontWeight: 700, color: '#f8fafc', letterSpacing: '-0.02em' }}>
                                                Editar Lote
                                            </h3>
                                            <span style={{ fontSize: '0.8rem', color: '#94a3b8' }}>
                                                Modifica las propiedades y anotaciones del lote
                                            </span>
                                        </div>
                                    </div>
                                    <button
                                        onClick={closeEditBatchModal}
                                        style={{
                                            background: 'rgba(255, 255, 255, 0.05)',
                                            border: '1px solid rgba(255, 255, 255, 0.1)',
                                            borderRadius: '0.5rem',
                                            width: 32,
                                            height: 32,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            cursor: 'pointer',
                                            color: '#94a3b8',
                                            transition: 'all 0.2s ease'
                                        }}
                                        onMouseOver={(e) => {
                                            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                                            e.currentTarget.style.color = '#ffffff';
                                        }}
                                        onMouseOut={(e) => {
                                            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                                            e.currentTarget.style.color = '#94a3b8';
                                        }}
                                    >
                                        <LucideX size={16} />
                                    </button>
                                </div>

                                <FormGroup style={{ marginBottom: '1.25rem' }}>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.5rem', fontWeight: 600, fontSize: '0.88rem', color: '#cbd5e1' }}>
                                        <LucideTag size={14} style={{ color: '#38bdf8' }} /> Nombre / Identificador
                                    </label>
                                    <input
                                        value={editBatchForm.name}
                                        onChange={e => setEditBatchForm({ ...editBatchForm, name: e.target.value })}
                                        style={{
                                            width: '100%',
                                            padding: '0.75rem 0.85rem',
                                            background: 'rgba(15, 23, 42, 0.6)',
                                            border: '1px solid rgba(255, 255, 255, 0.12)',
                                            color: '#f8fafc',
                                            borderRadius: '0.5rem',
                                            fontSize: '0.92rem',
                                            outline: 'none',
                                            transition: 'all 0.2s ease'
                                        }}
                                    />
                                </FormGroup>
                                <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.25rem' }}>
                                    <FormGroup style={{ flex: 1, marginBottom: 0 }}>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.5rem', fontWeight: 600, fontSize: '0.88rem', color: '#cbd5e1' }}>
                                            <LucidePackage size={14} style={{ color: '#34d399' }} /> Cantidad
                                        </label>
                                        <input
                                            type="number"
                                            min="1"
                                            value={editBatchForm.quantity}
                                            onChange={e => setEditBatchForm({ ...editBatchForm, quantity: e.target.value })}
                                            style={{
                                                width: '100%',
                                                padding: '0.75rem 0.85rem',
                                                background: 'rgba(15, 23, 42, 0.6)',
                                                border: '1px solid rgba(255, 255, 255, 0.12)',
                                                color: '#f8fafc',
                                                borderRadius: '0.5rem',
                                                fontSize: '0.92rem',
                                                outline: 'none',
                                                transition: 'all 0.2s ease'
                                            }}
                                        />
                                    </FormGroup>
                                    <FormGroup style={{ flex: 1, marginBottom: 0 }}>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.5rem', fontWeight: 600, fontSize: '0.88rem', color: '#cbd5e1' }}>
                                            <LucideCalendar size={14} style={{ color: '#a78bfa' }} /> Fecha de Inicio
                                        </label>
                                        <input
                                            type="date"
                                            value={editBatchForm.start_date}
                                            onChange={e => setEditBatchForm({ ...editBatchForm, start_date: e.target.value })}
                                            style={{
                                                width: '100%',
                                                padding: '0.75rem 0.85rem',
                                                borderRadius: '0.5rem',
                                                background: 'rgba(15, 23, 42, 0.6)',
                                                border: '1px solid rgba(255, 255, 255, 0.12)',
                                                color: '#f8fafc',
                                                fontSize: '0.92rem',
                                                outline: 'none',
                                                transition: 'all 0.2s ease'
                                            }}
                                        />
                                    </FormGroup>
                                </div>
                                <FormGroup style={{ marginBottom: '1.5rem' }}>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.5rem', fontWeight: 600, fontSize: '0.88rem', color: '#cbd5e1' }}>
                                        <LucideFileText size={14} style={{ color: '#94a3b8' }} /> Notas / Bitácora
                                    </label>
                                    <textarea
                                        value={editBatchForm.notes}
                                        onChange={e => setEditBatchForm({ ...editBatchForm, notes: e.target.value })}
                                        rows={3}
                                        style={{
                                            width: '100%',
                                            padding: '0.75rem 0.85rem',
                                            background: 'rgba(15, 23, 42, 0.6)',
                                            border: '1px solid rgba(255, 255, 255, 0.12)',
                                            color: '#f8fafc',
                                            borderRadius: '0.5rem',
                                            fontSize: '0.92rem',
                                            resize: 'vertical',
                                            outline: 'none',
                                            transition: 'all 0.2s ease'
                                        }}
                                    />
                                </FormGroup>
                                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '1.5rem', paddingTop: '1.25rem', borderTop: '1px solid rgba(255, 255, 255, 0.08)' }}>
                                    <ShadcnButton variant="secondary" onClick={closeEditBatchModal} disabled={isUpdatingBatch}>
                                        Cancelar
                                    </ShadcnButton>
                                    <ShadcnButton
                                        variant="default"
                                        onClick={handleUpdateBatch}
                                        disabled={isUpdatingBatch || !editBatchForm.quantity || Number(editBatchForm.quantity) <= 0}
                                    >
                                        {isUpdatingBatch ? <Loader2 className="spin" /> : <LucideCheck size={16} />}
                                        {isUpdatingBatch ? 'Guardando...' : 'Guardar Cambios'}
                                    </ShadcnButton>
                                </div>
                            </ModalContent>
                        </PortalModalOverlay>
                    )
                }

                {/* Delete Available Batch Modal */}
                {
                    availableBatchToDelete && (
                        <PortalModalOverlay>
                            <ModalContent style={{ maxWidth: '440px', textAlign: 'center' }}>
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1.25rem' }}>
                                    <div style={{
                                        width: '56px',
                                        height: '56px',
                                        borderRadius: '50%',
                                        background: 'rgba(239, 68, 68, 0.15)',
                                        border: '1px solid rgba(239, 68, 68, 0.3)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: '#f87171',
                                        boxShadow: '0 0 20px rgba(239, 68, 68, 0.2)'
                                    }}>
                                        <LucideTrash2 size={24} />
                                    </div>
                                    <div>
                                        <h3 style={{ fontSize: '1.25rem', fontWeight: 700, margin: '0 0 0.5rem 0', color: '#f8fafc' }}>
                                            ¿Eliminar Lote Disponible?
                                        </h3>
                                        <p style={{ color: '#94a3b8', fontSize: '0.92rem', margin: 0, lineHeight: 1.5 }}>
                                            Vas a eliminar permanentemente el lote: <strong style={{ color: '#f8fafc' }}>{availableBatchToDelete.tracking_code || availableBatchToDelete.name}</strong>.
                                        </p>
                                    </div>
                                    <div style={{
                                        background: 'rgba(245, 158, 11, 0.1)',
                                        border: '1px solid rgba(245, 158, 11, 0.25)',
                                        borderRadius: '0.75rem',
                                        padding: '0.85rem 1rem',
                                        width: '100%',
                                        textAlign: 'left',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.65rem'
                                    }}>
                                        <LucideAlertTriangle size={18} style={{ color: '#fbbf24', flexShrink: 0 }} />
                                        <p style={{ color: '#fbbf24', fontSize: '0.83rem', margin: 0, fontWeight: 500, lineHeight: 1.4 }}>
                                            Esta acción no generará registro de mortalidad en tus métricas generales.
                                        </p>
                                    </div>
                                </div>
                                <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.75rem', paddingTop: '1.25rem', borderTop: '1px solid rgba(255, 255, 255, 0.08)' }}>
                                    <ShadcnButton variant="secondary" onClick={() => setAvailableBatchToDelete(null)} disabled={isDeletingAvailable} style={{ flex: 1 }}>
                                        Cancelar
                                    </ShadcnButton>
                                    <ShadcnButton
                                        variant="destructive"
                                        onClick={handleConfirmDeleteAvailable}
                                        disabled={isDeletingAvailable}
                                        style={{ flex: 1 }}
                                    >
                                        {isDeletingAvailable ? <Loader2 className="spin" /> : <LucideTrash2 size={16} />}
                                        {isDeletingAvailable ? 'Eliminando...' : 'Sí, eliminar'}
                                    </ShadcnButton>
                                </div>
                            </ModalContent>
                        </PortalModalOverlay>
                    )
                }

                {/* Bulk Edit Modal */}
                {
                    isBulkEditModalOpen && (
                        <PortalModalOverlay>
                            <ModalContent style={{ maxWidth: '440px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.75rem', borderBottom: '1px solid rgba(255, 255, 255, 0.08)', paddingBottom: '1rem' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.85rem' }}>
                                        <div style={{
                                            width: 42,
                                            height: 42,
                                            borderRadius: '10px',
                                            background: 'linear-gradient(135deg, rgba(56, 189, 248, 0.2) 0%, rgba(14, 165, 233, 0.1) 100%)',
                                            border: '1px solid rgba(56, 189, 248, 0.3)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: '#38bdf8',
                                            boxShadow: '0 0 15px rgba(56, 189, 248, 0.15)'
                                        }}>
                                            <LucideLayers size={20} />
                                        </div>
                                        <div>
                                            <h3 style={{ margin: 0, fontSize: '1.2rem', fontWeight: 700, color: '#f8fafc', letterSpacing: '-0.02em' }}>
                                                Edición Masiva
                                            </h3>
                                            <span style={{ fontSize: '0.8rem', color: '#94a3b8' }}>
                                                Editando <strong style={{ color: '#38bdf8' }}>{selectedBatchIds.size}</strong> lotes en paralelo
                                            </span>
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => setIsBulkEditModalOpen(false)}
                                        style={{
                                            background: 'rgba(255, 255, 255, 0.05)',
                                            border: '1px solid rgba(255, 255, 255, 0.1)',
                                            borderRadius: '0.5rem',
                                            width: 32,
                                            height: 32,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            cursor: 'pointer',
                                            color: '#94a3b8',
                                            transition: 'all 0.2s ease'
                                        }}
                                        onMouseOver={(e) => {
                                            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                                            e.currentTarget.style.color = '#ffffff';
                                        }}
                                        onMouseOut={(e) => {
                                            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                                            e.currentTarget.style.color = '#94a3b8';
                                        }}
                                    >
                                        <LucideX size={16} />
                                    </button>
                                </div>

                                <div style={{ marginBottom: '1.25rem' }}>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.5rem', fontWeight: 600, fontSize: '0.88rem', color: '#cbd5e1' }}>
                                        <LucideLayers size={14} style={{ color: '#38bdf8' }} /> Nueva Etapa (Opcional)
                                    </label>
                                    <select
                                        value={bulkEditForm.stage}
                                        onChange={e => setBulkEditForm({ ...bulkEditForm, stage: e.target.value })}
                                        style={{
                                            width: '100%',
                                            padding: '0.75rem 0.85rem',
                                            borderRadius: '0.5rem',
                                            background: 'rgba(15, 23, 42, 0.6)',
                                            border: '1px solid rgba(255, 255, 255, 0.12)',
                                            color: '#f8fafc',
                                            fontSize: '0.92rem',
                                            outline: 'none'
                                        }}
                                    >
                                        <option value="" style={{ background: '#0f172a', color: '#94a3b8' }}>-- No cambiar etapa --</option>
                                        <option value="seedling" style={{ background: '#0f172a', color: '#f8fafc' }}>Plántula</option>
                                        <option value="vegetation" style={{ background: '#0f172a', color: '#f8fafc' }}>Vegetativo</option>
                                        <option value="flowering" style={{ background: '#0f172a', color: '#f8fafc' }}>Floración</option>
                                        <option value="drying" style={{ background: '#0f172a', color: '#f8fafc' }}>Secado</option>
                                        <option value="completed" style={{ background: '#0f172a', color: '#f8fafc' }}>Corte</option>
                                    </select>
                                </div>

                                <div style={{ marginBottom: '1.5rem' }}>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.5rem', fontWeight: 600, fontSize: '0.88rem', color: '#cbd5e1' }}>
                                        <LucideFileText size={14} style={{ color: '#94a3b8' }} /> Notas / Bitácora (Se sobreescribirá en todos)
                                    </label>
                                    <textarea
                                        value={bulkEditForm.notes}
                                        onChange={e => setBulkEditForm({ ...bulkEditForm, notes: e.target.value })}
                                        placeholder="Escribe una nota para aplicar a todos los lotes..."
                                        rows={3}
                                        style={{
                                            width: '100%',
                                            padding: '0.75rem 0.85rem',
                                            borderRadius: '0.5rem',
                                            background: 'rgba(15, 23, 42, 0.6)',
                                            border: '1px solid rgba(255, 255, 255, 0.12)',
                                            color: '#f8fafc',
                                            fontSize: '0.92rem',
                                            resize: 'vertical',
                                            outline: 'none'
                                        }}
                                    />
                                </div>

                                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '1.5rem', paddingTop: '1.25rem', borderTop: '1px solid rgba(255, 255, 255, 0.08)' }}>
                                    <ShadcnButton variant="secondary" onClick={() => setIsBulkEditModalOpen(false)}>
                                        Cancelar
                                    </ShadcnButton>
                                    <ShadcnButton variant="default" onClick={handleBulkEditSubmit}>
                                        <LucideCheck size={16} /> Aplicar Cambios
                                    </ShadcnButton>
                                </div>
                            </ModalContent>
                        </PortalModalOverlay>
                    )
                }

                {/* Change Phase Modal (New) */}
                {
                    isChangePhaseModalOpen && (
                        <PortalModalOverlay>
                            <ModalContent style={{ width: '500px', maxWidth: '95%' }}>
                                {/* Header */}
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem' }}>
                                    <div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                            <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#9F7AEA' }}></div> {/* Generic Dot */}
                                            <h2 style={{ fontSize: '1.25rem', fontWeight: 'bold', margin: 0, color: '#2d3748' }}>
                                                {selectedBatchIds.size === 1
                                                    ? (room?.batches?.find(b => b.id === Array.from(selectedBatchIds)[0])?.name || 'Lote')
                                                    : `${selectedBatchIds.size} Lotes Seleccionados`
                                                }
                                            </h2>
                                        </div>
                                        {selectedBatchIds.size === 1 && (() => {
                                            const b = room?.batches?.find(bat => bat.id === Array.from(selectedBatchIds)[0]);
                                            // Calculate Position Label if possible, or just ignore for now
                                            // const pos = ... 
                                            return (
                                                <div style={{ marginTop: '0.25rem', fontSize: '0.9rem', color: '#718096', display: 'flex', gap: '1rem' }}>
                                                    {/* <span>Posición: <strong>{b?.position}</strong></span> */}
                                                    <span>Código: <strong>{b?.tracking_code || '-'}</strong></span>
                                                </div>
                                            )
                                        })()}
                                    </div>
                                    <button onClick={() => setIsChangePhaseModalOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.25rem', color: '#a0aec0' }}>✕</button>
                                </div>

                                {/* Stage Selector */}
                                <div style={{ marginBottom: '1.5rem' }}>
                                    <label style={{ display: 'block', marginBottom: '0.75rem', fontWeight: 600, color: '#4a5568' }}>Etapa Actual</label>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '0.75rem' }}>
                                        {[
                                            { id: 'seedling', label: 'Plántula', icon: <Sprout /> },
                                            { id: 'vegetation', label: 'Vege', icon: <LucideLeaf /> }, // Leaf/Cannabis
                                            { id: 'flowering', label: 'Flora', icon: <LucideFlower2 /> }, // Flower
                                            { id: 'completed', label: 'Corte', icon: <LucideCheck /> }
                                        ].map(option => (
                                            <StageButton
                                                key={option.id}
                                                onClick={() => setChangePhaseForm({ ...changePhaseForm, stage: option.id as BatchStage })}
                                                isActive={changePhaseForm.stage === option.id}
                                            >
                                                <div style={{ fontSize: '1.25rem' }}>{option.icon}</div>
                                                {option.label}
                                            </StageButton>
                                        ))}
                                    </div>
                                </div>

                                {/* Notes */}
                                <div style={{ marginBottom: '2rem' }}>
                                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: '#4a5568' }}>Notas / Bitácora</label>
                                    <textarea
                                        value={changePhaseForm.notes}
                                        onChange={e => setChangePhaseForm({ ...changePhaseForm, notes: e.target.value })}
                                        placeholder={selectedBatchIds.size > 1 ? "Escribe una nota para aplicar a todos..." : "Registrar eventos, altura, poda..."}
                                        style={{ width: '100%', minHeight: '100px', padding: '0.75rem', borderRadius: '0.5rem', border: '1px solid #e2e8f0', resize: 'vertical' }}
                                    />
                                    <div style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: '#a0aec0', fontStyle: 'italic' }}>
                                        (Elimina esta nota por completo para desactivar la alerta de observación)
                                    </div>
                                </div>

                                {/* Footer */}
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '1rem', borderTop: '1px solid #edf2f7' }}>
                                    {/* Delete Button (Left aligned) */}
                                    {/* Delete Button (Left aligned) */}
                                    <ModalActionButton
                                        variant="danger"
                                        onClick={() => {
                                            setIsChangePhaseModalOpen(false);
                                            // Trigger existing delete flow
                                            if (selectedBatchIds.size === 1) {
                                                const b = room?.batches?.find(bat => bat.id === Array.from(selectedBatchIds)[0]);
                                                if (b) {
                                                    setDeleteConfirm({ isOpen: true, batch: b });
                                                }
                                            } else {
                                                handleBulkDeleteFirstStep();
                                            }
                                        }}
                                    >
                                        <LucideTrash2 size={12} /> Eliminar
                                    </ModalActionButton>

                                    {/* Action Buttons (Right aligned) */}
                                    <div style={{ display: 'flex', gap: '1rem' }}>
                                        <ModalActionButton
                                            variant="primary"
                                            onClick={handleChangePhaseSubmit}
                                            disabled={isUpdatingMap}
                                        >
                                            {isUpdatingMap ? (
                                                <>
                                                    <ButtonSpinner /> Guardando...
                                                </>
                                            ) : 'Guardar y Cerrar'}
                                        </ModalActionButton>
                                    </div>
                                </div>
                            </ModalContent>
                        </PortalModalOverlay>
                    )
                }

                {/* History Modal */}
                {
                    (isHistoryModalOpen || isClosingHistory) && (
                        <PortalModalOverlay isClosing={isClosingHistory}>
                            <ModalContent style={{ maxWidth: '980px', width: '92%' }} isClosing={isClosingHistory}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.75rem', borderBottom: '1px solid rgba(255, 255, 255, 0.08)', paddingBottom: '1rem' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.85rem' }}>
                                        <div style={{
                                            width: 42,
                                            height: 42,
                                            borderRadius: '10px',
                                            background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.2) 0%, rgba(5, 150, 105, 0.1) 100%)',
                                            border: '1px solid rgba(16, 185, 129, 0.3)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: '#34d399',
                                            boxShadow: '0 0 15px rgba(16, 185, 129, 0.15)'
                                        }}>
                                            <LucideHistory size={20} />
                                        </div>
                                        <div>
                                            <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 700, color: '#f8fafc', letterSpacing: '-0.02em' }}>
                                                Historial de Movimientos
                                            </h3>
                                            <span style={{ fontSize: '0.8rem', color: '#94a3b8' }}>
                                                Auditoría cronológica y trazabilidad de eventos en esta sala
                                            </span>
                                        </div>
                                    </div>
                                    <button
                                        onClick={closeHistoryModal}
                                        style={{
                                            background: 'rgba(255, 255, 255, 0.05)',
                                            border: '1px solid rgba(255, 255, 255, 0.1)',
                                            borderRadius: '0.5rem',
                                            width: 32,
                                            height: 32,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            cursor: 'pointer',
                                            color: '#94a3b8',
                                            transition: 'all 0.2s ease'
                                        }}
                                        onMouseOver={(e) => {
                                            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                                            e.currentTarget.style.color = '#ffffff';
                                        }}
                                        onMouseOut={(e) => {
                                            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                                            e.currentTarget.style.color = '#94a3b8';
                                        }}
                                    >
                                        <LucideX size={16} />
                                    </button>
                                </div>

                                <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                                    {historyLoading ? (
                                        <div style={{ textAlign: 'center', padding: '3rem', color: '#94a3b8', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.75rem' }}>
                                            <Loader2 className="spin" style={{ fontSize: '1.5rem', color: '#34d399' }} />
                                            <p style={{ margin: 0, fontSize: '0.9rem' }}>Cargando registros históricos...</p>
                                        </div>
                                    ) : roomHistory.length === 0 ? (
                                        <div style={{ textAlign: 'center', padding: '3rem 2rem', color: '#94a3b8', background: 'rgba(255, 255, 255, 0.02)', borderRadius: '0.75rem', border: '1px dashed rgba(255, 255, 255, 0.12)' }}>
                                            <LucideHistory size={32} style={{ color: '#64748b', marginBottom: '0.75rem' }} />
                                            <p style={{ margin: 0, fontSize: '0.95rem', fontWeight: 500, color: '#cbd5e1' }}>No hay movimientos registrados recientemente</p>
                                            <span style={{ fontSize: '0.8rem', color: '#64748b' }}>Los cambios de etapa, trasplantes y notas aparecerán aquí</span>
                                        </div>
                                    ) : (
                                        <div style={{ width: '100%', overflowX: 'auto', WebkitOverflowScrolling: 'touch', border: '1px solid rgba(255, 255, 255, 0.08)', borderRadius: '0.75rem' }}>
                                            <table style={{ minWidth: '600px', width: '100%', borderCollapse: 'collapse', color: '#f8fafc', background: 'rgba(15, 23, 42, 0.4)' }}>
                                                <thead>
                                                    <tr style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)', background: 'rgba(255, 255, 255, 0.03)', textAlign: 'left' }}>
                                                        <th style={{ padding: '0.85rem 1rem', color: '#94a3b8', fontSize: '0.8rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>Fecha</th>
                                                        <th style={{ padding: '0.85rem 1rem', color: '#94a3b8', fontSize: '0.8rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>Usuario</th>
                                                        <th style={{ padding: '0.85rem 1rem', color: '#94a3b8', fontSize: '0.8rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>Código Lote</th>
                                                        <th style={{ padding: '0.85rem 1rem', color: '#94a3b8', fontSize: '0.8rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>Acción / Notas</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {roomHistory.map((move: any) => {
                                                        // Date Formatting
                                                        const dateObj = move.moved_at ? new Date(move.moved_at) : null;
                                                        const dateStr = dateObj ? dateObj.toLocaleDateString('es-AR', { weekday: 'short', year: 'numeric', month: '2-digit', day: '2-digit' }) : '-';
                                                        const timeStr = dateObj ? dateObj.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit', hour12: true }).toLowerCase() : '';
                                                        const fullDate = dateObj ? `${dateStr.charAt(0).toUpperCase() + dateStr.slice(1)} • ${timeStr}` : '-';

                                                        // Color Coding Logic
                                                        let badgeBg = 'rgba(255, 255, 255, 0.05)';
                                                        let badgeColor = '#94a3b8';
                                                        let badgeBorder = 'rgba(255, 255, 255, 0.1)';

                                                        const noteLower = (move.notes || '').toLowerCase();

                                                        if (noteLower.includes('observación') || noteLower.includes('alerta')) {
                                                            badgeBg = 'rgba(253, 224, 71, 0.12)';
                                                            badgeColor = '#fde047';
                                                            badgeBorder = 'rgba(253, 224, 71, 0.25)';
                                                        } else if (noteLower.includes('etapa') || noteLower.includes('transplante') || noteLower.includes('siembra')) {
                                                            badgeBg = 'rgba(74, 222, 128, 0.12)';
                                                            badgeColor = '#4ade80';
                                                            badgeBorder = 'rgba(74, 222, 128, 0.25)';
                                                        } else if (noteLower.includes('eliminado') || noteLower.includes('baja') || noteLower.includes('descartad')) {
                                                            badgeBg = 'rgba(248, 113, 113, 0.12)';
                                                            badgeColor = '#f87171';
                                                            badgeBorder = 'rgba(248, 113, 113, 0.25)';
                                                        } else if (noteLower.includes('nota') || noteLower.includes('edición')) {
                                                            badgeBg = 'rgba(56, 189, 248, 0.12)';
                                                            badgeColor = '#38bdf8';
                                                            badgeBorder = 'rgba(56, 189, 248, 0.25)';
                                                        }

                                                        return (
                                                            <tr key={move.id} style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.05)', transition: 'background 0.15s ease' }}>
                                                                <td style={{ padding: '0.85rem 1rem', color: '#94a3b8', fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
                                                                    {fullDate}
                                                                </td>
                                                                <td style={{ padding: '0.85rem 1rem', color: '#cbd5e1', fontSize: '0.88rem', whiteSpace: 'nowrap' }}>
                                                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                                                                        <LucideUser size={13} style={{ color: '#64748b' }} />
                                                                        {move.user?.full_name || move.user?.email || 'Sistema'}
                                                                    </span>
                                                                </td>
                                                                <td style={{ padding: '0.85rem 1rem', fontWeight: 600, color: '#f8fafc', fontSize: '0.88rem', whiteSpace: 'nowrap' }}>
                                                                    <span style={{
                                                                        padding: '0.2rem 0.5rem',
                                                                        borderRadius: '0.375rem',
                                                                        background: 'rgba(255, 255, 255, 0.05)',
                                                                        border: '1px solid rgba(255, 255, 255, 0.08)',
                                                                        fontFamily: 'monospace',
                                                                        fontSize: '0.82rem'
                                                                    }}>
                                                                        {move.batch?.tracking_code || move.batch?.name || 'Desconocido'}
                                                                    </span>
                                                                </td>
                                                                <td style={{ padding: '0.85rem 1rem', fontSize: '0.88rem' }}>
                                                                    <span style={{
                                                                        display: 'inline-block',
                                                                        padding: '0.25rem 0.65rem',
                                                                        borderRadius: '9999px',
                                                                        background: badgeBg,
                                                                        color: badgeColor,
                                                                        border: `1px solid ${badgeBorder}`,
                                                                        fontWeight: 500,
                                                                        fontSize: '0.82rem'
                                                                    }}>
                                                                        {move.notes || '-'}
                                                                    </span>
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            </ModalContent>
                        </PortalModalOverlay>
                    )
                }

                <ConfirmationModal
                    isOpen={isCreateMapConfirmOpen}
                    title="¿Crear nueva Mesa?"
                    message={`¿Estás seguro de crear una nueva mesa para "${pendingMapBatch?.name}"(${pendingMapBatch?.quantity} unidades) ?\n\nSe distribuirán las plantas automáticamente en una grilla.`}
                    onConfirm={handleConfirmCreateMap}
                    onCancel={() => {
                        setIsCreateMapConfirmOpen(false);
                        setPendingMapBatch(null);
                    }}
                    confirmText="Crear Mesa"
                    cancelText="Cancelar"
                />

                {/* Harvest Modal */}
                {
                    (isHarvestModalOpen || isClosingHarvestModal) && room && (
                        <HarvestModal
                            isOpen={isHarvestModalOpen}
                            isClosing={isClosingHarvestModal}
                            onClose={() => {
                                closeHarvestModal();
                                setTimeout(() => setHarvestTargetMapId(null), 300); // Clear target after animation
                            }}
                            batches={harvestTargetMapId
                                ? (room.batches?.filter(b => b.clone_map_id === harvestTargetMapId && (room.type !== 'living_soil' || b.stage === 'completed')) || [])
                                : (room.batches?.filter(b => room.type !== 'living_soil' || b.stage === 'completed') || [])}
                            rooms={allRooms.filter(r => r.spot_id === room.spot_id)}
                            onConfirm={handleHarvestConfirm}
                            overrideGroupName={harvestTargetMapId ? (room.clone_maps?.find(m => m.id === harvestTargetMapId)?.name) : undefined}
                            maps={room.clone_maps}
                        />
                    )
                }
                {/* Create Map From Group Confirmation Modal */}
                {
                    (isCreateMapFromGroupConfirmOpen || isClosingCreateMapFromGroup) && pendingMapGroup && (
                        <PortalModalOverlay isClosing={isClosingCreateMapFromGroup}>
                            <ModalContent onClick={e => e.stopPropagation()} isClosing={isClosingCreateMapFromGroup}>
                                <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#f8fafc', margin: 0, paddingBottom: '1rem' }}>
                                    <LucideMap /> Crear Mapa desde Grupo
                                </h2>
                                {(() => {
                                    // PRIORITIZE VIRTUAL GROUP NAME
                                    if (pendingMapGroup.root._virtualGroupName) {
                                        return (
                                            <p style={{ color: '#cbd5e1', marginBottom: '1rem', fontSize: '1.05rem', lineHeight: '1.5' }}>
                                                Vas a crear un nuevo mapa para el lote <strong style={{ color: '#f8fafc' }}>{pendingMapGroup.root._virtualGroupName}</strong> y sus variantes.
                                            </p>
                                        );
                                    }

                                    const geneticName = pendingMapGroup.root.genetic?.name || pendingMapGroup.root.name || 'Desconocida';
                                    const prefix = geneticName.substring(0, 6).toUpperCase();

                                    const d = new Date(pendingMapGroup.root.start_date || pendingMapGroup.root.created_at);
                                    d.setMinutes(d.getMinutes() + d.getTimezoneOffset());
                                    const displayDate = `${d.getDate().toString().padStart(2, '0')}/${(d.getMonth() + 1).toString().padStart(2, '0')}/${d.getFullYear()}`;

                                    const displayName = `Lote ${prefix} ${displayDate}`;

                                    return (
                                        <p style={{ color: '#cbd5e1', marginBottom: '1rem', fontSize: '1.05rem', lineHeight: '1.5' }}>
                                            Vas a crear un nuevo mapa para el lote <strong style={{ color: '#f8fafc' }}>{displayName}</strong> y sus variantes.
                                        </p>
                                    );
                                })()}

                                <p style={{ color: '#94a3b8', marginBottom: '1.5rem', fontSize: '0.95rem' }}>
                                    Total de plantas: <strong style={{ color: '#4ade80' }}>{pendingMapGroup.root.quantity + pendingMapGroup.children.reduce((acc: number, c: any) => acc + c.quantity, 0)}</strong>
                                </p>

                                <div style={{ padding: '1rem', background: 'rgba(56, 189, 248, 0.1)', border: '1px solid rgba(56, 189, 248, 0.2)', borderRadius: '0.5rem', marginBottom: '2rem' }}>
                                    <p style={{ color: '#bae6fd', margin: 0, fontSize: '0.95rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                        <LucideCheck style={{ color: '#38bdf8' }} /> Se creará una mesa automática y se distribuirán todas las plantas individualmente.
                                    </p>
                                </div>

                                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
                                    <CancelButton
                                        onClick={closeCreateMapFromGroupModal}
                                        style={{ flex: 'none', padding: '0.75rem 1.5rem' }}
                                    >
                                        Cancelar
                                    </CancelButton>
                                    <ActionButton
                                        onClick={handleConfirmCreateMapFromGroup}
                                        disabled={isCreatingMapFromGroup}
                                        $variant="success"
                                    >
                                        {isCreatingMapFromGroup ? (
                                            <>
                                                <Loader2 className="spin" /> Creando...
                                            </>
                                        ) : (
                                            <>
                                                <LucidePlus /> Crear Mapa
                                            </>
                                        )}
                                    </ActionButton>
                                </div>
                            </ModalContent>
                        </PortalModalOverlay>
                    )
                }

                <ToastModal
                    isOpen={toastModal.isOpen}
                    message={toastModal.message}
                    type={toastModal.type}
                    onClose={() => setToastModal({ ...toastModal, isOpen: false })}
                />
                <GroupDetailModal
                    isOpen={isGroupDetailModalOpen}
                    onClose={() => setIsGroupDetailModalOpen(false)}
                    groupName={selectedGroupName || ''}
                    batches={room?.batches?.filter(b => (b.name || b.genetic?.name || 'Lote sin nombre') === selectedGroupName) || []}
                    onDeleteBatch={handleDeleteBatchFromGroup}
                />
                {
                    isMetricsModalOpen && (
                        <PortalModalOverlay>
                            <ModalContent style={{ maxWidth: '800px', width: '90%' }} onClick={e => e.stopPropagation()}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                                    <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#2d3748' }}>Métricas de Enraizamiento</h2>
                                    <button onClick={() => setIsMetricsModalOpen(false)} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#a0aec0' }}>&times;</button>
                                </div>

                                {loadingMetrics ? (
                                    <div style={{ padding: '2rem', textAlign: 'center' }}>
                                        <LoadingSpinner />
                                        <p style={{ marginTop: '1rem', color: '#718096' }}>Calculando métricas...</p>
                                    </div>
                                ) : (
                                    <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                            <thead>
                                                <tr style={{ background: '#f7fafc', borderBottom: '2px solid #e2e8f0' }}>
                                                    <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.85rem', color: '#4a5568' }}>Genética</th>
                                                    <th style={{ padding: '0.75rem', textAlign: 'center', fontSize: '0.85rem', color: '#4a5568' }}>Activos</th>
                                                    <th style={{ padding: '0.75rem', textAlign: 'center', fontSize: '0.85rem', color: '#38a169' }}>Transplantados</th>
                                                    <th style={{ padding: '0.75rem', textAlign: 'center', fontSize: '0.85rem', color: '#e53e3e' }}>Bajas/Fallas</th>
                                                    <th style={{ padding: '0.75rem', textAlign: 'center', fontSize: '0.85rem', color: '#2d3748' }}>Total Proc.</th>
                                                    <th style={{ padding: '0.75rem', textAlign: 'center', fontSize: '0.85rem', color: '#3182ce' }}>% Éxito</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {metricsData.map((stat, i) => (
                                                    <tr key={i} style={{ borderBottom: '1px solid #edf2f7' }}>
                                                        <td style={{ padding: '0.75rem', fontWeight: 600 }}>{stat.name}</td>
                                                        <td style={{ padding: '0.75rem', textAlign: 'center' }}>{stat.active}</td>
                                                        <td style={{ padding: '0.75rem', textAlign: 'center', color: '#38a169', fontWeight: 500 }}>{stat.success}</td>
                                                        <td style={{ padding: '0.75rem', textAlign: 'center', color: '#e53e3e', fontWeight: 500 }}>{stat.failed}</td>
                                                        <td style={{ padding: '0.75rem', textAlign: 'center' }}>{stat.total}</td>
                                                        <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                                                                <div style={{
                                                                    width: '60px', height: '6px', background: '#e2e8f0', borderRadius: '3px', overflow: 'hidden'
                                                                }}>
                                                                    <div style={{
                                                                        width: `${stat.successRate}% `,
                                                                        height: '100%',
                                                                        background: parseFloat(stat.successRate) > 80 ? '#48bb78' : parseFloat(stat.successRate) > 50 ? '#ecc94b' : '#f56565'
                                                                    }} />
                                                                </div>
                                                                <span style={{ fontWeight: 'bold', minWidth: '40px' }}>{stat.successRate}%</span>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                                {metricsData.length === 0 && (
                                                    <tr>
                                                        <td colSpan={6} style={{ padding: '2rem', textAlign: 'center', color: '#a0aec0' }}>
                                                            No hay suficientes datos para mostrar métricas.
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                        <div style={{ marginTop: '1rem', padding: '1rem', background: '#ebf8ff', borderRadius: '0.5rem', fontSize: '0.85rem', color: '#2c5282' }}>
                                            <strong>Nota:</strong> El % de Éxito se calcula sobre los esquejes que han finalizado su ciclo en esta sala (Transplantados vs Bajas). Los esquejes activos no afectan el porcentaje.
                                        </div>
                                    </div>
                                )}
                            </ModalContent>
                        </PortalModalOverlay >
                    )
                }
                {/* EDIT ROOM MODAL */}
                {/* EDIT ROOM MODAL */}
                {
                    (isEditModalOpen || isClosingEditRoom) && (
                        <PortalModalOverlay isClosing={isClosingEditRoom}>
                            <ModalContent isClosing={isClosingEditRoom} style={{ maxWidth: '480px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.75rem', borderBottom: '1px solid rgba(255, 255, 255, 0.08)', paddingBottom: '1rem' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.85rem' }}>
                                        <div style={{
                                            width: 42,
                                            height: 42,
                                            borderRadius: '10px',
                                            background: 'linear-gradient(135deg, rgba(56, 189, 248, 0.2) 0%, rgba(14, 165, 233, 0.1) 100%)',
                                            border: '1px solid rgba(56, 189, 248, 0.3)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: '#38bdf8',
                                            boxShadow: '0 0 15px rgba(56, 189, 248, 0.15)'
                                        }}>
                                            <LucideSettings size={20} />
                                        </div>
                                        <div>
                                            <h3 style={{ margin: 0, fontSize: '1.2rem', fontWeight: 700, color: '#f8fafc', letterSpacing: '-0.02em' }}>
                                                Editar Sala
                                            </h3>
                                            <span style={{ fontSize: '0.8rem', color: '#94a3b8' }}>
                                                Ajusta el nombre, etapa de cultivo y fecha
                                            </span>
                                        </div>
                                    </div>
                                    <button
                                        onClick={closeEditRoomModal}
                                        style={{
                                            background: 'rgba(255, 255, 255, 0.05)',
                                            border: '1px solid rgba(255, 255, 255, 0.1)',
                                            borderRadius: '0.5rem',
                                            width: 32,
                                            height: 32,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            cursor: 'pointer',
                                            color: '#94a3b8',
                                            transition: 'all 0.2s ease'
                                        }}
                                        onMouseOver={(e) => {
                                            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                                            e.currentTarget.style.color = '#ffffff';
                                        }}
                                        onMouseOut={(e) => {
                                            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                                            e.currentTarget.style.color = '#94a3b8';
                                        }}
                                    >
                                        <LucideX size={16} />
                                    </button>
                                </div>

                                <div style={{ marginBottom: '1.25rem' }}>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.5rem', fontWeight: 600, fontSize: '0.88rem', color: '#cbd5e1' }}>
                                        <LucideTag size={14} style={{ color: '#38bdf8' }} /> Nombre de la Sala
                                    </label>
                                    <input
                                        type="text"
                                        value={editRoomName}
                                        onChange={e => setEditRoomName(e.target.value)}
                                        style={{
                                            width: '100%',
                                            padding: '0.75rem 0.85rem',
                                            background: 'rgba(15, 23, 42, 0.6)',
                                            border: '1px solid rgba(255, 255, 255, 0.12)',
                                            color: '#f8fafc',
                                            borderRadius: '0.5rem',
                                            fontSize: '0.92rem',
                                            outline: 'none',
                                            transition: 'all 0.2s ease'
                                        }}
                                    />
                                </div>

                                <div style={{ marginBottom: '1.25rem' }}>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.65rem', fontWeight: 600, fontSize: '0.88rem', color: '#cbd5e1' }}>
                                        <LucideLayers size={14} style={{ color: '#34d399' }} /> Etapa de Cultivo
                                    </label>
                                    <div style={{
                                        display: 'grid',
                                        gridTemplateColumns: ['clones', 'vegetation', 'flowering'].includes(room?.type || '') ? '1fr 1fr' : 'repeat(4, minmax(0, 1fr))',
                                        gap: '0.5rem'
                                    }}>
                                        {[
                                            { id: 'vegetation', label: 'Vege', icon: <LucideLeaf size={18} /> },
                                            { id: 'flowering', label: 'Flora', icon: <LucideFlower2 size={18} /> },
                                            { id: 'drying', label: 'Secado', icon: <LucideWarehouse size={18} /> },
                                            { id: 'living_soil', label: 'Agro', icon: <LucideSprout size={18} /> }
                                        ].filter(option => {
                                            if (['clones', 'vegetation', 'flowering'].includes(room?.type || '')) {
                                                return ['vegetation', 'flowering'].includes(option.id);
                                            }
                                            return true;
                                        }).map(option => (
                                            <StageButton
                                                key={option.id}
                                                onClick={() => setEditRoomType(option.id as any)}
                                                isActive={editRoomType === option.id}
                                                style={{ padding: '0.65rem 0.5rem', minHeight: '72px' }}
                                            >
                                                <div style={{ marginBottom: '0.25rem' }}>{option.icon}</div>
                                                <span style={{ fontSize: '0.8rem', textAlign: 'center', lineHeight: '1.1' }}>{option.label}</span>
                                            </StageButton>
                                        ))}
                                    </div>
                                </div>

                                <div style={{ marginBottom: '1.75rem' }}>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.5rem', fontWeight: 600, fontSize: '0.88rem', color: '#cbd5e1' }}>
                                        <LucideCalendar size={14} style={{ color: '#a78bfa' }} /> Fecha de Inicio
                                    </label>
                                    <CustomDatePicker
                                        selected={editRoomStartDate ? new Date(editRoomStartDate.replace(/-/g, '/') + ' 12:00:00') : new Date()}
                                        onChange={(date) => {
                                            if (date) {
                                                setEditRoomStartDate(format(date, 'yyyy-MM-dd'));
                                            }
                                        }}
                                    />
                                </div>

                                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '1.5rem', paddingTop: '1.25rem', borderTop: '1px solid rgba(255, 255, 255, 0.08)' }}>
                                    <ShadcnButton
                                        variant="secondary"
                                        onClick={closeEditRoomModal}
                                    >
                                        Cancelar
                                    </ShadcnButton>
                                    <ShadcnButton
                                        variant="default"
                                        onClick={handleUpdateRoom}
                                        disabled={isUpdatingRoom}
                                    >
                                        {isUpdatingRoom ? <Loader2 className="spin" /> : <LucideCheck size={16} />}
                                        {isUpdatingRoom ? 'Guardando...' : 'Guardar Cambios'}
                                    </ShadcnButton>
                                </div>
                            </ModalContent>
                        </PortalModalOverlay>
                    )
                }

                {/* FINALIZE MODAL */}
                {
                    (finalizeBatch || finalizeBatches || isClosingFinalize) && (
                        <PortalModalOverlay isClosing={isClosingFinalize}>
                            <ModalContent isClosing={isClosingFinalize} style={{ maxWidth: '400px' }}>
                                <h2 style={{ marginBottom: '1rem', color: '#f8fafc' }}>
                                    {finalizeBatches ? 'Finalizar Grupo Completo' : 'Finalizar Secado'}
                                </h2>
                                <p style={{ marginBottom: '1rem', color: '#cbd5e1' }}>
                                    {finalizeBatches ? (
                                        <>
                                            Estás enviando <strong>1 lote</strong> de {finalizeBatches.reduce((acc, b) => acc + b.quantity, 0)} plantas a Stock.
                                            <br /><span style={{ fontSize: '0.85rem', color: '#94a3b8' }}>El peso total se distribuirá proporcionalmente.</span>
                                        </>
                                    ) : (
                                        <>Estás enviando <strong>{finalizeBatch?.name}</strong> ({finalizeBatch?.quantity} plantas) a Stock.</>
                                    )}
                                </p>

                                <div style={{ marginBottom: '1rem' }}>
                                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: '#f8fafc' }}>
                                        {finalizeBatches ? 'Peso Seco TOTAL del Grupo (g)' : 'Peso Seco Final (g)'}
                                    </label>
                                    <input
                                        type="number"
                                        autoFocus
                                        value={finalWeight}
                                        onChange={e => setFinalWeight(e.target.value)}
                                        style={{ width: '100%', padding: '0.75rem', background: 'rgba(15, 23, 42, 0.6)', border: '1px solid rgba(255, 255, 255, 0.1)', color: '#f8fafc', borderRadius: '0.5rem' }}
                                        placeholder="0.00"
                                    />
                                </div>

                                <div style={{ marginBottom: '1.5rem' }}>
                                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: '#f8fafc' }}>Notas</label>
                                    <textarea
                                        value={finalNotes}
                                        onChange={e => setFinalNotes(e.target.value)}
                                        style={{ width: '100%', padding: '0.75rem', background: 'rgba(15, 23, 42, 0.6)', border: '1px solid rgba(255, 255, 255, 0.1)', color: '#f8fafc', borderRadius: '0.5rem' }}
                                        rows={3}
                                        placeholder="Calidad, observaciones..."
                                    />
                                </div>

                                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
                                    <CancelButton
                                        onClick={() => {
                                            setIsClosingFinalize(true);
                                            setTimeout(() => {
                                                setFinalizeBatch(null);
                                                setFinalizeBatches(null);
                                                setIsClosingFinalize(false);
                                            }, 200);
                                        }}
                                    >
                                        Cancelar
                                    </CancelButton>
                                    <ActionButton
                                        onClick={async () => {
                                            await handleConfirmFinalize();
                                        }}
                                        disabled={isFinalizing}
                                        $variant="success"
                                        style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                                    >
                                        {isFinalizing && <Loader2 className="spin" />}
                                        {isFinalizing ? 'Enviando...' : 'Confirmar y Enviar'}
                                    </ActionButton>
                                </div>
                            </ModalContent>
                        </PortalModalOverlay>
                    )
                }
                < ConfirmationModal
                    isOpen={isDistributionConfirmOpen}
                    isClosing={isClosingDistribution}
                    isLoading={loading}
                    title="Distribuir Lote Automáticamente"
                    message={distributionData ? `¿Deseas distribuir ${distributionData.batches.length} lotes (${distributionData.batches.reduce((a, b) => a + b.quantity, 0)} plantas) en el mapa comenzando desde la celda ${distributionData.position}?` : ''}
                    onConfirm={handleConfirmDistribution}
                    onCancel={() => {
                        setIsClosingDistribution(true);
                        setTimeout(() => {
                            setIsDistributionConfirmOpen(false);
                            setIsClosingDistribution(false);
                            setDistributionData(null);
                        }, 200);
                    }}
                    confirmText="Distribuir"
                    cancelText="Cancelar"
                    variant="success"
                />

                {/* Single Distribution Confirmation Modal */}
                <ConfirmationModal
                    isOpen={isSingleDistributeConfirmOpen}
                    isClosing={isClosingSingleDistribution}
                    isLoading={loading}
                    title="Distribuir Plantas"
                    message={singleDistributionData ? `¿Distribuir ${singleDistributionData.quantity} plantas individualmente en el mapa a partir de ${singleDistributionData.position}?` : ''}
                    onConfirm={async () => {
                        if (!singleDistributionData) return;
                        setLoading(true);
                        // Close happens after success/fail in logic, but we can trigger loading here

                        const { batchId, position, mapId } = singleDistributionData;
                        // ... existing logic ...
                        // Calculate start row/col from position (e.g. "A1")
                        const rowLetter = position.charAt(0);
                        const colStr = position.slice(1);
                        const startRow = rowLetter.charCodeAt(0) - 64; // A=1
                        const startCol = parseInt(colStr, 10);

                        const map = cloneMaps.find(m => m.id === mapId);

                        if (map) {
                            try {
                                const success = await roomsService.distributeBatchToMap(
                                    batchId,
                                    mapId,
                                    startRow,
                                    startCol,
                                    map.grid_rows,
                                    map.grid_columns,
                                    user?.id
                                );
                                if (success) {
                                    if (id) await loadData(id);
                                    showToast("Plantas distribuidas correctamente", 'success');
                                    setIsClosingSingleDistribution(true);
                                    setTimeout(() => {
                                        setIsSingleDistributeConfirmOpen(false);
                                        setIsClosingSingleDistribution(false);
                                        setSingleDistributionData(null);
                                    }, 200);
                                } else {
                                    showToast("No se pudo distribuir el lote completo. Verifica el espacio.", 'error');
                                    setLoading(false); // Stop loading if failed
                                }
                            } catch (e) {
                                console.error(e);
                                showToast("Error al distribuir el lote.", 'error');
                                setLoading(false);
                            }
                        } else {
                            setLoading(false);
                        }
                    }}
                    onCancel={() => {
                        setIsClosingSingleDistribution(true);
                        setTimeout(() => {
                            setIsSingleDistributeConfirmOpen(false);
                            setIsClosingSingleDistribution(false);
                            setSingleDistributionData(null);
                        }, 200);
                    }}
                    confirmText="Distribuir"
                    cancelText="Cancelar"
                    variant="success"
                />

                {/* OBSEVATION MODAL */}
                {/* OBSEVATION MODAL */}
                {
                    (isObservationModalOpen || isClosingObservation) && (
                        <PortalModalOverlay isClosing={isClosingObservation}>
                            <ModalContent isClosing={isClosingObservation} style={{ maxWidth: '400px' }}>
                                <h3 style={{ marginTop: 0, color: '#f8fafc', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                    <LucideAlertTriangle style={{ color: '#fbbf24' }} /> Registrar Observación
                                </h3>
                                <p style={{ color: '#cbd5e1', fontSize: '0.95rem', marginBottom: '1rem' }}>
                                    Esto agregará una nota y una alerta a los <strong style={{ color: '#4ade80' }}>{selectedBatchIds.size}</strong> lotes seleccionados.
                                </p>
                                <textarea
                                    value={observationText}
                                    onChange={(e) => setObservationText(e.target.value)}
                                    placeholder="Escribe tu observación aquí..."
                                    style={{
                                        width: '100%',
                                        minHeight: '100px',
                                        padding: '0.75rem',
                                        borderRadius: '0.5rem',
                                        border: '1px solid rgba(255, 255, 255, 0.1)',
                                        background: 'rgba(15, 23, 42, 0.6)',
                                        color: '#f8fafc',
                                        marginBottom: '0.5rem',
                                        fontFamily: 'inherit',
                                        resize: 'vertical'
                                    }}
                                    autoFocus
                                />
                                <div style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: '1.5rem', fontStyle: 'italic' }}>
                                    (Elimina esta nota por completo para desactivar la alerta de observación)
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
                                    <CancelButton
                                        onClick={handleCloseObservation}
                                        style={{ flex: 'none', padding: '0.75rem 1.5rem' }}
                                    >
                                        Cancelar
                                    </CancelButton>
                                    <ActionButton
                                        onClick={handleRegisterObservation}
                                        $variant="success"
                                    >
                                        Guardar Observación
                                    </ActionButton>
                                </div>
                            </ModalContent>
                        </PortalModalOverlay>
                    )
                }
                <ToastModal
                    isOpen={toastState.isOpen}
                    message={toastState.message}
                    type={toastState.type}
                    onClose={() => setToastState(prev => ({ ...prev, isOpen: false }))}
                />
                {/* Bulk Delete Config Modal */}
                {
                    (isBulkDeleteConfirmOpen || isClosingBulkDelete) && (
                        <DeleteReasonModal
                            isOpen={isBulkDeleteConfirmOpen}
                            onCancel={handleCloseBulkDelete}
                            onConfirm={handleBulkDeleteConfirm}
                            itemName={`${selectedBatchIds.size} lotes seleccionados`}
                            isSubmitting={isDeletingBulk}
                        />
                    )
                } {/* CREATE BATCH MODAL */}
                {
                    (isCreateBatchModalOpen || isClosingCreateBatch) && (
                        <PortalModalOverlay isClosing={isClosingCreateBatch}>
                            <ModalContent isClosing={isClosingCreateBatch} style={{ maxWidth: '480px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.75rem', borderBottom: '1px solid rgba(255, 255, 255, 0.08)', paddingBottom: '1rem' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.85rem' }}>
                                        <div style={{
                                            width: 42,
                                            height: 42,
                                            borderRadius: '10px',
                                            background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.2) 0%, rgba(5, 150, 105, 0.1) 100%)',
                                            border: '1px solid rgba(16, 185, 129, 0.3)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: '#34d399',
                                            boxShadow: '0 0 15px rgba(16, 185, 129, 0.15)'
                                        }}>
                                            <LucideLayers size={20} />
                                        </div>
                                        <div>
                                            <h3 style={{ margin: 0, fontSize: '1.2rem', fontWeight: 700, color: '#f8fafc', letterSpacing: '-0.02em' }}>
                                                {['germinacion', 'germinación', 'germination', 'semillero'].includes((room?.type || '').toLowerCase()) ? 'Nuevo Lote de Semillas' : 'Nuevo Lote de Cultivo'}
                                            </h3>
                                            <span style={{ fontSize: '0.8rem', color: '#94a3b8' }}>
                                                Registra un nuevo lote de plantas en esta sala
                                            </span>
                                        </div>
                                    </div>
                                    <button
                                        onClick={closeCreateBatchModal}
                                        style={{
                                            background: 'rgba(255, 255, 255, 0.05)',
                                            border: '1px solid rgba(255, 255, 255, 0.1)',
                                            borderRadius: '0.5rem',
                                            width: 32,
                                            height: 32,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            cursor: 'pointer',
                                            color: '#94a3b8',
                                            transition: 'all 0.2s ease'
                                        }}
                                        onMouseOver={(e) => {
                                            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                                            e.currentTarget.style.color = '#ffffff';
                                        }}
                                        onMouseOut={(e) => {
                                            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                                            e.currentTarget.style.color = '#94a3b8';
                                        }}
                                    >
                                        <LucideX size={16} />
                                    </button>
                                </div>

                                <div style={{ marginBottom: '1.25rem' }}>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.5rem', fontWeight: 600, fontSize: '0.88rem', color: '#cbd5e1' }}>
                                        <LucideSprout size={14} style={{ color: '#34d399' }} /> Genética
                                    </label>
                                    <CustomSelect
                                        value={newBatch.geneticId}
                                        onChange={(value) => setNewBatch({ ...newBatch, geneticId: value })}
                                        options={[
                                            { value: "", label: "Seleccionar Genética" },
                                            ...genetics.map(m => ({ value: m.id, label: m.name }))
                                        ]}
                                        placeholder="Seleccionar Genética"
                                    />
                                </div>
                                <div style={{ marginBottom: '1.25rem' }}>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.5rem', fontWeight: 600, fontSize: '0.88rem', color: '#cbd5e1' }}>
                                        <LucidePackage size={14} style={{ color: '#38bdf8' }} /> Cantidad de Plantas
                                    </label>
                                    <input
                                        type="number"
                                        value={newBatch.quantity}
                                        onChange={e => setNewBatch({ ...newBatch, quantity: e.target.value })}
                                        style={{
                                            width: '100%',
                                            padding: '0.75rem 0.85rem',
                                            background: 'rgba(15, 23, 42, 0.6)',
                                            border: '1px solid rgba(255, 255, 255, 0.12)',
                                            color: '#f8fafc',
                                            borderRadius: '0.5rem',
                                            fontSize: '0.92rem',
                                            outline: 'none',
                                            transition: 'all 0.2s ease'
                                        }}
                                        onFocus={(e) => {
                                            e.target.style.borderColor = '#10b981';
                                            e.target.style.boxShadow = '0 0 0 2px rgba(16, 185, 129, 0.2)';
                                        }}
                                        onBlur={(e) => {
                                            e.target.style.borderColor = 'rgba(255, 255, 255, 0.12)';
                                            e.target.style.boxShadow = 'none';
                                        }}
                                        placeholder="0"
                                    />
                                </div>
                                <div style={{ marginBottom: '1.75rem' }}>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.5rem', fontWeight: 600, fontSize: '0.88rem', color: '#cbd5e1' }}>
                                        <LucideCalendar size={14} style={{ color: '#a78bfa' }} /> Fecha de Inicio
                                    </label>
                                    <CustomDatePicker
                                        selected={newBatch.date ? new Date(newBatch.date) : new Date()}
                                        onChange={(date) => {
                                            if (date) {
                                                const now = new Date();
                                                // Preserve current time
                                                date.setHours(now.getHours(), now.getMinutes(), now.getSeconds());
                                                setNewBatch({ ...newBatch, date: date.toISOString() });
                                            }
                                        }}
                                    />
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '1.5rem', paddingTop: '1.25rem', borderTop: '1px solid rgba(255, 255, 255, 0.08)' }}>
                                    <ShadcnButton
                                        variant="secondary"
                                        onClick={closeCreateBatchModal}
                                    >
                                        Cancelar
                                    </ShadcnButton>
                                    <ShadcnButton
                                        variant="default"
                                        onClick={handleCreateBatch}
                                        disabled={isCreatingBatch}
                                    >
                                        {isCreatingBatch ? <Loader2 className="spin" /> : <LucidePlus size={16} />}
                                        {isCreatingBatch ? 'Creando...' : 'Crear Lote'}
                                    </ShadcnButton>
                                </div>
                            </ModalContent>
                        </PortalModalOverlay>
                    )
                }
                {/* Stage Selection Modal */}
                <StageSelectionModal
                    isOpen={isStageModalOpen}
                    onClose={() => setIsStageModalOpen(false)}
                    onConfirm={handleBulkStageChange}
                    currentStage={undefined}
                    count={selectedBatchIds.size}
                />
                {/* REPLACE INLINE PRINT LOGIC WITH ROBUST COMPONENT */}
                <div style={{ display: 'none' }}>
                    <div ref={mapReportRef}>
                        <PrintableMapReport
                            roomName={room?.name || 'Sala'}
                            mapName={activeMapId ? (cloneMaps.find(m => m.id === activeMapId)?.name || 'Mapa') : 'General'}
                            rows={activeMapId ? (cloneMaps.find(m => m.id === activeMapId)?.grid_rows || 0) : (room?.grid_rows || 0)}
                            cols={activeMapId ? (cloneMaps.find(m => m.id === activeMapId)?.grid_columns || 0) : (room?.grid_columns || 0)}
                            batches={(room?.batches || []).filter(b => b.quantity > 0 && (!activeMapId || b.clone_map_id === activeMapId))}
                        />
                    </div>
                </div>

                {/* Etiquetas Print Portal */}
                <div style={{ display: 'none' }}>
                    <div ref={batchLabelsRef}>
                        <PrintableBatchLabels
                            batches={room?.batches?.filter(b => selectedBatchIds.has(b.id)) || []}
                        />
                    </div>
                </div>

                {/* Calendar Print Portal */}
                <div style={{ display: 'none' }}>
                    <PrintableRoomCalendar
                        ref={calendarPrintRef}
                        room={room}
                        tasks={tasks}
                        currentDate={currentDate}
                    />
                </div>

                {/* TrazApp Device Linking & Config Modal */}
                {isDeviceModalOpen && selectedDeviceForModal && (
                    <TrazAppDeviceDetailModal
                        device={selectedDeviceForModal}
                        initialView={deviceModalInitialView}
                        onClose={() => {
                            setIsDeviceModalOpen(false);
                            setSelectedDeviceForModal(null);
                        }}
                        onUpdate={() => {
                            fetchTrazappDevices();
                            if (id) loadData(id, false, false);
                        }}
                    />
                )}

            </Container >
        </RoomDetailContainer>
    );
}

const BatchActionButton = styled.button<{ $variant?: 'delete' }>`
                background: rgba(15, 23, 42, 0.5);
                backdrop-filter: blur(4px);
                -webkit-backdrop-filter: blur(4px);
                border: 1px solid rgba(255, 255, 255, 0.05);
                border-radius: 6px;
                color: ${props => props.$variant === 'delete' ? '#f56565' : '#63b3ed'};
                cursor: pointer;
                width: 28px;
                height: 28px;
                display: flex;
                align-items: center;
                justify-content: center;
                transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
                box-shadow: 0 1px 2px rgba(0,0,0,0.2);

                &:hover {
                    transform: translateY(-2px) scale(1.05);
                background: ${props => props.$variant === 'delete' ? 'rgba(229, 62, 62, 0.25)' : 'rgba(49, 130, 206, 0.25)'};
                border-color: ${props => props.$variant === 'delete' ? 'rgba(229, 62, 62, 0.4)' : 'rgba(49, 130, 206, 0.4)'};
                box-shadow: 0 4px 6px ${props => props.$variant === 'delete' ? 'rgba(229, 62, 62, 0.3)' : 'rgba(49, 130, 206, 0.3)'};
                color: white;
                z-index: 2;
    }

                &:active {
                    transform: translateY(0) scale(1);
                box-shadow: 0 1px 2px rgba(0,0,0,0.05);
    }
                `;

const ExpandableContainer = styled.div<{ $expanded: boolean }>`
                display: grid;
                grid-template-rows: ${props => props.$expanded ? '1fr' : '0fr'};
                transition: grid-template-rows 0.3s ease-out;
                `;

const ExpandableInner = styled.div`
                overflow: hidden;
                `;

export default RoomDetail;
